// src/runtime.ts
var BASE_PATH = "https://svc.blockdaemon.com".replace(/\/+$/, "");
var Configuration = class {
  constructor(configuration = {}) {
    this.configuration = configuration;
  }
  set config(configuration) {
    this.configuration = configuration;
  }
  get basePath() {
    return this.configuration.basePath != null ? this.configuration.basePath : BASE_PATH;
  }
  get fetchApi() {
    return this.configuration.fetchApi;
  }
  get middleware() {
    return this.configuration.middleware || [];
  }
  get queryParamsStringify() {
    return this.configuration.queryParamsStringify || querystring;
  }
  get username() {
    return this.configuration.username;
  }
  get password() {
    return this.configuration.password;
  }
  get apiKey() {
    const apiKey = this.configuration.apiKey;
    if (apiKey) {
      return typeof apiKey === "function" ? apiKey : () => apiKey;
    }
    return void 0;
  }
  get accessToken() {
    const accessToken = this.configuration.accessToken;
    if (accessToken) {
      return typeof accessToken === "function" ? accessToken : async () => accessToken;
    }
    return void 0;
  }
  get headers() {
    return this.configuration.headers;
  }
  get credentials() {
    return this.configuration.credentials;
  }
};
var DefaultConfig = new Configuration();
var _BaseAPI = class _BaseAPI {
  constructor(configuration = DefaultConfig) {
    this.configuration = configuration;
    this.fetchApi = async (url, init) => {
      let fetchParams = { url, init };
      for (const middleware of this.middleware) {
        if (middleware.pre) {
          fetchParams = await middleware.pre({
            fetch: this.fetchApi,
            ...fetchParams
          }) || fetchParams;
        }
      }
      let response = void 0;
      try {
        response = await (this.configuration.fetchApi || fetch)(fetchParams.url, fetchParams.init);
      } catch (e) {
        for (const middleware of this.middleware) {
          if (middleware.onError) {
            response = await middleware.onError({
              fetch: this.fetchApi,
              url: fetchParams.url,
              init: fetchParams.init,
              error: e,
              response: response ? response.clone() : void 0
            }) || response;
          }
        }
        if (response === void 0) {
          if (e instanceof Error) {
            throw new FetchError(e, "The request failed and the interceptors did not return an alternative response");
          } else {
            throw e;
          }
        }
      }
      for (const middleware of this.middleware) {
        if (middleware.post) {
          response = await middleware.post({
            fetch: this.fetchApi,
            url: fetchParams.url,
            init: fetchParams.init,
            response: response.clone()
          }) || response;
        }
      }
      return response;
    };
    this.middleware = configuration.middleware;
  }
  withMiddleware(...middlewares) {
    const next = this.clone();
    next.middleware = next.middleware.concat(...middlewares);
    return next;
  }
  withPreMiddleware(...preMiddlewares) {
    const middlewares = preMiddlewares.map((pre) => ({ pre }));
    return this.withMiddleware(...middlewares);
  }
  withPostMiddleware(...postMiddlewares) {
    const middlewares = postMiddlewares.map((post) => ({ post }));
    return this.withMiddleware(...middlewares);
  }
  /**
   * Check if the given MIME is a JSON MIME.
   * JSON MIME examples:
   *   application/json
   *   application/json; charset=UTF8
   *   APPLICATION/JSON
   *   application/vnd.company+json
   * @param mime - MIME (Multipurpose Internet Mail Extensions)
   * @return True if the given MIME is JSON, false otherwise.
   */
  isJsonMime(mime) {
    if (!mime) {
      return false;
    }
    return _BaseAPI.jsonRegex.test(mime);
  }
  async request(context, initOverrides) {
    const { url, init } = await this.createFetchParams(context, initOverrides);
    const response = await this.fetchApi(url, init);
    if (response && (response.status >= 200 && response.status < 300)) {
      return response;
    }
    throw new ResponseError(response, "Response returned an error code");
  }
  async createFetchParams(context, initOverrides) {
    let url = this.configuration.basePath + context.path;
    if (context.query !== void 0 && Object.keys(context.query).length !== 0) {
      url += "?" + this.configuration.queryParamsStringify(context.query);
    }
    const headers = Object.assign({}, this.configuration.headers, context.headers);
    Object.keys(headers).forEach((key) => headers[key] === void 0 ? delete headers[key] : {});
    const initOverrideFn = typeof initOverrides === "function" ? initOverrides : async () => initOverrides;
    const initParams = {
      method: context.method,
      headers,
      body: context.body,
      credentials: this.configuration.credentials
    };
    const overriddenInit = {
      ...initParams,
      ...await initOverrideFn({
        init: initParams,
        context
      })
    };
    let body;
    if (isFormData(overriddenInit.body) || overriddenInit.body instanceof URLSearchParams || isBlob(overriddenInit.body)) {
      body = overriddenInit.body;
    } else if (this.isJsonMime(headers["Content-Type"])) {
      body = JSON.stringify(overriddenInit.body);
    } else {
      body = overriddenInit.body;
    }
    const init = {
      ...overriddenInit,
      body
    };
    return { url, init };
  }
  /**
   * Create a shallow clone of `this` by constructing a new instance
   * and then shallow cloning data members.
   */
  clone() {
    const constructor = this.constructor;
    const next = new constructor(this.configuration);
    next.middleware = this.middleware.slice();
    return next;
  }
};
_BaseAPI.jsonRegex = new RegExp("^(:?application/json|[^;/ 	]+/[^;/ 	]+[+]json)[ 	]*(:?;.*)?$", "i");
var BaseAPI = _BaseAPI;
function isBlob(value) {
  return typeof Blob !== "undefined" && value instanceof Blob;
}
function isFormData(value) {
  return typeof FormData !== "undefined" && value instanceof FormData;
}
var ResponseError = class extends Error {
  constructor(response, msg) {
    super(msg);
    this.response = response;
    this.name = "ResponseError";
    const actualProto = new.target.prototype;
    if (Object.setPrototypeOf) {
      Object.setPrototypeOf(this, actualProto);
    }
  }
};
var FetchError = class extends Error {
  constructor(cause, msg) {
    super(msg);
    this.cause = cause;
    this.name = "FetchError";
    const actualProto = new.target.prototype;
    if (Object.setPrototypeOf) {
      Object.setPrototypeOf(this, actualProto);
    }
  }
};
var RequiredError = class extends Error {
  constructor(field, msg) {
    super(msg);
    this.field = field;
    this.name = "RequiredError";
    const actualProto = new.target.prototype;
    if (Object.setPrototypeOf) {
      Object.setPrototypeOf(this, actualProto);
    }
  }
};
var COLLECTION_FORMATS = {
  csv: ",",
  ssv: " ",
  tsv: "	",
  pipes: "|"
};
function querystring(params, prefix = "") {
  return Object.keys(params).map((key) => querystringSingleKey(key, params[key], prefix)).filter((part) => part.length > 0).join("&");
}
function querystringSingleKey(key, value, keyPrefix = "") {
  const fullKey = keyPrefix + (keyPrefix.length ? `[${key}]` : key);
  if (value instanceof Array) {
    const multiValue = value.map((singleValue) => encodeURIComponent(String(singleValue))).join(`&${encodeURIComponent(fullKey)}=`);
    return `${encodeURIComponent(fullKey)}=${multiValue}`;
  }
  if (value instanceof Set) {
    const valueAsArray = Array.from(value);
    return querystringSingleKey(key, valueAsArray, keyPrefix);
  }
  if (value instanceof Date) {
    return `${encodeURIComponent(fullKey)}=${encodeURIComponent(value.toISOString())}`;
  }
  if (value instanceof Object) {
    return querystring(value, fullKey);
  }
  return `${encodeURIComponent(fullKey)}=${encodeURIComponent(String(value))}`;
}
function exists(json, key) {
  const value = json[key];
  return value !== null && value !== void 0;
}
function canConsumeForm(consumes) {
  for (const consume of consumes) {
    if ("multipart/form-data" === consume.contentType) {
      return true;
    }
  }
  return false;
}
var JSONApiResponse = class {
  constructor(raw, transformer = (jsonValue) => jsonValue) {
    this.raw = raw;
    this.transformer = transformer;
  }
  async value() {
    return this.transformer(await this.raw.json());
  }
};
var VoidApiResponse = class {
  constructor(raw) {
    this.raw = raw;
  }
  async value() {
    return void 0;
  }
};
var BlobApiResponse = class {
  constructor(raw) {
    this.raw = raw;
  }
  async value() {
    return await this.raw.blob();
  }
};
var TextApiResponse = class {
  constructor(raw) {
    this.raw = raw;
  }
  async value() {
    return await this.raw.text();
  }
};

// src/apis/AaApi.ts
var AaApi = class extends BaseAPI {
  /**
   * Creates request options for aaGetpaymasterdata without sending the request
   */
  async aaGetpaymasterdataRequestOpts(requestParameters) {
    if (requestParameters["sender"] == null) {
      throw new RequiredError(
        "sender",
        'Required parameter "sender" was null or undefined when calling aaGetpaymasterdata().'
      );
    }
    if (requestParameters["paymasterContractAddress"] == null) {
      throw new RequiredError(
        "paymasterContractAddress",
        'Required parameter "paymasterContractAddress" was null or undefined when calling aaGetpaymasterdata().'
      );
    }
    if (requestParameters["mockValidAfter"] == null) {
      throw new RequiredError(
        "mockValidAfter",
        'Required parameter "mockValidAfter" was null or undefined when calling aaGetpaymasterdata().'
      );
    }
    if (requestParameters["mockValidUntil"] == null) {
      throw new RequiredError(
        "mockValidUntil",
        'Required parameter "mockValidUntil" was null or undefined when calling aaGetpaymasterdata().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["sender"] != null) {
      queryParameters["sender"] = requestParameters["sender"];
    }
    if (requestParameters["nonce"] != null) {
      queryParameters["nonce"] = requestParameters["nonce"];
    }
    if (requestParameters["initCode"] != null) {
      queryParameters["initCode"] = requestParameters["initCode"];
    }
    if (requestParameters["preVerificationGas"] != null) {
      queryParameters["preVerificationGas"] = requestParameters["preVerificationGas"];
    }
    if (requestParameters["verificationGasLimit"] != null) {
      queryParameters["verificationGasLimit"] = requestParameters["verificationGasLimit"];
    }
    if (requestParameters["gasLimit"] != null) {
      queryParameters["gasLimit"] = requestParameters["gasLimit"];
    }
    if (requestParameters["callGasLimit"] != null) {
      queryParameters["callGasLimit"] = requestParameters["callGasLimit"];
    }
    if (requestParameters["callData"] != null) {
      queryParameters["callData"] = requestParameters["callData"];
    }
    if (requestParameters["maxPriorityFeePerGas"] != null) {
      queryParameters["maxPriorityFeePerGas"] = requestParameters["maxPriorityFeePerGas"];
    }
    if (requestParameters["maxFeePerGas"] != null) {
      queryParameters["maxFeePerGas"] = requestParameters["maxFeePerGas"];
    }
    if (requestParameters["paymasterAndData"] != null) {
      queryParameters["paymasterAndData"] = requestParameters["paymasterAndData"];
    }
    if (requestParameters["paymasterContractAddress"] != null) {
      queryParameters["paymasterContractAddress"] = requestParameters["paymasterContractAddress"];
    }
    if (requestParameters["mockValidAfter"] != null) {
      queryParameters["mockValidAfter"] = requestParameters["mockValidAfter"];
    }
    if (requestParameters["mockValidUntil"] != null) {
      queryParameters["mockValidUntil"] = requestParameters["mockValidUntil"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/aa/getpaymasterdata`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * This endpoint retrieves the paymaster data necessary for account abstraction. It returns a payload containing the paymasterData, a verification hash, and validity timestamps (mockValidUntil and mockValidAfter) that should be incorporated into user operations.  > 💡  **Compute Unit Value:** `15` (Fixed)
   * Get Paymaster Data
   */
  async aaGetpaymasterdataRaw(requestParameters, initOverrides) {
    const requestOptions = await this.aaGetpaymasterdataRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * This endpoint retrieves the paymaster data necessary for account abstraction. It returns a payload containing the paymasterData, a verification hash, and validity timestamps (mockValidUntil and mockValidAfter) that should be incorporated into user operations.  > 💡  **Compute Unit Value:** `15` (Fixed)
   * Get Paymaster Data
   */
  async aaGetpaymasterdata(requestParameters, initOverrides) {
    const response = await this.aaGetpaymasterdataRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for aaGetsignaturemessage without sending the request
   */
  async aaGetsignaturemessageRequestOpts(requestParameters) {
    if (requestParameters["sender"] == null) {
      throw new RequiredError(
        "sender",
        'Required parameter "sender" was null or undefined when calling aaGetsignaturemessage().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["sender"] != null) {
      queryParameters["sender"] = requestParameters["sender"];
    }
    if (requestParameters["nonce"] != null) {
      queryParameters["nonce"] = requestParameters["nonce"];
    }
    if (requestParameters["initCode"] != null) {
      queryParameters["initCode"] = requestParameters["initCode"];
    }
    if (requestParameters["preVerificationGas"] != null) {
      queryParameters["preVerificationGas"] = requestParameters["preVerificationGas"];
    }
    if (requestParameters["verificationGasLimit"] != null) {
      queryParameters["verificationGasLimit"] = requestParameters["verificationGasLimit"];
    }
    if (requestParameters["gasLimit"] != null) {
      queryParameters["gasLimit"] = requestParameters["gasLimit"];
    }
    if (requestParameters["callGasLimit"] != null) {
      queryParameters["callGasLimit"] = requestParameters["callGasLimit"];
    }
    if (requestParameters["callData"] != null) {
      queryParameters["callData"] = requestParameters["callData"];
    }
    if (requestParameters["maxPriorityFeePerGas"] != null) {
      queryParameters["maxPriorityFeePerGas"] = requestParameters["maxPriorityFeePerGas"];
    }
    if (requestParameters["maxFeePerGas"] != null) {
      queryParameters["maxFeePerGas"] = requestParameters["maxFeePerGas"];
    }
    if (requestParameters["paymasterAndData"] != null) {
      queryParameters["paymasterAndData"] = requestParameters["paymasterAndData"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/aa/getsignaturemessage`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * This endpoint retrieves a hexadecimal signature message that must be signed by the sender as part of the account abstraction process. The signed message serves as a critical authorization component for subsequent user operations on the blockchain.  > 💡  **Compute Unit Value:** `15` (Fixed)
   * Get Signature Message
   */
  async aaGetsignaturemessageRaw(requestParameters, initOverrides) {
    const requestOptions = await this.aaGetsignaturemessageRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * This endpoint retrieves a hexadecimal signature message that must be signed by the sender as part of the account abstraction process. The signed message serves as a critical authorization component for subsequent user operations on the blockchain.  > 💡  **Compute Unit Value:** `15` (Fixed)
   * Get Signature Message
   */
  async aaGetsignaturemessage(requestParameters, initOverrides) {
    const response = await this.aaGetsignaturemessageRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for aaGetuserops without sending the request
   */
  async aaGetuseropsRequestOpts(requestParameters) {
    if (requestParameters["sender"] == null) {
      throw new RequiredError(
        "sender",
        'Required parameter "sender" was null or undefined when calling aaGetuserops().'
      );
    }
    if (requestParameters["userOps"] == null) {
      throw new RequiredError(
        "userOps",
        'Required parameter "userOps" was null or undefined when calling aaGetuserops().'
      );
    }
    if (requestParameters["bundler"] == null) {
      throw new RequiredError(
        "bundler",
        'Required parameter "bundler" was null or undefined when calling aaGetuserops().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["sender"] != null) {
      queryParameters["sender"] = requestParameters["sender"];
    }
    if (requestParameters["nonce"] != null) {
      queryParameters["nonce"] = requestParameters["nonce"];
    }
    if (requestParameters["initCode"] != null) {
      queryParameters["initCode"] = requestParameters["initCode"];
    }
    if (requestParameters["preVerificationGas"] != null) {
      queryParameters["preVerificationGas"] = requestParameters["preVerificationGas"];
    }
    if (requestParameters["verificationGasLimit"] != null) {
      queryParameters["verificationGasLimit"] = requestParameters["verificationGasLimit"];
    }
    if (requestParameters["gasLimit"] != null) {
      queryParameters["gasLimit"] = requestParameters["gasLimit"];
    }
    if (requestParameters["callGasLimit"] != null) {
      queryParameters["callGasLimit"] = requestParameters["callGasLimit"];
    }
    if (requestParameters["callData"] != null) {
      queryParameters["callData"] = requestParameters["callData"];
    }
    if (requestParameters["maxPriorityFeePerGas"] != null) {
      queryParameters["maxPriorityFeePerGas"] = requestParameters["maxPriorityFeePerGas"];
    }
    if (requestParameters["maxFeePerGas"] != null) {
      queryParameters["maxFeePerGas"] = requestParameters["maxFeePerGas"];
    }
    if (requestParameters["paymasterAndData"] != null) {
      queryParameters["paymasterAndData"] = requestParameters["paymasterAndData"];
    }
    if (requestParameters["userOps"] != null) {
      queryParameters["userOps"] = requestParameters["userOps"];
    }
    if (requestParameters["bundler"] != null) {
      queryParameters["bundler"] = requestParameters["bundler"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/aa/getuserops`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a user operation object that can be signed and submitted to the bundler. Used in account abstraction flows to gather the required transaction data.  > 💡  **Compute Unit Value:** `5` (Fixed)
   * Get User Ops
   */
  async aaGetuseropsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.aaGetuseropsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a user operation object that can be signed and submitted to the bundler. Used in account abstraction flows to gather the required transaction data.  > 💡  **Compute Unit Value:** `5` (Fixed)
   * Get User Ops
   */
  async aaGetuserops(requestParameters, initOverrides) {
    const response = await this.aaGetuseropsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for aaSenduserops without sending the request
   */
  async aaSenduseropsRequestOpts(requestParameters) {
    if (requestParameters["aaSenduseropsRequestPayload"] == null) {
      throw new RequiredError(
        "aaSenduseropsRequestPayload",
        'Required parameter "aaSenduseropsRequestPayload" was null or undefined when calling aaSenduserops().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/aa/senduserops`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["aaSenduseropsRequestPayload"]
    };
  }
  /**
   * This endpoint generates and returns the necessary paymaster parameters for account abstraction. It constructs a user operation payload including sender, recipient, gas, and call data details which can be submitted to execute transactions on the blockchain.  > 💡  **Compute Unit Value:** `15` (Fixed)
   * Send User Ops
   */
  async aaSenduseropsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.aaSenduseropsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * This endpoint generates and returns the necessary paymaster parameters for account abstraction. It constructs a user operation payload including sender, recipient, gas, and call data details which can be submitted to execute transactions on the blockchain.  > 💡  **Compute Unit Value:** `15` (Fixed)
   * Send User Ops
   */
  async aaSenduserops(requestParameters, initOverrides) {
    const response = await this.aaSenduseropsRaw(requestParameters, initOverrides);
    return await response.value();
  }
};
var AaGetpaymasterdataChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _137: "137",
  _250: "250",
  _10: "10",
  _11155420: "11155420",
  _42161: "42161",
  _421614: "421614",
  _25: "25",
  _338: "338",
  _84532: "84532",
  _8453: "8453",
  _43113: "43113",
  _97: "97",
  _80002: "80002",
  _300: "300",
  _324: "324"
};
var AaGetsignaturemessageChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _137: "137",
  _250: "250",
  _10: "10",
  _11155420: "11155420",
  _42161: "42161",
  _421614: "421614",
  _25: "25",
  _338: "338",
  _84532: "84532",
  _8453: "8453",
  _43113: "43113",
  _97: "97",
  _80002: "80002",
  _300: "300",
  _324: "324"
};
var AaGetuseropsChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _137: "137",
  _250: "250",
  _10: "10",
  _11155420: "11155420",
  _42161: "42161",
  _421614: "421614",
  _25: "25",
  _338: "338",
  _84532: "84532",
  _8453: "8453",
  _43113: "43113",
  _97: "97",
  _80002: "80002",
  _300: "300",
  _324: "324"
};

// src/apis/AccountApi.ts
var AccountApi = class extends BaseAPI {
  /**
   * Creates request options for accountV1Allowance without sending the request
   */
  async accountV1AllowanceRequestOpts(requestParameters) {
    if (requestParameters["tokenAddress"] == null) {
      throw new RequiredError(
        "tokenAddress",
        'Required parameter "tokenAddress" was null or undefined when calling accountV1Allowance().'
      );
    }
    if (requestParameters["owner"] == null) {
      throw new RequiredError(
        "owner",
        'Required parameter "owner" was null or undefined when calling accountV1Allowance().'
      );
    }
    if (requestParameters["spender"] == null) {
      throw new RequiredError(
        "spender",
        'Required parameter "spender" was null or undefined when calling accountV1Allowance().'
      );
    }
    const queryParameters = {};
    if (requestParameters["tokenAddress"] != null) {
      queryParameters["tokenAddress"] = requestParameters["tokenAddress"];
    }
    if (requestParameters["owner"] != null) {
      queryParameters["owner"] = requestParameters["owner"];
    }
    if (requestParameters["spender"] != null) {
      queryParameters["spender"] = requestParameters["spender"];
    }
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/account/v1/allowance`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * ![Beta](https://img.shields.io/badge/Status-Beta-yellow) <br/> Retrieves the approved allowance for a specific ERC-20 token, indicating how many tokens a designated spender is authorized to withdraw from an owner’s wallet. It is especially useful for verifying spending limits before initiating transactions, ensuring that decentralized applications or swap interfaces can confirm the available allowance for token transfers.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * Get User Allowance
   */
  async accountV1AllowanceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.accountV1AllowanceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * ![Beta](https://img.shields.io/badge/Status-Beta-yellow) <br/> Retrieves the approved allowance for a specific ERC-20 token, indicating how many tokens a designated spender is authorized to withdraw from an owner’s wallet. It is especially useful for verifying spending limits before initiating transactions, ensuring that decentralized applications or swap interfaces can confirm the available allowance for token transfers.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * Get User Allowance
   */
  async accountV1Allowance(requestParameters, initOverrides) {
    const response = await this.accountV1AllowanceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for accountV1Portfolio without sending the request
   */
  async accountV1PortfolioRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling accountV1Portfolio().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["tokens"] != null) {
      queryParameters["tokens"] = requestParameters["tokens"];
    }
    if (requestParameters["pageToken"] != null) {
      queryParameters["pageToken"] = requestParameters["pageToken"];
    }
    if (requestParameters["assetType"] != null) {
      queryParameters["assetType"] = requestParameters["assetType"];
    }
    if (requestParameters["timestamp"] != null) {
      queryParameters["timestamp"] = requestParameters["timestamp"];
    }
    if (requestParameters["blockNumber"] != null) {
      queryParameters["blockNumber"] = requestParameters["blockNumber"];
    }
    if (requestParameters["include24hrChange"] != null) {
      queryParameters["include24hrChange"] = requestParameters["include24hrChange"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    if (requestParameters["availableOnly"] != null) {
      queryParameters["availableOnly"] = requestParameters["availableOnly"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/account/v1/portfolio`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * ![Beta](https://img.shields.io/badge/Status-Beta-yellow) <br/> Provides a snapshot of a wallet’s asset portfolio across supported blockchains, including native tokens and detailed EVM token information. Each token entry includes metadata (name, symbol, decimals, contract address, balance, USD valuation) and optional filtering to hide zero balances, simplifying portfolio management.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Get User Portfolio
   */
  async accountV1PortfolioRaw(requestParameters, initOverrides) {
    const requestOptions = await this.accountV1PortfolioRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * ![Beta](https://img.shields.io/badge/Status-Beta-yellow) <br/> Provides a snapshot of a wallet’s asset portfolio across supported blockchains, including native tokens and detailed EVM token information. Each token entry includes metadata (name, symbol, decimals, contract address, balance, USD valuation) and optional filtering to hide zero balances, simplifying portfolio management.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Get User Portfolio
   */
  async accountV1Portfolio(requestParameters, initOverrides) {
    const response = await this.accountV1PortfolioRaw(requestParameters, initOverrides);
    return await response.value();
  }
};
var AccountV1AllowanceChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _137: "137",
  _250: "250",
  _10: "10",
  _11155420: "11155420",
  _42161: "42161",
  _421614: "421614",
  _25: "25",
  _338: "338",
  _84532: "84532",
  _8453: "8453",
  _43113: "43113",
  _97: "97",
  _80002: "80002",
  _300: "300",
  _324: "324",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636",
  _8217: "8217",
  _1001: "1001"
};
var AccountV1PortfolioChainIdEnum = {
  _1: "1",
  _8217: "8217",
  _56: "56",
  _25: "25",
  _43114: "43114",
  _900: "900",
  _901: "901",
  _1200: "1200",
  _42161: "42161",
  _10: "10",
  _1600: "1600",
  _1601: "1601",
  _1700: "1700",
  _1701: "1701",
  Osmosis1: "osmosis-1",
  OsmoTest5: "osmo-test-5",
  _137: "137",
  _250: "250",
  _8453: "8453",
  _1100: "1100",
  _1500: "1500",
  _1501: "1501"
};
var AccountV1PortfolioAssetTypeEnum = {
  Fungible: "fungible",
  NonFungible: "nonFungible",
  All: "all"
};

// src/apis/BridgeApi.ts
var BridgeApi = class extends BaseAPI {
  /**
   * Creates request options for bridgeAddliquidity without sending the request
   */
  async bridgeAddliquidityRequestOpts(requestParameters) {
    if (requestParameters["bridgeAddliquidityRequestPayload"] == null) {
      throw new RequiredError(
        "bridgeAddliquidityRequestPayload",
        'Required parameter "bridgeAddliquidityRequestPayload" was null or undefined when calling bridgeAddliquidity().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/addliquidity`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["bridgeAddliquidityRequestPayload"]
    };
  }
  /**
   * This endpoint enables you to add liquidity to a cross-chain bridge by depositing a specified amount of tokens from a source blockchain. Upon a successful call, the API returns comprehensive details about the liquidity addition, including chain IDs, source and destination addresses, gas settings, and a unique reference ID for tracking.  > 💡  **Compute Unit Value:** `85` (Fixed)
   * Add Liquidity
   */
  async bridgeAddliquidityRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgeAddliquidityRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * This endpoint enables you to add liquidity to a cross-chain bridge by depositing a specified amount of tokens from a source blockchain. Upon a successful call, the API returns comprehensive details about the liquidity addition, including chain IDs, source and destination addresses, gas settings, and a unique reference ID for tracking.  > 💡  **Compute Unit Value:** `85` (Fixed)
   * Add Liquidity
   */
  async bridgeAddliquidity(requestParameters, initOverrides) {
    const response = await this.bridgeAddliquidityRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgeGetliquidity without sending the request
   */
  async bridgeGetliquidityRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling bridgeGetliquidity().'
      );
    }
    const queryParameters = {};
    if (requestParameters["poolAddress"] != null) {
      queryParameters["poolAddress"] = requestParameters["poolAddress"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["srcChainId"] != null) {
      queryParameters["srcChainId"] = requestParameters["srcChainId"];
    }
    if (requestParameters["bridgeId"] != null) {
      queryParameters["bridgeId"] = requestParameters["bridgeId"];
    }
    if (requestParameters["srcChainSymbol"] != null) {
      queryParameters["srcChainSymbol"] = requestParameters["srcChainSymbol"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    if (requestParameters["srcTokenSymbol"] != null) {
      queryParameters["srcTokenSymbol"] = requestParameters["srcTokenSymbol"];
    }
    if (requestParameters["srcTokenAddress"] != null) {
      queryParameters["srcTokenAddress"] = requestParameters["srcTokenAddress"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/getliquidity`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves the balance of a specific token pair within a liquidity pool for a given account. This endpoint allows users to query the current liquidity provided to a bridge by an account, facilitating monitoring and management of assets across different blockchain networks.  > 💡  **Compute Unit Value:** `65` (Fixed)
   * Get Liquidity
   */
  async bridgeGetliquidityRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgeGetliquidityRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves the balance of a specific token pair within a liquidity pool for a given account. This endpoint allows users to query the current liquidity provided to a bridge by an account, facilitating monitoring and management of assets across different blockchain networks.  > 💡  **Compute Unit Value:** `65` (Fixed)
   * Get Liquidity
   */
  async bridgeGetliquidity(requestParameters, initOverrides) {
    const response = await this.bridgeGetliquidityRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgeGetroute without sending the request
   */
  async bridgeGetrouteRequestOpts(requestParameters) {
    if (requestParameters["dstChainId"] == null) {
      throw new RequiredError(
        "dstChainId",
        'Required parameter "dstChainId" was null or undefined when calling bridgeGetroute().'
      );
    }
    if (requestParameters["amountIn"] == null) {
      throw new RequiredError(
        "amountIn",
        'Required parameter "amountIn" was null or undefined when calling bridgeGetroute().'
      );
    }
    const queryParameters = {};
    if (requestParameters["srcChainId"] != null) {
      queryParameters["srcChainId"] = requestParameters["srcChainId"];
    }
    if (requestParameters["bridgeId"] != null) {
      queryParameters["bridgeId"] = requestParameters["bridgeId"];
    }
    if (requestParameters["dstChainId"] != null) {
      queryParameters["dstChainId"] = requestParameters["dstChainId"];
    }
    if (requestParameters["srcTokenSymbol"] != null) {
      queryParameters["srcTokenSymbol"] = requestParameters["srcTokenSymbol"];
    }
    if (requestParameters["dstTokenSymbol"] != null) {
      queryParameters["dstTokenSymbol"] = requestParameters["dstTokenSymbol"];
    }
    if (requestParameters["srcTokenAddress"] != null) {
      queryParameters["srcTokenAddress"] = requestParameters["srcTokenAddress"];
    }
    if (requestParameters["dstTokenAddress"] != null) {
      queryParameters["dstTokenAddress"] = requestParameters["dstTokenAddress"];
    }
    if (requestParameters["amountIn"] != null) {
      queryParameters["amountIn"] = requestParameters["amountIn"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/getroute`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves an optimal bridging path between two blockchain networks, detailing the sequence of operations, tokens, intermediary addresses, swap paths, fees, gas estimates, and minimum tokens received. Also includes route duration and express processing options, allowing users to evaluate the feasibility, cost, and speed of cross-chain token transfers.  > 💡  **Compute Unit Value:** `65` (Fixed)
   * Get Route
   */
  async bridgeGetrouteRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgeGetrouteRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves an optimal bridging path between two blockchain networks, detailing the sequence of operations, tokens, intermediary addresses, swap paths, fees, gas estimates, and minimum tokens received. Also includes route duration and express processing options, allowing users to evaluate the feasibility, cost, and speed of cross-chain token transfers.  > 💡  **Compute Unit Value:** `65` (Fixed)
   * Get Route
   */
  async bridgeGetroute(requestParameters, initOverrides) {
    const response = await this.bridgeGetrouteRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgeGetsignature without sending the request
   */
  async bridgeGetsignatureRequestOpts(requestParameters) {
    if (requestParameters["transactionHash"] == null) {
      throw new RequiredError(
        "transactionHash",
        'Required parameter "transactionHash" was null or undefined when calling bridgeGetsignature().'
      );
    }
    const queryParameters = {};
    if (requestParameters["bridgeId"] != null) {
      queryParameters["bridgeId"] = requestParameters["bridgeId"];
    }
    if (requestParameters["transactionHash"] != null) {
      queryParameters["transactionHash"] = requestParameters["transactionHash"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/getsignature`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves the signature and transaction metadata for a cross-chain swap identified by its transaction hash. The response includes chain IDs, locked token amounts, the recipient address, and a cryptographic signature, enabling dApps to validate or finalize the swap on the destination chain.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Get Signature
   */
  async bridgeGetsignatureRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgeGetsignatureRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves the signature and transaction metadata for a cross-chain swap identified by its transaction hash. The response includes chain IDs, locked token amounts, the recipient address, and a cryptographic signature, enabling dApps to validate or finalize the swap on the destination chain.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Get Signature
   */
  async bridgeGetsignature(requestParameters, initOverrides) {
    const response = await this.bridgeGetsignatureRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgeGetsupportedchains without sending the request
   */
  async bridgeGetsupportedchainsRequestOpts(requestParameters) {
    const queryParameters = {};
    if (requestParameters["bridgeId"] != null) {
      queryParameters["bridgeId"] = requestParameters["bridgeId"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/getsupportedchains`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves an up-to-date list of blockchain networks that are supported by the bridging service. The response includes each network’s name (e.g., Ethereum, Arbitrum, Optimism) and its unique chain identifier.  > 💡  **Compute Unit Value:** `50` (Fixed)
   * Get Supported Chains
   */
  async bridgeGetsupportedchainsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgeGetsupportedchainsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves an up-to-date list of blockchain networks that are supported by the bridging service. The response includes each network’s name (e.g., Ethereum, Arbitrum, Optimism) and its unique chain identifier.  > 💡  **Compute Unit Value:** `50` (Fixed)
   * Get Supported Chains
   */
  async bridgeGetsupportedchains(requestParameters = {}, initOverrides) {
    const response = await this.bridgeGetsupportedchainsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgeGetsupportedtokens without sending the request
   */
  async bridgeGetsupportedtokensRequestOpts(requestParameters) {
    const queryParameters = {};
    if (requestParameters["bridgeId"] != null) {
      queryParameters["bridgeId"] = requestParameters["bridgeId"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/getsupportedtokens`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Returns a comprehensive list of tokens that the bridge currently supports. The response includes each token’s symbol and its corresponding blockchain address, enabling users to easily determine which assets can be transferred or managed through the bridge.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * Get Supported Tokens
   */
  async bridgeGetsupportedtokensRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgeGetsupportedtokensRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Returns a comprehensive list of tokens that the bridge currently supports. The response includes each token’s symbol and its corresponding blockchain address, enabling users to easily determine which assets can be transferred or managed through the bridge.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * Get Supported Tokens
   */
  async bridgeGetsupportedtokens(requestParameters = {}, initOverrides) {
    const response = await this.bridgeGetsupportedtokensRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgePrice without sending the request
   */
  async bridgePriceRequestOpts(requestParameters) {
    if (requestParameters["dstChainId"] == null) {
      throw new RequiredError(
        "dstChainId",
        'Required parameter "dstChainId" was null or undefined when calling bridgePrice().'
      );
    }
    if (requestParameters["amountIn"] == null) {
      throw new RequiredError(
        "amountIn",
        'Required parameter "amountIn" was null or undefined when calling bridgePrice().'
      );
    }
    const queryParameters = {};
    if (requestParameters["srcChainId"] != null) {
      queryParameters["srcChainId"] = requestParameters["srcChainId"];
    }
    if (requestParameters["bridgeId"] != null) {
      queryParameters["bridgeId"] = requestParameters["bridgeId"];
    }
    if (requestParameters["dstChainId"] != null) {
      queryParameters["dstChainId"] = requestParameters["dstChainId"];
    }
    if (requestParameters["srcTokenSymbol"] != null) {
      queryParameters["srcTokenSymbol"] = requestParameters["srcTokenSymbol"];
    }
    if (requestParameters["dstTokenSymbol"] != null) {
      queryParameters["dstTokenSymbol"] = requestParameters["dstTokenSymbol"];
    }
    if (requestParameters["srcTokenAddress"] != null) {
      queryParameters["srcTokenAddress"] = requestParameters["srcTokenAddress"];
    }
    if (requestParameters["dstTokenAddress"] != null) {
      queryParameters["dstTokenAddress"] = requestParameters["dstTokenAddress"];
    }
    if (requestParameters["amountIn"] != null) {
      queryParameters["amountIn"] = requestParameters["amountIn"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/price`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * This endpoint provides a real‑time swap quotation for transferring tokens between two blockchain networks using a designated bridge. It calculates the estimated amount of destination tokens that you would receive based on a given input token amount and token pair, factoring in the current liquidity and fees across the specified source and destination chains.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Get Bridge Swap Price
   */
  async bridgePriceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgePriceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * This endpoint provides a real‑time swap quotation for transferring tokens between two blockchain networks using a designated bridge. It calculates the estimated amount of destination tokens that you would receive based on a given input token amount and token pair, factoring in the current liquidity and fees across the specified source and destination chains.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Get Bridge Swap Price
   */
  async bridgePrice(requestParameters, initOverrides) {
    const response = await this.bridgePriceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgePriceLegacy without sending the request
   */
  async bridgePriceLegacyRequestOpts(requestParameters) {
    if (requestParameters["dstChainId"] == null) {
      throw new RequiredError(
        "dstChainId",
        'Required parameter "dstChainId" was null or undefined when calling bridgePriceLegacy().'
      );
    }
    if (requestParameters["amountIn"] == null) {
      throw new RequiredError(
        "amountIn",
        'Required parameter "amountIn" was null or undefined when calling bridgePriceLegacy().'
      );
    }
    const queryParameters = {};
    if (requestParameters["srcChainId"] != null) {
      queryParameters["srcChainId"] = requestParameters["srcChainId"];
    }
    if (requestParameters["bridgeId"] != null) {
      queryParameters["bridgeId"] = requestParameters["bridgeId"];
    }
    if (requestParameters["dstChainId"] != null) {
      queryParameters["dstChainId"] = requestParameters["dstChainId"];
    }
    if (requestParameters["srcTokenSymbol"] != null) {
      queryParameters["srcTokenSymbol"] = requestParameters["srcTokenSymbol"];
    }
    if (requestParameters["dstTokenSymbol"] != null) {
      queryParameters["dstTokenSymbol"] = requestParameters["dstTokenSymbol"];
    }
    if (requestParameters["srcTokenAddress"] != null) {
      queryParameters["srcTokenAddress"] = requestParameters["srcTokenAddress"];
    }
    if (requestParameters["dstTokenAddress"] != null) {
      queryParameters["dstTokenAddress"] = requestParameters["dstTokenAddress"];
    }
    if (requestParameters["amountIn"] != null) {
      queryParameters["amountIn"] = requestParameters["amountIn"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/getprice`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * This endpoint provides a real‑time swap quotation for transferring tokens between two blockchain networks using a designated bridge. It calculates the estimated amount of destination tokens that you would receive based on a given input token amount and token pair, factoring in the current liquidity and fees across the specified source and destination chains.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Get Bridge Swap Price
   */
  async bridgePriceLegacyRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgePriceLegacyRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * This endpoint provides a real‑time swap quotation for transferring tokens between two blockchain networks using a designated bridge. It calculates the estimated amount of destination tokens that you would receive based on a given input token amount and token pair, factoring in the current liquidity and fees across the specified source and destination chains.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Get Bridge Swap Price
   */
  async bridgePriceLegacy(requestParameters, initOverrides) {
    const response = await this.bridgePriceLegacyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgeQuotes without sending the request
   */
  async bridgeQuotesRequestOpts(requestParameters) {
    if (requestParameters["dstChainId"] == null) {
      throw new RequiredError(
        "dstChainId",
        'Required parameter "dstChainId" was null or undefined when calling bridgeQuotes().'
      );
    }
    if (requestParameters["amountIn"] == null) {
      throw new RequiredError(
        "amountIn",
        'Required parameter "amountIn" was null or undefined when calling bridgeQuotes().'
      );
    }
    const queryParameters = {};
    if (requestParameters["srcChainId"] != null) {
      queryParameters["srcChainId"] = requestParameters["srcChainId"];
    }
    if (requestParameters["dstChainId"] != null) {
      queryParameters["dstChainId"] = requestParameters["dstChainId"];
    }
    if (requestParameters["srcChainSymbol"] != null) {
      queryParameters["srcChainSymbol"] = requestParameters["srcChainSymbol"];
    }
    if (requestParameters["srcTokenSymbol"] != null) {
      queryParameters["srcTokenSymbol"] = requestParameters["srcTokenSymbol"];
    }
    if (requestParameters["dstTokenSymbol"] != null) {
      queryParameters["dstTokenSymbol"] = requestParameters["dstTokenSymbol"];
    }
    if (requestParameters["srcTokenAddress"] != null) {
      queryParameters["srcTokenAddress"] = requestParameters["srcTokenAddress"];
    }
    if (requestParameters["dstTokenAddress"] != null) {
      queryParameters["dstTokenAddress"] = requestParameters["dstTokenAddress"];
    }
    if (requestParameters["amountIn"] != null) {
      queryParameters["amountIn"] = requestParameters["amountIn"];
    }
    if (requestParameters["excludedBridges"] != null) {
      queryParameters["excludedBridges"] = requestParameters["excludedBridges"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/quotes`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a comprehensive list of swap quotations from multiple bridges for a specified token pair pool. By aggregating swap quotes across various bridges, it enables users to compare rates and identify the most favorable trading option. The response includes detailed information such as the Bridge name and identifier, the input amount, the swap path, and the resulting output amounts from each bridge, making it a valuable tool for pricing data and swap execution decisions.  > 💡  **Compute Unit Value:** `85` (Fixed)
   * Get Bridge Swap Quotes
   */
  async bridgeQuotesRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgeQuotesRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a comprehensive list of swap quotations from multiple bridges for a specified token pair pool. By aggregating swap quotes across various bridges, it enables users to compare rates and identify the most favorable trading option. The response includes detailed information such as the Bridge name and identifier, the input amount, the swap path, and the resulting output amounts from each bridge, making it a valuable tool for pricing data and swap execution decisions.  > 💡  **Compute Unit Value:** `85` (Fixed)
   * Get Bridge Swap Quotes
   */
  async bridgeQuotes(requestParameters, initOverrides) {
    const response = await this.bridgeQuotesRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgeQuotesLegacy without sending the request
   */
  async bridgeQuotesLegacyRequestOpts(requestParameters) {
    if (requestParameters["dstChainId"] == null) {
      throw new RequiredError(
        "dstChainId",
        'Required parameter "dstChainId" was null or undefined when calling bridgeQuotesLegacy().'
      );
    }
    if (requestParameters["amountIn"] == null) {
      throw new RequiredError(
        "amountIn",
        'Required parameter "amountIn" was null or undefined when calling bridgeQuotesLegacy().'
      );
    }
    const queryParameters = {};
    if (requestParameters["srcChainId"] != null) {
      queryParameters["srcChainId"] = requestParameters["srcChainId"];
    }
    if (requestParameters["dstChainId"] != null) {
      queryParameters["dstChainId"] = requestParameters["dstChainId"];
    }
    if (requestParameters["srcChainSymbol"] != null) {
      queryParameters["srcChainSymbol"] = requestParameters["srcChainSymbol"];
    }
    if (requestParameters["srcTokenSymbol"] != null) {
      queryParameters["srcTokenSymbol"] = requestParameters["srcTokenSymbol"];
    }
    if (requestParameters["dstTokenSymbol"] != null) {
      queryParameters["dstTokenSymbol"] = requestParameters["dstTokenSymbol"];
    }
    if (requestParameters["srcTokenAddress"] != null) {
      queryParameters["srcTokenAddress"] = requestParameters["srcTokenAddress"];
    }
    if (requestParameters["dstTokenAddress"] != null) {
      queryParameters["dstTokenAddress"] = requestParameters["dstTokenAddress"];
    }
    if (requestParameters["amountIn"] != null) {
      queryParameters["amountIn"] = requestParameters["amountIn"];
    }
    if (requestParameters["excludedBridges"] != null) {
      queryParameters["excludedBridges"] = requestParameters["excludedBridges"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/quoteaggregator`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a comprehensive list of swap quotations from multiple bridges for a specified token pair pool. By aggregating swap quotes across various bridges, it enables users to compare rates and identify the most favorable trading option. The response includes detailed information such as the Bridge name and identifier, the input amount, the swap path, and the resulting output amounts from each bridge, making it a valuable tool for pricing data and swap execution decisions.  > 💡  **Compute Unit Value:** `85` (Fixed)
   * Get Bridge Swap Quotes
   */
  async bridgeQuotesLegacyRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgeQuotesLegacyRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a comprehensive list of swap quotations from multiple bridges for a specified token pair pool. By aggregating swap quotes across various bridges, it enables users to compare rates and identify the most favorable trading option. The response includes detailed information such as the Bridge name and identifier, the input amount, the swap path, and the resulting output amounts from each bridge, making it a valuable tool for pricing data and swap execution decisions.  > 💡  **Compute Unit Value:** `85` (Fixed)
   * Get Bridge Swap Quotes
   */
  async bridgeQuotesLegacy(requestParameters, initOverrides) {
    const response = await this.bridgeQuotesLegacyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgeRemoveliquidity without sending the request
   */
  async bridgeRemoveliquidityRequestOpts(requestParameters) {
    if (requestParameters["bridgeRemoveliquidityRequestPayload"] == null) {
      throw new RequiredError(
        "bridgeRemoveliquidityRequestPayload",
        'Required parameter "bridgeRemoveliquidityRequestPayload" was null or undefined when calling bridgeRemoveliquidity().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/removeliquidity`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["bridgeRemoveliquidityRequestPayload"]
    };
  }
  /**
   * Removes user-provided liquidity from one or more blockchain networks in a single transaction, streamlining cross-chain asset management. The response includes details such as chain IDs, addresses, gas limits, payload data, and a unique reference ID for transaction tracking.  > 💡  **Compute Unit Value:** `85` (Fixed)
   * Remove Liquidity
   */
  async bridgeRemoveliquidityRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgeRemoveliquidityRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Removes user-provided liquidity from one or more blockchain networks in a single transaction, streamlining cross-chain asset management. The response includes details such as chain IDs, addresses, gas limits, payload data, and a unique reference ID for transaction tracking.  > 💡  **Compute Unit Value:** `85` (Fixed)
   * Remove Liquidity
   */
  async bridgeRemoveliquidity(requestParameters, initOverrides) {
    const response = await this.bridgeRemoveliquidityRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgeSwap without sending the request
   */
  async bridgeSwapRequestOpts(requestParameters) {
    if (requestParameters["bridgeSwapRequestPayload"] == null) {
      throw new RequiredError(
        "bridgeSwapRequestPayload",
        'Required parameter "bridgeSwapRequestPayload" was null or undefined when calling bridgeSwap().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/swap`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["bridgeSwapRequestPayload"]
    };
  }
  /**
   * Enables seamless, cross-chain token exchanges by swapping assets between supported blockchain networks. This endpoint processes the swap transaction securely and efficiently, converting tokens from one chain to another while managing the necessary transaction parameters. The response provides key details—including source and destination chain IDs, sender and receiver addresses, transferred value, gas specifications, and a unique reference ID—allowing developers and users to verify and track the swap transaction.  > 💡  **Compute Unit Value:** `80` (Fixed)
   * Create Bridge Swap Transaction
   */
  async bridgeSwapRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgeSwapRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Enables seamless, cross-chain token exchanges by swapping assets between supported blockchain networks. This endpoint processes the swap transaction securely and efficiently, converting tokens from one chain to another while managing the necessary transaction parameters. The response provides key details—including source and destination chain IDs, sender and receiver addresses, transferred value, gas specifications, and a unique reference ID—allowing developers and users to verify and track the swap transaction.  > 💡  **Compute Unit Value:** `80` (Fixed)
   * Create Bridge Swap Transaction
   */
  async bridgeSwap(requestParameters, initOverrides) {
    const response = await this.bridgeSwapRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgeSwapWithApproval without sending the request
   */
  async bridgeSwapWithApprovalRequestOpts(requestParameters) {
    if (requestParameters["bridgeSwapwithapprovalRequestPayload"] == null) {
      throw new RequiredError(
        "bridgeSwapwithapprovalRequestPayload",
        'Required parameter "bridgeSwapwithapprovalRequestPayload" was null or undefined when calling bridgeSwapWithApproval().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/swap-with-approval`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["bridgeSwapwithapprovalRequestPayload"]
    };
  }
  /**
   * Generates both token approval and swap transactions in a single call with configurable slippage protection.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Create Bridge Swap Transaction with Approval
   */
  async bridgeSwapWithApprovalRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgeSwapWithApprovalRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Generates both token approval and swap transactions in a single call with configurable slippage protection.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Create Bridge Swap Transaction with Approval
   */
  async bridgeSwapWithApproval(requestParameters, initOverrides) {
    const response = await this.bridgeSwapWithApprovalRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgeSwapWithApprovalLegacy without sending the request
   */
  async bridgeSwapWithApprovalLegacyRequestOpts(requestParameters) {
    if (requestParameters["bridgeSwapwithapprovalRequestPayload"] == null) {
      throw new RequiredError(
        "bridgeSwapwithapprovalRequestPayload",
        'Required parameter "bridgeSwapwithapprovalRequestPayload" was null or undefined when calling bridgeSwapWithApprovalLegacy().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/swapwithapproval`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["bridgeSwapwithapprovalRequestPayload"]
    };
  }
  /**
   * Generates both token approval and swap transactions in a single call with configurable slippage protection.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Create Bridge Swap Transaction with Approval
   */
  async bridgeSwapWithApprovalLegacyRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgeSwapWithApprovalLegacyRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Generates both token approval and swap transactions in a single call with configurable slippage protection.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Create Bridge Swap Transaction with Approval
   */
  async bridgeSwapWithApprovalLegacy(requestParameters, initOverrides) {
    const response = await this.bridgeSwapWithApprovalLegacyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgeSwaps without sending the request
   */
  async bridgeSwapsRequestOpts(requestParameters) {
    if (requestParameters["bridgeSwapaggregatorRequestPayload"] == null) {
      throw new RequiredError(
        "bridgeSwapaggregatorRequestPayload",
        'Required parameter "bridgeSwapaggregatorRequestPayload" was null or undefined when calling bridgeSwaps().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/swaps`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["bridgeSwapaggregatorRequestPayload"]
    };
  }
  /**
   * Aggregates swap quotes from all bridges supporting a specified token pair. It allows users to customize their query to prioritize either the best output or the lowest gas costs. The response provides detailed transaction data including bridge identifiers, output amounts, swap routing parameters (such as call data), gas estimates, and a reference, enabling traders to compare and select the most efficient swap route for their needs.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Aggregate Bridge Swap
   */
  async bridgeSwapsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgeSwapsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Aggregates swap quotes from all bridges supporting a specified token pair. It allows users to customize their query to prioritize either the best output or the lowest gas costs. The response provides detailed transaction data including bridge identifiers, output amounts, swap routing parameters (such as call data), gas estimates, and a reference, enabling traders to compare and select the most efficient swap route for their needs.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Aggregate Bridge Swap
   */
  async bridgeSwaps(requestParameters, initOverrides) {
    const response = await this.bridgeSwapsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgeSwapsLegacy without sending the request
   */
  async bridgeSwapsLegacyRequestOpts(requestParameters) {
    if (requestParameters["bridgeSwapaggregatorRequestPayload"] == null) {
      throw new RequiredError(
        "bridgeSwapaggregatorRequestPayload",
        'Required parameter "bridgeSwapaggregatorRequestPayload" was null or undefined when calling bridgeSwapsLegacy().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/swapaggregator`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["bridgeSwapaggregatorRequestPayload"]
    };
  }
  /**
   * Aggregates swap quotes from all bridges supporting a specified token pair. It allows users to customize their query to prioritize either the best output or the lowest gas costs. The response provides detailed transaction data including bridge identifiers, output amounts, swap routing parameters (such as call data), gas estimates, and a reference, enabling traders to compare and select the most efficient swap route for their needs.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Aggregate Bridge Swap
   */
  async bridgeSwapsLegacyRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgeSwapsLegacyRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Aggregates swap quotes from all bridges supporting a specified token pair. It allows users to customize their query to prioritize either the best output or the lowest gas costs. The response provides detailed transaction data including bridge identifiers, output amounts, swap routing parameters (such as call data), gas estimates, and a reference, enabling traders to compare and select the most efficient swap route for their needs.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Aggregate Bridge Swap
   */
  async bridgeSwapsLegacy(requestParameters, initOverrides) {
    const response = await this.bridgeSwapsLegacyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgeTokens without sending the request
   */
  async bridgeTokensRequestOpts() {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/tokens`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Returns a comprehensive list of tokens that the all the bridge currently supports. The response includes each token’s symbol and its corresponding blockchain address, enabling users to easily determine which assets can be transferred or managed through the bridge.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * Get User Bridge Tokens
   */
  async bridgeTokensRaw(initOverrides) {
    const requestOptions = await this.bridgeTokensRequestOpts();
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Returns a comprehensive list of tokens that the all the bridge currently supports. The response includes each token’s symbol and its corresponding blockchain address, enabling users to easily determine which assets can be transferred or managed through the bridge.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * Get User Bridge Tokens
   */
  async bridgeTokens(initOverrides) {
    const response = await this.bridgeTokensRaw(initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgeTransactions without sending the request
   */
  async bridgeTransactionsRequestOpts(requestParameters) {
    if (requestParameters["transactionHash"] == null) {
      throw new RequiredError(
        "transactionHash",
        'Required parameter "transactionHash" was null or undefined when calling bridgeTransactions().'
      );
    }
    const queryParameters = {};
    if (requestParameters["transactionHash"] != null) {
      queryParameters["transactionHash"] = requestParameters["transactionHash"];
    }
    if (requestParameters["bridgeId"] != null) {
      queryParameters["bridgeId"] = requestParameters["bridgeId"];
    }
    if (requestParameters["srcChainId"] != null) {
      queryParameters["srcChainId"] = requestParameters["srcChainId"];
    }
    if (requestParameters["srcChainSymbol"] != null) {
      queryParameters["srcChainSymbol"] = requestParameters["srcChainSymbol"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/transactions`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves detailed information about a specific cross-chain bridge transaction using its transaction hash. It returns key data such as source and destination addresses, block details, transaction status, and timestamps, enabling users to track and verify the progress and outcome of the transaction.  > 💡  **Compute Unit Value:** `50` (Fixed)
   * Get Bridge Transaction Details
   */
  async bridgeTransactionsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgeTransactionsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves detailed information about a specific cross-chain bridge transaction using its transaction hash. It returns key data such as source and destination addresses, block details, transaction status, and timestamps, enabling users to track and verify the progress and outcome of the transaction.  > 💡  **Compute Unit Value:** `50` (Fixed)
   * Get Bridge Transaction Details
   */
  async bridgeTransactions(requestParameters, initOverrides) {
    const response = await this.bridgeTransactionsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgeTransactionsLegacy without sending the request
   */
  async bridgeTransactionsLegacyRequestOpts(requestParameters) {
    if (requestParameters["transactionHash"] == null) {
      throw new RequiredError(
        "transactionHash",
        'Required parameter "transactionHash" was null or undefined when calling bridgeTransactionsLegacy().'
      );
    }
    const queryParameters = {};
    if (requestParameters["transactionHash"] != null) {
      queryParameters["transactionHash"] = requestParameters["transactionHash"];
    }
    if (requestParameters["bridgeId"] != null) {
      queryParameters["bridgeId"] = requestParameters["bridgeId"];
    }
    if (requestParameters["srcChainId"] != null) {
      queryParameters["srcChainId"] = requestParameters["srcChainId"];
    }
    if (requestParameters["srcChainSymbol"] != null) {
      queryParameters["srcChainSymbol"] = requestParameters["srcChainSymbol"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/gettransaction`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves detailed information about a specific cross-chain bridge transaction using its transaction hash. It returns key data such as source and destination addresses, block details, transaction status, and timestamps, enabling users to track and verify the progress and outcome of the transaction.  > 💡  **Compute Unit Value:** `50` (Fixed)
   * Get Bridge Transaction Details
   */
  async bridgeTransactionsLegacyRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgeTransactionsLegacyRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves detailed information about a specific cross-chain bridge transaction using its transaction hash. It returns key data such as source and destination addresses, block details, transaction status, and timestamps, enabling users to track and verify the progress and outcome of the transaction.  > 💡  **Compute Unit Value:** `50` (Fixed)
   * Get Bridge Transaction Details
   */
  async bridgeTransactionsLegacy(requestParameters, initOverrides) {
    const response = await this.bridgeTransactionsLegacyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for bridgeUnlock without sending the request
   */
  async bridgeUnlockRequestOpts(requestParameters) {
    if (requestParameters["bridgeUnlockRequestPayload"] == null) {
      throw new RequiredError(
        "bridgeUnlockRequestPayload",
        'Required parameter "bridgeUnlockRequestPayload" was null or undefined when calling bridgeUnlock().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/bridge/unlock`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["bridgeUnlockRequestPayload"]
    };
  }
  /**
   * Initiates the unlocking process for assets on the destination blockchain that were previously locked during a bridging operation. This endpoint triggers the release of tokens, making them available for use on the target network. The response provides critical transaction details—including the destination chain ID, sender and receiver addresses, gas usage, and a unique reference ID—allowing developers and users to verify that the unlocking process has been successfully initiated and to track the transaction\'s progress.  > 💡  **Compute Unit Value:** `50` (Fixed)
   * Unlock Tokens for Bridge Swap
   */
  async bridgeUnlockRaw(requestParameters, initOverrides) {
    const requestOptions = await this.bridgeUnlockRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Initiates the unlocking process for assets on the destination blockchain that were previously locked during a bridging operation. This endpoint triggers the release of tokens, making them available for use on the target network. The response provides critical transaction details—including the destination chain ID, sender and receiver addresses, gas usage, and a unique reference ID—allowing developers and users to verify that the unlocking process has been successfully initiated and to track the transaction\'s progress.  > 💡  **Compute Unit Value:** `50` (Fixed)
   * Unlock Tokens for Bridge Swap
   */
  async bridgeUnlock(requestParameters, initOverrides) {
    const response = await this.bridgeUnlockRaw(requestParameters, initOverrides);
    return await response.value();
  }
};
var BridgeGetliquidityBridgeIdEnum = {
  _100: "100",
  _500: "500"
};
var BridgeGetrouteBridgeIdEnum = {
  _600: "600"
};
var BridgeGetsignatureBridgeIdEnum = {
  _300: "300"
};
var BridgeGetsupportedchainsBridgeIdEnum = {
  _600: "600",
  _300: "300",
  _400: "400",
  _500: "500",
  _100: "100"
};
var BridgeGetsupportedtokensBridgeIdEnum = {
  _600: "600",
  _300: "300",
  _400: "400",
  _500: "500",
  _100: "100"
};
var BridgePriceBridgeIdEnum = {
  _600: "600",
  _300: "300",
  _400: "400",
  _500: "500",
  _100: "100"
};
var BridgePriceLegacyBridgeIdEnum = {
  _600: "600",
  _300: "300",
  _400: "400",
  _500: "500",
  _100: "100"
};
var BridgeTransactionsBridgeIdEnum = {
  _100: "100",
  _600: "600",
  _300: "300",
  _400: "400",
  _500: "500"
};
var BridgeTransactionsLegacyBridgeIdEnum = {
  _100: "100",
  _600: "600",
  _300: "300",
  _400: "400",
  _500: "500"
};

// src/apis/ChainApi.ts
var ChainApi = class extends BaseAPI {
  /**
   * Creates request options for accountV1PortfolioLegacy without sending the request
   */
  async accountV1PortfolioLegacyRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling accountV1PortfolioLegacy().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["tokens"] != null) {
      queryParameters["tokens"] = requestParameters["tokens"];
    }
    if (requestParameters["pageToken"] != null) {
      queryParameters["pageToken"] = requestParameters["pageToken"];
    }
    if (requestParameters["assetType"] != null) {
      queryParameters["assetType"] = requestParameters["assetType"];
    }
    if (requestParameters["timestamp"] != null) {
      queryParameters["timestamp"] = requestParameters["timestamp"];
    }
    if (requestParameters["blockNumber"] != null) {
      queryParameters["blockNumber"] = requestParameters["blockNumber"];
    }
    if (requestParameters["include24hrChange"] != null) {
      queryParameters["include24hrChange"] = requestParameters["include24hrChange"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    if (requestParameters["availableOnly"] != null) {
      queryParameters["availableOnly"] = requestParameters["availableOnly"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/portfolio`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Provides a snapshot of a wallet’s asset portfolio across supported blockchains, including native tokens and detailed EVM token information. Each token entry includes metadata (name, symbol, decimals, contract address, balance, USD valuation) and optional filtering to hide zero balances, simplifying portfolio management.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Get User Portfolio
   */
  async accountV1PortfolioLegacyRaw(requestParameters, initOverrides) {
    const requestOptions = await this.accountV1PortfolioLegacyRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Provides a snapshot of a wallet’s asset portfolio across supported blockchains, including native tokens and detailed EVM token information. Each token entry includes metadata (name, symbol, decimals, contract address, balance, USD valuation) and optional filtering to hide zero balances, simplifying portfolio management.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Get User Portfolio
   */
  async accountV1PortfolioLegacy(requestParameters, initOverrides) {
    const response = await this.accountV1PortfolioLegacyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainCreateaccount without sending the request
   */
  async chainCreateaccountRequestOpts(requestParameters) {
    if (requestParameters["chainCreateaccountRequestPayload"] == null) {
      throw new RequiredError(
        "chainCreateaccountRequestPayload",
        'Required parameter "chainCreateaccountRequestPayload" was null or undefined when calling chainCreateaccount().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/createaccount`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["chainCreateaccountRequestPayload"]
    };
  }
  /**
   * Creates a new blockchain account by funding it with an initial transaction, using the recipient’s public key as the account identifier. The response includes details like chain ID, funding source, gas usage, and transaction data, enabling immediate activation and interaction on the network.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Create Account
   */
  async chainCreateaccountRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainCreateaccountRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Creates a new blockchain account by funding it with an initial transaction, using the recipient’s public key as the account identifier. The response includes details like chain ID, funding source, gas usage, and transaction data, enabling immediate activation and interaction on the network.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Create Account
   */
  async chainCreateaccount(requestParameters, initOverrides) {
    const response = await this.chainCreateaccountRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainCreatefeetransaction without sending the request
   */
  async chainCreatefeetransactionRequestOpts(requestParameters) {
    if (requestParameters["chainCreatefeetransactionRequestPayload"] == null) {
      throw new RequiredError(
        "chainCreatefeetransactionRequestPayload",
        'Required parameter "chainCreatefeetransactionRequestPayload" was null or undefined when calling chainCreatefeetransaction().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/createfeetransaction`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["chainCreatefeetransactionRequestPayload"]
    };
  }
  /**
   * Initiates a fee-collection transaction on the blockchain, deducting a default or customizable basis-point fee from the sender. The response returns chain ID, addresses, gas limits, and nonce, ensuring proper verification of fee deductions and transfers.  > 💡  **Compute Unit Value:** `5` (Fixed)
   * Create Fee Transaction
   */
  async chainCreatefeetransactionRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainCreatefeetransactionRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Initiates a fee-collection transaction on the blockchain, deducting a default or customizable basis-point fee from the sender. The response returns chain ID, addresses, gas limits, and nonce, ensuring proper verification of fee deductions and transfers.  > 💡  **Compute Unit Value:** `5` (Fixed)
   * Create Fee Transaction
   */
  async chainCreatefeetransaction(requestParameters, initOverrides) {
    const response = await this.chainCreatefeetransactionRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainDecodetransaction without sending the request
   */
  async chainDecodetransactionRequestOpts(requestParameters) {
    if (requestParameters["chainDecodetransactionRequestPayload"] == null) {
      throw new RequiredError(
        "chainDecodetransactionRequestPayload",
        'Required parameter "chainDecodetransactionRequestPayload" was null or undefined when calling chainDecodetransaction().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/decodetransaction`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["chainDecodetransactionRequestPayload"]
    };
  }
  /**
   * Decodes a raw, signed transaction into a human-readable format, revealing fields like nonce, gas price, recipient, value, and signature components. This helps developers and users audit or debug transactions by highlighting asset changes (sender, receiver, and transferred amount).  > 💡  **Compute Unit Value:** `45` (Fixed)
   * Decode Transaction
   */
  async chainDecodetransactionRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainDecodetransactionRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Decodes a raw, signed transaction into a human-readable format, revealing fields like nonce, gas price, recipient, value, and signature components. This helps developers and users audit or debug transactions by highlighting asset changes (sender, receiver, and transferred amount).  > 💡  **Compute Unit Value:** `45` (Fixed)
   * Decode Transaction
   */
  async chainDecodetransaction(requestParameters, initOverrides) {
    const response = await this.chainDecodetransactionRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainEstimategas without sending the request
   */
  async chainEstimategasRequestOpts(requestParameters) {
    if (requestParameters["chainEstimategasRequestPayload"] == null) {
      throw new RequiredError(
        "chainEstimategasRequestPayload",
        'Required parameter "chainEstimategasRequestPayload" was null or undefined when calling chainEstimategas().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/estimategas`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["chainEstimategasRequestPayload"]
    };
  }
  /**
   * Estimates the gas required for a proposed transaction on EVM-compatible chains by simulating its execution. The response provides an approximate gas limit, helping developers set optimal fees and avoid underfunding errors.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Estimate Gas
   */
  async chainEstimategasRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainEstimategasRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Estimates the gas required for a proposed transaction on EVM-compatible chains by simulating its execution. The response provides an approximate gas limit, helping developers set optimal fees and avoid underfunding errors.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Estimate Gas
   */
  async chainEstimategas(requestParameters, initOverrides) {
    const response = await this.chainEstimategasRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainGenericsmartcontractmethod without sending the request
   */
  async chainGenericsmartcontractmethodRequestOpts(requestParameters) {
    if (requestParameters["chainGenericsmartcontractmethodRequestPayload"] == null) {
      throw new RequiredError(
        "chainGenericsmartcontractmethodRequestPayload",
        'Required parameter "chainGenericsmartcontractmethodRequestPayload" was null or undefined when calling chainGenericsmartcontractmethod().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/genericsmartcontractmethod`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["chainGenericsmartcontractmethodRequestPayload"]
    };
  }
  /**
   * Executes read-only (view) functions on verified smart contracts, enabling queries like token balances or exchange rates without gas costs. The result includes on-chain data returned by the invoked function, facilitating up-to-date insights for decentralized applications.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Generic Smart Contract Method
   */
  async chainGenericsmartcontractmethodRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainGenericsmartcontractmethodRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Executes read-only (view) functions on verified smart contracts, enabling queries like token balances or exchange rates without gas costs. The result includes on-chain data returned by the invoked function, facilitating up-to-date insights for decentralized applications.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Generic Smart Contract Method
   */
  async chainGenericsmartcontractmethod(requestParameters, initOverrides) {
    const response = await this.chainGenericsmartcontractmethodRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainGetapprovaldata without sending the request
   */
  async chainGetapprovaldataRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling chainGetapprovaldata().'
      );
    }
    const queryParameters = {};
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["pageToken"] != null) {
      queryParameters["pageToken"] = requestParameters["pageToken"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/getapprovaldata`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves token approval allowances granted by a specified address, listing spender addresses, token symbols, and allowed amounts. It also provides risk factors and value-at-risk metrics, aiding in auditing permissions and managing DeFi exposures.  > 💡  **Compute Unit Value:** `75` (Fixed)
   * Get Approval Data
   */
  async chainGetapprovaldataRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainGetapprovaldataRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves token approval allowances granted by a specified address, listing spender addresses, token symbols, and allowed amounts. It also provides risk factors and value-at-risk metrics, aiding in auditing permissions and managing DeFi exposures.  > 💡  **Compute Unit Value:** `75` (Fixed)
   * Get Approval Data
   */
  async chainGetapprovaldata(requestParameters, initOverrides) {
    const response = await this.chainGetapprovaldataRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainGetbalance without sending the request
   */
  async chainGetbalanceRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling chainGetbalance().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["blockNumber"] != null) {
      queryParameters["blockNumber"] = requestParameters["blockNumber"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["tokenAddress"] != null) {
      queryParameters["tokenAddress"] = requestParameters["tokenAddress"];
    }
    if (requestParameters["timestamp"] != null) {
      queryParameters["timestamp"] = requestParameters["timestamp"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    if (requestParameters["issuer"] != null) {
      queryParameters["issuer"] = requestParameters["issuer"];
    }
    if (requestParameters["assetCode"] != null) {
      queryParameters["assetCode"] = requestParameters["assetCode"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/getbalance`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * retrieves the current token balance for a specified wallet address on supported blockchain networks, with optional filtering by asset codes and issuers. It returns precise balance data essential for real-time portfolio tracking, fee calculations, and wallet-related operations in decentralized applications  > 💡  **Compute Unit Value:** `50` (Fixed)
   * Get Balance
   */
  async chainGetbalanceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainGetbalanceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * retrieves the current token balance for a specified wallet address on supported blockchain networks, with optional filtering by asset codes and issuers. It returns precise balance data essential for real-time portfolio tracking, fee calculations, and wallet-related operations in decentralized applications  > 💡  **Compute Unit Value:** `50` (Fixed)
   * Get Balance
   */
  async chainGetbalance(requestParameters, initOverrides) {
    const response = await this.chainGetbalanceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainGetblock without sending the request
   */
  async chainGetblockRequestOpts(requestParameters) {
    if (requestParameters["blockNumber"] == null) {
      throw new RequiredError(
        "blockNumber",
        'Required parameter "blockNumber" was null or undefined when calling chainGetblock().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["blockNumber"] != null) {
      queryParameters["blockNumber"] = requestParameters["blockNumber"];
    }
    if (requestParameters["startBlock"] != null) {
      queryParameters["startBlock"] = requestParameters["startBlock"];
    }
    if (requestParameters["endBlock"] != null) {
      queryParameters["endBlock"] = requestParameters["endBlock"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/getblock`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves comprehensive information about a specific block in the blockchain, including its hash, number, timestamp, and all contained transactions. It provides essential data for developers and operators to analyze blockchain history, verify transactions, and monitor network performance.  > 💡  **Compute Unit Value:** `45` (Fixed)
   * Get Block
   */
  async chainGetblockRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainGetblockRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves comprehensive information about a specific block in the blockchain, including its hash, number, timestamp, and all contained transactions. It provides essential data for developers and operators to analyze blockchain history, verify transactions, and monitor network performance.  > 💡  **Compute Unit Value:** `45` (Fixed)
   * Get Block
   */
  async chainGetblock(requestParameters, initOverrides) {
    const response = await this.chainGetblockRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainGetevents without sending the request
   */
  async chainGeteventsRequestOpts(requestParameters) {
    if (requestParameters["contractAddress"] == null) {
      throw new RequiredError(
        "contractAddress",
        'Required parameter "contractAddress" was null or undefined when calling chainGetevents().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["contractAddress"] != null) {
      queryParameters["contractAddress"] = requestParameters["contractAddress"];
    }
    if (requestParameters["sortOrder"] != null) {
      queryParameters["sortOrder"] = requestParameters["sortOrder"];
    }
    if (requestParameters["pageToken"] != null) {
      queryParameters["pageToken"] = requestParameters["pageToken"];
    }
    if (requestParameters["startBlock"] != null) {
      queryParameters["startBlock"] = requestParameters["startBlock"];
    }
    if (requestParameters["contracts"] != null) {
      queryParameters["contracts"] = requestParameters["contracts"];
    }
    if (requestParameters["topics1"] != null) {
      queryParameters["topics1"] = requestParameters["topics1"];
    }
    if (requestParameters["topics2"] != null) {
      queryParameters["topics2"] = requestParameters["topics2"];
    }
    if (requestParameters["topics3"] != null) {
      queryParameters["topics3"] = requestParameters["topics3"];
    }
    if (requestParameters["topics4"] != null) {
      queryParameters["topics4"] = requestParameters["topics4"];
    }
    if (requestParameters["topics5"] != null) {
      queryParameters["topics5"] = requestParameters["topics5"];
    }
    if (requestParameters["type"] != null) {
      queryParameters["type"] = requestParameters["type"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/getevents`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves blockchain events emitted within a specified ledger range (up to 10 hours from the latest ledger), providing detailed information on event types, ledger numbers, closing times, and unique identifiers. It returns structured data with topics and values that help developers monitor on-chain activities, debug smart contract interactions, and track blockchain state changes.  > 💡  **Compute Unit Value:** `10` (Fixed)
   * Get Events
   */
  async chainGeteventsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainGeteventsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves blockchain events emitted within a specified ledger range (up to 10 hours from the latest ledger), providing detailed information on event types, ledger numbers, closing times, and unique identifiers. It returns structured data with topics and values that help developers monitor on-chain activities, debug smart contract interactions, and track blockchain state changes.  > 💡  **Compute Unit Value:** `10` (Fixed)
   * Get Events
   */
  async chainGetevents(requestParameters, initOverrides) {
    const response = await this.chainGeteventsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainGetgasfees without sending the request
   */
  async chainGetgasfeesRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling chainGetgasfees().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["startBlock"] != null) {
      queryParameters["startBlock"] = requestParameters["startBlock"];
    }
    if (requestParameters["endBlock"] != null) {
      queryParameters["endBlock"] = requestParameters["endBlock"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/getgasfees`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves detailed gas fee metrics for a specified user address over a defined block range, including total fees spent and current gas prices.  > 💡  **Compute Unit Value:** `70` (Fixed)
   * Get Gas Fees
   */
  async chainGetgasfeesRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainGetgasfeesRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves detailed gas fee metrics for a specified user address over a defined block range, including total fees spent and current gas prices.  > 💡  **Compute Unit Value:** `70` (Fixed)
   * Get Gas Fees
   */
  async chainGetgasfees(requestParameters, initOverrides) {
    const response = await this.chainGetgasfeesRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainGetgasprice without sending the request
   */
  async chainGetgaspriceRequestOpts(requestParameters) {
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/getgasprice`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Fetches the current gas price from the specified blockchain network.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Get Gas Price
   */
  async chainGetgaspriceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainGetgaspriceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Fetches the current gas price from the specified blockchain network.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Get Gas Price
   */
  async chainGetgasprice(requestParameters = {}, initOverrides) {
    const response = await this.chainGetgaspriceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainGethistoricalrewards without sending the request
   */
  async chainGethistoricalrewardsRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling chainGethistoricalrewards().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["pageToken"] != null) {
      queryParameters["pageToken"] = requestParameters["pageToken"];
    }
    if (requestParameters["sortOrder"] != null) {
      queryParameters["sortOrder"] = requestParameters["sortOrder"];
    }
    if (requestParameters["currency"] != null) {
      queryParameters["currency"] = requestParameters["currency"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/gethistoricalrewards`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves historical staking reward data for specified addresses, including transaction hashes, unique IDs (UTXO or node), and the staked amount with start/end timestamps. The response also provides asset details (identifier, name, symbol, etc.), reward amounts, and reward types (e.g., DELEGATOR), supporting thorough audits and portfolio integrations.  > 💡  **Compute Unit Value:** `50` (Fixed)
   * Get Historical Rewards
   */
  async chainGethistoricalrewardsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainGethistoricalrewardsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves historical staking reward data for specified addresses, including transaction hashes, unique IDs (UTXO or node), and the staked amount with start/end timestamps. The response also provides asset details (identifier, name, symbol, etc.), reward amounts, and reward types (e.g., DELEGATOR), supporting thorough audits and portfolio integrations.  > 💡  **Compute Unit Value:** `50` (Fixed)
   * Get Historical Rewards
   */
  async chainGethistoricalrewards(requestParameters, initOverrides) {
    const response = await this.chainGethistoricalrewardsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainGetlatestledger without sending the request
   */
  async chainGetlatestledgerRequestOpts(requestParameters) {
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/getlatestledger`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Fetches the most recent finalized block from the blockchain and returns comprehensive real-time information about its state. The endpoint provides key block metadata—including the block number, hash, parent hash, timestamp, and transaction details—allowing users and developers to monitor current network activity and verify the latest ledger status.  > 💡  **Compute Unit Value:** `85` (Fixed)
   * Get Latest Ledger
   */
  async chainGetlatestledgerRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainGetlatestledgerRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Fetches the most recent finalized block from the blockchain and returns comprehensive real-time information about its state. The endpoint provides key block metadata—including the block number, hash, parent hash, timestamp, and transaction details—allowing users and developers to monitor current network activity and verify the latest ledger status.  > 💡  **Compute Unit Value:** `85` (Fixed)
   * Get Latest Ledger
   */
  async chainGetlatestledger(requestParameters = {}, initOverrides) {
    const response = await this.chainGetlatestledgerRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainGetstakepositions without sending the request
   */
  async chainGetstakepositionsRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling chainGetstakepositions().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["validatorAddress"] != null) {
      queryParameters["validatorAddress"] = requestParameters["validatorAddress"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/getstakepositions`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves details about staked native assets for a specified address, including the total amount staked across all validators and a breakdown of each position. For every stake, it provides the staked amount, staker/withdrawer addresses, validator ID, rent-exempt reserve, and activation/deactivation epochs for thorough monitoring and auditing.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Get Stake Positions
   */
  async chainGetstakepositionsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainGetstakepositionsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves details about staked native assets for a specified address, including the total amount staked across all validators and a breakdown of each position. For every stake, it provides the staked amount, staker/withdrawer addresses, validator ID, rent-exempt reserve, and activation/deactivation epochs for thorough monitoring and auditing.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Get Stake Positions
   */
  async chainGetstakepositions(requestParameters, initOverrides) {
    const response = await this.chainGetstakepositionsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainGetstorage without sending the request
   */
  async chainGetstorageRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling chainGetstorage().'
      );
    }
    if (requestParameters["keys"] == null) {
      throw new RequiredError(
        "keys",
        'Required parameter "keys" was null or undefined when calling chainGetstorage().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["index"] != null) {
      queryParameters["index"] = requestParameters["index"];
    }
    if (requestParameters["keys"] != null) {
      queryParameters["keys"] = requestParameters["keys"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/getstorage`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Accesses and returns detailed storage data for a specified smart contract, including its state variables, code, and ledger entries. This endpoint leverages base64-encoded keys to accurately retrieve binary data from the contract’s storage, ensuring that complex on-chain state information is properly encoded and accessible.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Get Storage
   */
  async chainGetstorageRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainGetstorageRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Accesses and returns detailed storage data for a specified smart contract, including its state variables, code, and ledger entries. This endpoint leverages base64-encoded keys to accurately retrieve binary data from the contract’s storage, ensuring that complex on-chain state information is properly encoded and accessible.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Get Storage
   */
  async chainGetstorage(requestParameters, initOverrides) {
    const response = await this.chainGetstorageRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainGetsuibalance without sending the request
   */
  async chainGetsuibalanceRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling chainGetsuibalance().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["blockNumber"] != null) {
      queryParameters["blockNumber"] = requestParameters["blockNumber"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["tokenAddress"] != null) {
      queryParameters["tokenAddress"] = requestParameters["tokenAddress"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/getsuibalance`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves the current or historical token balance for a specified SUI blockchain address, with the option to specify a block number for precise point-in-time analysis.  > 💡  **Compute Unit Value:** `185` (Fixed)
   * Get Sui Balance
   */
  async chainGetsuibalanceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainGetsuibalanceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves the current or historical token balance for a specified SUI blockchain address, with the option to specify a block number for precise point-in-time analysis.  > 💡  **Compute Unit Value:** `185` (Fixed)
   * Get Sui Balance
   */
  async chainGetsuibalance(requestParameters, initOverrides) {
    const response = await this.chainGetsuibalanceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainGettickerprice without sending the request
   */
  async chainGettickerpriceRequestOpts(requestParameters) {
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/gettickerprice`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieve available token symbols and their addresses supported by the streamTickerPrice service. Currently limited to Ethereum blockchain tokens.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Get Ticker Price
   */
  async chainGettickerpriceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainGettickerpriceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieve available token symbols and their addresses supported by the streamTickerPrice service. Currently limited to Ethereum blockchain tokens.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Get Ticker Price
   */
  async chainGettickerprice(requestParameters = {}, initOverrides) {
    const response = await this.chainGettickerpriceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainGettokenaddress without sending the request
   */
  async chainGettokenaddressRequestOpts(requestParameters) {
    if (requestParameters["tokenSymbol"] == null) {
      throw new RequiredError(
        "tokenSymbol",
        'Required parameter "tokenSymbol" was null or undefined when calling chainGettokenaddress().'
      );
    }
    const queryParameters = {};
    if (requestParameters["tokenSymbol"] != null) {
      queryParameters["tokenSymbol"] = requestParameters["tokenSymbol"];
    }
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/gettokenaddress`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Maps token symbols to their corresponding smart contract addresses on specified blockchain networks, enabling developers to verify and interact with tokens in decentralized applications. The response returns the token’s address in hexadecimal format, crucial for executing transactions, integrating with smart contracts, and maintaining data integrity.  > 💡  **Compute Unit Value:** `5` (Fixed)
   * Get Token Address
   */
  async chainGettokenaddressRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainGettokenaddressRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Maps token symbols to their corresponding smart contract addresses on specified blockchain networks, enabling developers to verify and interact with tokens in decentralized applications. The response returns the token’s address in hexadecimal format, crucial for executing transactions, integrating with smart contracts, and maintaining data integrity.  > 💡  **Compute Unit Value:** `5` (Fixed)
   * Get Token Address
   */
  async chainGettokenaddress(requestParameters, initOverrides) {
    const response = await this.chainGettokenaddressRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainGettransaction without sending the request
   */
  async chainGettransactionRequestOpts(requestParameters) {
    if (requestParameters["transactionHash"] == null) {
      throw new RequiredError(
        "transactionHash",
        'Required parameter "transactionHash" was null or undefined when calling chainGettransaction().'
      );
    }
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling chainGettransaction().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["transactionHash"] != null) {
      queryParameters["transactionHash"] = requestParameters["transactionHash"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/gettransaction`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves comprehensive details for a specific blockchain transaction (hash, block number, sender/recipient, status, fee/gas metrics). Also provides the full encoded input, signature components, and contract event logs if applicable, enabling thorough transaction verification, troubleshooting, and on-chain analysis.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Get Transaction
   */
  async chainGettransactionRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainGettransactionRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves comprehensive details for a specific blockchain transaction (hash, block number, sender/recipient, status, fee/gas metrics). Also provides the full encoded input, signature components, and contract event logs if applicable, enabling thorough transaction verification, troubleshooting, and on-chain analysis.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Get Transaction
   */
  async chainGettransaction(requestParameters, initOverrides) {
    const response = await this.chainGettransactionRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainGetusertransactions without sending the request
   */
  async chainGetusertransactionsRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling chainGetusertransactions().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["endBlock"] != null) {
      queryParameters["endBlock"] = requestParameters["endBlock"];
    }
    if (requestParameters["startBlock"] != null) {
      queryParameters["startBlock"] = requestParameters["startBlock"];
    }
    if (requestParameters["pageToken"] != null) {
      queryParameters["pageToken"] = requestParameters["pageToken"];
    }
    if (requestParameters["sortOrder"] != null) {
      queryParameters["sortOrder"] = requestParameters["sortOrder"];
    }
    if (requestParameters["pageSize"] != null) {
      queryParameters["pageSize"] = requestParameters["pageSize"];
    }
    if (requestParameters["fromTimestamp"] != null) {
      queryParameters["fromTimestamp"] = requestParameters["fromTimestamp"];
    }
    if (requestParameters["toTimestamp"] != null) {
      queryParameters["toTimestamp"] = requestParameters["toTimestamp"];
    }
    if (requestParameters["limit"] != null) {
      queryParameters["limit"] = requestParameters["limit"];
    }
    if (requestParameters["beforeBlock"] != null) {
      queryParameters["beforeBlock"] = requestParameters["beforeBlock"];
    }
    if (requestParameters["afterBlock"] != null) {
      queryParameters["afterBlock"] = requestParameters["afterBlock"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/getusertransactions`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves historical transaction details for one or more user addresses over a defined block range, including both simple native transfers and complex contract interactions. For each transaction, the response provides the hash, nonce, sender/receiver addresses, value, gas details, block info, and any ERC20/NFT/native token transfers, along with summaries, categorization (send, receive, etc.), and pagination via a next-page token.  > 💡  **Compute Unit Value:** `245` (Fixed)
   * Get User Transactions
   */
  async chainGetusertransactionsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainGetusertransactionsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves historical transaction details for one or more user addresses over a defined block range, including both simple native transfers and complex contract interactions. For each transaction, the response provides the hash, nonce, sender/receiver addresses, value, gas details, block info, and any ERC20/NFT/native token transfers, along with summaries, categorization (send, receive, etc.), and pagination via a next-page token.  > 💡  **Compute Unit Value:** `245` (Fixed)
   * Get User Transactions
   */
  async chainGetusertransactions(requestParameters, initOverrides) {
    const response = await this.chainGetusertransactionsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainRegistercoin without sending the request
   */
  async chainRegistercoinRequestOpts(requestParameters) {
    if (requestParameters["chainRegistercoinRequestPayload"] == null) {
      throw new RequiredError(
        "chainRegistercoinRequestPayload",
        'Required parameter "chainRegistercoinRequestPayload" was null or undefined when calling chainRegistercoin().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/registercoin`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["chainRegistercoinRequestPayload"]
    };
  }
  /**
   * This API creates a payload to register a coin on the Aptos wallet address.  > 💡  **Compute Unit Value:** `55` (Fixed)
   * Register Coin
   */
  async chainRegistercoinRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainRegistercoinRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * This API creates a payload to register a coin on the Aptos wallet address.  > 💡  **Compute Unit Value:** `55` (Fixed)
   * Register Coin
   */
  async chainRegistercoin(requestParameters, initOverrides) {
    const response = await this.chainRegistercoinRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainSendtransaction without sending the request
   */
  async chainSendtransactionRequestOpts(requestParameters) {
    if (requestParameters["chainSendtransactionRequestPayload"] == null) {
      throw new RequiredError(
        "chainSendtransactionRequestPayload",
        'Required parameter "chainSendtransactionRequestPayload" was null or undefined when calling chainSendtransaction().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/sendtransaction`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["chainSendtransactionRequestPayload"]
    };
  }
  /**
   * Submits a signed transaction to the blockchain for processing. This endpoint broadcasts the transaction data to the network and returns a unique transaction hash that can be used to track its confirmation status.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Send Transaction
   */
  async chainSendtransactionRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainSendtransactionRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Submits a signed transaction to the blockchain for processing. This endpoint broadcasts the transaction data to the network and returns a unique transaction hash that can be used to track its confirmation status.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Send Transaction
   */
  async chainSendtransaction(requestParameters, initOverrides) {
    const response = await this.chainSendtransactionRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for chainSimulatetransaction without sending the request
   */
  async chainSimulatetransactionRequestOpts(requestParameters) {
    if (requestParameters["chainSimulatetransactionRequestPayload"] == null) {
      throw new RequiredError(
        "chainSimulatetransactionRequestPayload",
        'Required parameter "chainSimulatetransactionRequestPayload" was null or undefined when calling chainSimulatetransaction().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/chain/simulatetransaction`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["chainSimulatetransactionRequestPayload"]
    };
  }
  /**
   * Simulates a signed transaction in the current network context without broadcasting, returning an encoded outcome, potential events, and resource cost metrics (CPU, memory). The response also includes the minimum resource fee and ledger number, enabling developers to verify, optimize, and confirm transaction behavior before committing funds.  > 💡  **Compute Unit Value:** `50` (Fixed)
   * Simulate Transaction
   */
  async chainSimulatetransactionRaw(requestParameters, initOverrides) {
    const requestOptions = await this.chainSimulatetransactionRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Simulates a signed transaction in the current network context without broadcasting, returning an encoded outcome, potential events, and resource cost metrics (CPU, memory). The response also includes the minimum resource fee and ledger number, enabling developers to verify, optimize, and confirm transaction behavior before committing funds.  > 💡  **Compute Unit Value:** `50` (Fixed)
   * Simulate Transaction
   */
  async chainSimulatetransaction(requestParameters, initOverrides) {
    const response = await this.chainSimulatetransactionRaw(requestParameters, initOverrides);
    return await response.value();
  }
};
var AccountV1PortfolioLegacyChainIdEnum = {
  _1: "1",
  _8217: "8217",
  _56: "56",
  _25: "25",
  _43114: "43114",
  _900: "900",
  _901: "901",
  _1200: "1200",
  _42161: "42161",
  _10: "10",
  _1600: "1600",
  _1601: "1601",
  _1700: "1700",
  _1701: "1701",
  Osmosis1: "osmosis-1",
  OsmoTest5: "osmo-test-5",
  _137: "137",
  _250: "250",
  _8453: "8453",
  _1100: "1100",
  _1500: "1500",
  _1501: "1501"
};
var AccountV1PortfolioLegacyAssetTypeEnum = {
  Fungible: "fungible",
  NonFungible: "nonFungible",
  All: "all"
};
var ChainGetapprovaldataChainIdEnum = {
  _1: "1",
  _137: "137",
  _56: "56",
  _43114: "43114",
  _25: "25",
  _42161: "42161",
  _10: "10",
  _8453: "8453",
  _250: "250"
};
var ChainGetbalanceChainIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _56: "56",
  _25: "25",
  _97: "97",
  _43114: "43114",
  _338: "338",
  _900: "900",
  _901: "901",
  _1000: "1000",
  _1002: "1002",
  _1200: "1200",
  _1201: "1201",
  _43113: "43113",
  _42161: "42161",
  _421614: "421614",
  _10: "10",
  _11155420: "11155420",
  _4002: "4002",
  _324: "324",
  _300: "300",
  _1300: "1300",
  _1301: "1301",
  Cosmoshub4: "cosmoshub-4",
  _1600: "1600",
  _1601: "1601",
  _1700: "1700",
  _1701: "1701",
  Osmosis1: "osmosis-1",
  OsmoTest5: "osmo-test-5",
  _1800: "1800",
  _137: "137",
  _80002: "80002",
  _250: "250",
  _8453: "8453",
  _84532: "84532",
  _1400: "1400",
  _1401: "1401",
  _400: "400",
  _401: "401",
  _1100: "1100",
  _1101: "1101",
  _1500: "1500",
  _1501: "1501",
  _17000: "17000",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636",
  _560048: "560048",
  _8217: "8217",
  _1001: "1001"
};
var ChainGetblockChainIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _56: "56",
  _25: "25",
  _43114: "43114",
  _900: "900",
  _901: "901",
  _1000: "1000",
  _1002: "1002",
  _1200: "1200",
  _1201: "1201",
  _43113: "43113",
  _42161: "42161",
  _421614: "421614",
  _10: "10",
  _11155420: "11155420",
  _4002: "4002",
  _324: "324",
  _300: "300",
  _1300: "1300",
  _1301: "1301",
  Cosmoshub4: "cosmoshub-4",
  _1600: "1600",
  _1601: "1601",
  _1700: "1700",
  _1701: "1701",
  Osmosis1: "osmosis-1",
  OsmoTest5: "osmo-test-5",
  _1800: "1800",
  _1801: "1801",
  _137: "137",
  _250: "250",
  _101: "101",
  _102: "102",
  _103: "103",
  _8453: "8453",
  _1400: "1400",
  _1401: "1401",
  _400: "400",
  _401: "401",
  _1100: "1100",
  _1101: "1101",
  _1500: "1500",
  _1501: "1501",
  _17000: "17000",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636",
  _8217: "8217",
  _1001: "1001"
};
var ChainGeteventsChainIdEnum = {
  _1700: "1700",
  _1701: "1701",
  _1500: "1500",
  _1501: "1501"
};
var ChainGeteventsSortOrderEnum = {
  Desc: "desc",
  Asc: "asc"
};
var ChainGeteventsTypeEnum = {
  Approval: "Approval",
  Transfer: "Transfer"
};
var ChainGetgasfeesChainIdEnum = {
  _1: "1"
};
var ChainGetgaspriceChainIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _56: "56",
  _25: "25",
  _97: "97",
  _43114: "43114",
  _338: "338",
  _1200: "1200",
  _1201: "1201",
  _43113: "43113",
  _42161: "42161",
  _10: "10",
  _11155420: "11155420",
  _4002: "4002",
  _324: "324",
  _300: "300",
  _101: "101",
  _102: "102",
  _103: "103",
  Cosmoshub4: "cosmoshub-4",
  _1600: "1600",
  _1601: "1601",
  _1700: "1700",
  _1701: "1701",
  Osmosis1: "osmosis-1",
  OsmoTest5: "osmo-test-5",
  _1800: "1800",
  _137: "137",
  _421614: "421614",
  _80002: "80002",
  _250: "250",
  _8453: "8453",
  _84532: "84532",
  _1100: "1100",
  _1101: "1101",
  _1400: "1400",
  _1401: "1401",
  _1500: "1500",
  _1501: "1501",
  _17000: "17000",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636",
  _8217: "8217",
  _1001: "1001"
};
var ChainGethistoricalrewardsChainIdEnum = {
  _43115: "43115"
};
var ChainGethistoricalrewardsSortOrderEnum = {
  Desc: "desc",
  Asc: "asc"
};
var ChainGetlatestledgerChainIdEnum = {
  _1600: "1600",
  _1601: "1601",
  _1500: "1500",
  _1501: "1501"
};
var ChainGetstakepositionsChainIdEnum = {
  _900: "900",
  _901: "901",
  _1100: "1100",
  _1200: "1200"
};
var ChainGetstorageChainIdEnum = {
  _1: "1",
  _25: "25",
  _11155111: "11155111",
  _56: "56",
  _97: "97",
  _43114: "43114",
  _338: "338",
  _43113: "43113",
  _42161: "42161",
  _421614: "421614",
  _10: "10",
  _11155420: "11155420",
  _4002: "4002",
  _324: "324",
  _300: "300",
  _101: "101",
  _102: "102",
  _103: "103",
  _137: "137",
  _80002: "80002",
  _250: "250",
  _8453: "8453",
  _84532: "84532",
  _400: "400",
  _401: "401",
  _1500: "1500",
  _1501: "1501",
  _17000: "17000",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636",
  _8217: "8217",
  _1001: "1001"
};
var ChainGetsuibalanceChainIdEnum = {
  _101: "101",
  _102: "102",
  _103: "103"
};
var ChainGettickerpriceChainIdEnum = {
  _1: "1"
};
var ChainGettokenaddressChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _137: "137",
  _25: "25",
  _42161: "42161",
  _250: "250",
  _10: "10",
  _900: "900",
  _1000: "1000",
  _1200: "1200",
  _1300: "1300",
  _101: "101",
  _1400: "1400"
};
var ChainGettransactionChainIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _56: "56",
  _25: "25",
  _97: "97",
  _43114: "43114",
  _338: "338",
  _900: "900",
  _901: "901",
  _1000: "1000",
  _1002: "1002",
  _1200: "1200",
  _1201: "1201",
  _43113: "43113",
  _42161: "42161",
  _421614: "421614",
  _10: "10",
  _11155420: "11155420",
  _4002: "4002",
  _324: "324",
  _300: "300",
  _101: "101",
  _102: "102",
  _103: "103",
  _1300: "1300",
  _1301: "1301",
  Cosmoshub4: "cosmoshub-4",
  _1600: "1600",
  _1601: "1601",
  _1700: "1700",
  _1701: "1701",
  Osmosis1: "osmosis-1",
  OsmoTest5: "osmo-test-5",
  _1800: "1800",
  _1801: "1801",
  _137: "137",
  _80002: "80002",
  _250: "250",
  _8453: "8453",
  _84532: "84532",
  _1400: "1400",
  _1401: "1401",
  _400: "400",
  _401: "401",
  _1100: "1100",
  _1101: "1101",
  _1500: "1500",
  _1501: "1501",
  _17000: "17000",
  _8217: "8217",
  _1001: "1001"
};
var ChainGetusertransactionsChainIdEnum = {
  _43115: "43115",
  _43116: "43116",
  _43114: "43114",
  _1: "1",
  _56: "56",
  _137: "137",
  _250: "250",
  _10: "10",
  _42161: "42161",
  _25: "25",
  _8453: "8453",
  _1100: "1100",
  _1101: "1101",
  _900: "900",
  _1500: "1500",
  _1501: "1501",
  _1600: "1600",
  _1601: "1601",
  _1700: "1700",
  _1701: "1701",
  _1800: "1800",
  _1400: "1400"
};
var ChainGetusertransactionsSortOrderEnum = {
  Desc: "desc",
  Asc: "asc"
};

// src/apis/DerivativeApi.ts
var DerivativeApi = class extends BaseAPI {
  /**
   * Creates request options for derivativeGetassets without sending the request
   */
  async derivativeGetassetsRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling derivativeGetassets().'
      );
    }
    if (requestParameters["subAccountNumber"] == null) {
      throw new RequiredError(
        "subAccountNumber",
        'Required parameter "subAccountNumber" was null or undefined when calling derivativeGetassets().'
      );
    }
    const queryParameters = {};
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["derivativeId"] != null) {
      queryParameters["derivativeId"] = requestParameters["derivativeId"];
    }
    if (requestParameters["subAccountNumber"] != null) {
      queryParameters["subAccountNumber"] = requestParameters["subAccountNumber"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/derivative/getassets`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Fetches a list of assets deposited in dYdX for the specified address and sub-account.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Get Assets
   */
  async derivativeGetassetsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.derivativeGetassetsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Fetches a list of assets deposited in dYdX for the specified address and sub-account.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Get Assets
   */
  async derivativeGetassets(requestParameters, initOverrides) {
    const response = await this.derivativeGetassetsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for derivativeGetfills without sending the request
   */
  async derivativeGetfillsRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling derivativeGetfills().'
      );
    }
    if (requestParameters["subAccountNumber"] == null) {
      throw new RequiredError(
        "subAccountNumber",
        'Required parameter "subAccountNumber" was null or undefined when calling derivativeGetfills().'
      );
    }
    const queryParameters = {};
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["derivativeId"] != null) {
      queryParameters["derivativeId"] = requestParameters["derivativeId"];
    }
    if (requestParameters["subAccountNumber"] != null) {
      queryParameters["subAccountNumber"] = requestParameters["subAccountNumber"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/derivative/getfills`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves the fill details for executed orders associated with a specified address and sub-account.  > 💡  **Compute Unit Value:** `20` (Fixed)
   * Get Fills
   */
  async derivativeGetfillsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.derivativeGetfillsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves the fill details for executed orders associated with a specified address and sub-account.  > 💡  **Compute Unit Value:** `20` (Fixed)
   * Get Fills
   */
  async derivativeGetfills(requestParameters, initOverrides) {
    const response = await this.derivativeGetfillsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for derivativeGethistoricalpnl without sending the request
   */
  async derivativeGethistoricalpnlRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling derivativeGethistoricalpnl().'
      );
    }
    if (requestParameters["subAccountNumber"] == null) {
      throw new RequiredError(
        "subAccountNumber",
        'Required parameter "subAccountNumber" was null or undefined when calling derivativeGethistoricalpnl().'
      );
    }
    const queryParameters = {};
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["derivativeId"] != null) {
      queryParameters["derivativeId"] = requestParameters["derivativeId"];
    }
    if (requestParameters["subAccountNumber"] != null) {
      queryParameters["subAccountNumber"] = requestParameters["subAccountNumber"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/derivative/gethistoricalpnl`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves historical profit and loss records for the specified address and sub-account.  > 💡  **Compute Unit Value:** `65` (Fixed)
   * Get Historical PnL
   */
  async derivativeGethistoricalpnlRaw(requestParameters, initOverrides) {
    const requestOptions = await this.derivativeGethistoricalpnlRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves historical profit and loss records for the specified address and sub-account.  > 💡  **Compute Unit Value:** `65` (Fixed)
   * Get Historical PnL
   */
  async derivativeGethistoricalpnl(requestParameters, initOverrides) {
    const response = await this.derivativeGethistoricalpnlRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for derivativeGetorder without sending the request
   */
  async derivativeGetorderRequestOpts(requestParameters) {
    if (requestParameters["orderId"] == null) {
      throw new RequiredError(
        "orderId",
        'Required parameter "orderId" was null or undefined when calling derivativeGetorder().'
      );
    }
    const queryParameters = {};
    if (requestParameters["orderId"] != null) {
      queryParameters["orderId"] = requestParameters["orderId"];
    }
    if (requestParameters["derivativeId"] != null) {
      queryParameters["derivativeId"] = requestParameters["derivativeId"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/derivative/getorder`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Fetches detailed information for a single order identified by its order ID.  > 💡  **Compute Unit Value:** `10` (Fixed)
   * Get Order
   */
  async derivativeGetorderRaw(requestParameters, initOverrides) {
    const requestOptions = await this.derivativeGetorderRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Fetches detailed information for a single order identified by its order ID.  > 💡  **Compute Unit Value:** `10` (Fixed)
   * Get Order
   */
  async derivativeGetorder(requestParameters, initOverrides) {
    const response = await this.derivativeGetorderRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for derivativeGetorders without sending the request
   */
  async derivativeGetordersRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling derivativeGetorders().'
      );
    }
    if (requestParameters["subAccountNumber"] == null) {
      throw new RequiredError(
        "subAccountNumber",
        'Required parameter "subAccountNumber" was null or undefined when calling derivativeGetorders().'
      );
    }
    const queryParameters = {};
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["derivativeId"] != null) {
      queryParameters["derivativeId"] = requestParameters["derivativeId"];
    }
    if (requestParameters["subAccountNumber"] != null) {
      queryParameters["subAccountNumber"] = requestParameters["subAccountNumber"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/derivative/getorders`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a comprehensive list of all orders for a given address and sub-account.  > 💡  **Compute Unit Value:** `15` (Fixed)
   * Get Orders
   */
  async derivativeGetordersRaw(requestParameters, initOverrides) {
    const requestOptions = await this.derivativeGetordersRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a comprehensive list of all orders for a given address and sub-account.  > 💡  **Compute Unit Value:** `15` (Fixed)
   * Get Orders
   */
  async derivativeGetorders(requestParameters, initOverrides) {
    const response = await this.derivativeGetordersRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for derivativeGetperpetualpositions without sending the request
   */
  async derivativeGetperpetualpositionsRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling derivativeGetperpetualpositions().'
      );
    }
    if (requestParameters["subAccountNumber"] == null) {
      throw new RequiredError(
        "subAccountNumber",
        'Required parameter "subAccountNumber" was null or undefined when calling derivativeGetperpetualpositions().'
      );
    }
    const queryParameters = {};
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["derivativeId"] != null) {
      queryParameters["derivativeId"] = requestParameters["derivativeId"];
    }
    if (requestParameters["subAccountNumber"] != null) {
      queryParameters["subAccountNumber"] = requestParameters["subAccountNumber"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/derivative/getperpetualpositions`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Fetches the current (or historical) perpetual positions for a specified address and sub-account.  > 💡  **Compute Unit Value:** `35` (Fixed)
   * Get Perpetual Positions
   */
  async derivativeGetperpetualpositionsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.derivativeGetperpetualpositionsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Fetches the current (or historical) perpetual positions for a specified address and sub-account.  > 💡  **Compute Unit Value:** `35` (Fixed)
   * Get Perpetual Positions
   */
  async derivativeGetperpetualpositions(requestParameters, initOverrides) {
    const response = await this.derivativeGetperpetualpositionsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for derivativeGetsubaccounts without sending the request
   */
  async derivativeGetsubaccountsRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling derivativeGetsubaccounts().'
      );
    }
    const queryParameters = {};
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["derivativeId"] != null) {
      queryParameters["derivativeId"] = requestParameters["derivativeId"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/derivative/getsubaccounts`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves details for sub-accounts linked to a given address.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Get Sub-Accounts
   */
  async derivativeGetsubaccountsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.derivativeGetsubaccountsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves details for sub-accounts linked to a given address.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Get Sub-Accounts
   */
  async derivativeGetsubaccounts(requestParameters, initOverrides) {
    const response = await this.derivativeGetsubaccountsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for derivativeGettransfers without sending the request
   */
  async derivativeGettransfersRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling derivativeGettransfers().'
      );
    }
    if (requestParameters["subAccountNumber"] == null) {
      throw new RequiredError(
        "subAccountNumber",
        'Required parameter "subAccountNumber" was null or undefined when calling derivativeGettransfers().'
      );
    }
    const queryParameters = {};
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["derivativeId"] != null) {
      queryParameters["derivativeId"] = requestParameters["derivativeId"];
    }
    if (requestParameters["subAccountNumber"] != null) {
      queryParameters["subAccountNumber"] = requestParameters["subAccountNumber"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/derivative/gettransfers`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Fetches detailed information on transfer transactions for a given address and sub-account.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Get Transfer
   */
  async derivativeGettransfersRaw(requestParameters, initOverrides) {
    const requestOptions = await this.derivativeGettransfersRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Fetches detailed information on transfer transactions for a given address and sub-account.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Get Transfer
   */
  async derivativeGettransfers(requestParameters, initOverrides) {
    const response = await this.derivativeGettransfersRaw(requestParameters, initOverrides);
    return await response.value();
  }
};
var DerivativeGetassetsDerivativeIdEnum = {
  _1000: "1000"
};
var DerivativeGetfillsDerivativeIdEnum = {
  _1000: "1000"
};
var DerivativeGethistoricalpnlDerivativeIdEnum = {
  _1000: "1000"
};
var DerivativeGetorderDerivativeIdEnum = {
  _1000: "1000"
};
var DerivativeGetordersDerivativeIdEnum = {
  _1000: "1000"
};
var DerivativeGetperpetualpositionsDerivativeIdEnum = {
  _1000: "1000"
};
var DerivativeGetsubaccountsDerivativeIdEnum = {
  _1000: "1000"
};
var DerivativeGettransfersDerivativeIdEnum = {
  _1000: "1000"
};

// src/apis/DexApi.ts
var DexApi = class extends BaseAPI {
  /**
   * Creates request options for dexAddliquidity without sending the request
   */
  async dexAddliquidityRequestOpts(requestParameters) {
    if (requestParameters["dexAddliquidityRequestPayload"] == null) {
      throw new RequiredError(
        "dexAddliquidityRequestPayload",
        'Required parameter "dexAddliquidityRequestPayload" was null or undefined when calling dexAddliquidity().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/addliquidity`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["dexAddliquidityRequestPayload"]
    };
  }
  /**
   * Allows users to deposit token pairs into decentralized exchange pools with adjustable slippage protection and gas settings. The response includes essential transaction details (chain ID, addresses, transaction data, and a reference ID) for verifying and tracking the liquidity deposit.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Add Liquidity
   */
  async dexAddliquidityRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexAddliquidityRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Allows users to deposit token pairs into decentralized exchange pools with adjustable slippage protection and gas settings. The response includes essential transaction details (chain ID, addresses, transaction data, and a reference ID) for verifying and tracking the liquidity deposit.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Add Liquidity
   */
  async dexAddliquidity(requestParameters, initOverrides) {
    const response = await this.dexAddliquidityRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexCancelorder without sending the request
   */
  async dexCancelorderRequestOpts(requestParameters) {
    if (requestParameters["dexCancelorderRequestPayload"] == null) {
      throw new RequiredError(
        "dexCancelorderRequestPayload",
        'Required parameter "dexCancelorderRequestPayload" was null or undefined when calling dexCancelorder().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/cancelorder`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["dexCancelorderRequestPayload"]
    };
  }
  /**
   * Generates an unsigned limit-order cancellation message, which must be signed using the Blockdaemon SDK for final on-chain execution. This endpoint provides EIP-712 domain data, type definitions, and a structured message (chain ID, maker address, and order IDs) to securely cancel pending orders on the decentralized exchange.  > 💡  **Compute Unit Value:** `15` (Fixed)
   * Cancel Order
   */
  async dexCancelorderRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexCancelorderRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Generates an unsigned limit-order cancellation message, which must be signed using the Blockdaemon SDK for final on-chain execution. This endpoint provides EIP-712 domain data, type definitions, and a structured message (chain ID, maker address, and order IDs) to securely cancel pending orders on the decentralized exchange.  > 💡  **Compute Unit Value:** `15` (Fixed)
   * Cancel Order
   */
  async dexCancelorder(requestParameters, initOverrides) {
    const response = await this.dexCancelorderRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexCreateorder without sending the request
   */
  async dexCreateorderRequestOpts(requestParameters) {
    if (requestParameters["dexCreateorderRequestPayload"] == null) {
      throw new RequiredError(
        "dexCreateorderRequestPayload",
        'Required parameter "dexCreateorderRequestPayload" was null or undefined when calling dexCreateorder().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/createorder`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["dexCreateorderRequestPayload"]
    };
  }
  /**
   * Generates an unsigned limit order message for decentralized trading, specifying parameters for maker/taker assets, amounts, fees, and execution conditions. The endpoint returns EIP-712 domain data, type definitions, and a structured payload (including salt, addresses, and asset data), which must be signed using the Blockdaemon SDK to finalize the order on-chain.  > 💡  **Compute Unit Value:** `15` (Fixed)
   * Create Order
   */
  async dexCreateorderRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexCreateorderRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Generates an unsigned limit order message for decentralized trading, specifying parameters for maker/taker assets, amounts, fees, and execution conditions. The endpoint returns EIP-712 domain data, type definitions, and a structured payload (including salt, addresses, and asset data), which must be signed using the Blockdaemon SDK to finalize the order on-chain.  > 💡  **Compute Unit Value:** `15` (Fixed)
   * Create Order
   */
  async dexCreateorder(requestParameters, initOverrides) {
    const response = await this.dexCreateorderRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexFillorder without sending the request
   */
  async dexFillorderRequestOpts(requestParameters) {
    if (requestParameters["dexFillorderRequestPayload"] == null) {
      throw new RequiredError(
        "dexFillorderRequestPayload",
        'Required parameter "dexFillorderRequestPayload" was null or undefined when calling dexFillorder().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/fillorder`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["dexFillorderRequestPayload"]
    };
  }
  /**
   * Facilitates the execution (“filling”) of an existing limit order on a decentralized exchange by specifying the order ID, taker address, and offered token amount. The endpoint returns transaction data (chain ID, addresses, gas details, etc.) and a reference ID, enabling secure settlement of trades while providing a clear audit trail.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Fill Order
   */
  async dexFillorderRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexFillorderRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Facilitates the execution (“filling”) of an existing limit order on a decentralized exchange by specifying the order ID, taker address, and offered token amount. The endpoint returns transaction data (chain ID, addresses, gas details, etc.) and a reference ID, enabling secure settlement of trades while providing a clear audit trail.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Fill Order
   */
  async dexFillorder(requestParameters, initOverrides) {
    const response = await this.dexFillorderRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGetactiveamount without sending the request
   */
  async dexGetactiveamountRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling dexGetactiveamount().'
      );
    }
    if (requestParameters["tokenAddress"] == null) {
      throw new RequiredError(
        "tokenAddress",
        'Required parameter "tokenAddress" was null or undefined when calling dexGetactiveamount().'
      );
    }
    const queryParameters = {};
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["tokenAddress"] != null) {
      queryParameters["tokenAddress"] = requestParameters["tokenAddress"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/getactiveamount`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves the active making amount of a specific token held by a maker. This endpoint returns the maker’s address, the token’s contract address, and the current active amount available for order execution. This active amount reflects the portion of the maker’s assets that are deployed in liquidity provision or trading on the decentralized exchange, providing crucial insight into market depth and the maker’s trading activity.  > 💡  **Compute Unit Value:** `140` (Fixed)
   * Get Active Amount
   */
  async dexGetactiveamountRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGetactiveamountRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves the active making amount of a specific token held by a maker. This endpoint returns the maker’s address, the token’s contract address, and the current active amount available for order execution. This active amount reflects the portion of the maker’s assets that are deployed in liquidity provision or trading on the decentralized exchange, providing crucial insight into market depth and the maker’s trading activity.  > 💡  **Compute Unit Value:** `140` (Fixed)
   * Get Active Amount
   */
  async dexGetactiveamount(requestParameters, initOverrides) {
    const response = await this.dexGetactiveamountRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGethistoricalpoolchartdata without sending the request
   */
  async dexGethistoricalpoolchartdataRequestOpts(requestParameters) {
    if (requestParameters["poolAddress"] == null) {
      throw new RequiredError(
        "poolAddress",
        'Required parameter "poolAddress" was null or undefined when calling dexGethistoricalpoolchartdata().'
      );
    }
    const queryParameters = {};
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["interval"] != null) {
      queryParameters["interval"] = requestParameters["interval"];
    }
    if (requestParameters["limit"] != null) {
      queryParameters["limit"] = requestParameters["limit"];
    }
    if (requestParameters["poolAddress"] != null) {
      queryParameters["poolAddress"] = requestParameters["poolAddress"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/gethistoricalpoolchartdata`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves historical candlestick data (OHLCV) for a specified liquidity pool, capturing open, high, low, close, and volume metrics over customizable intervals. This robust chart data supports detailed price analysis, liquidity tracking, and strategy development for traders, analysts, and developers.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Get Historical Pool Chart Data
   */
  async dexGethistoricalpoolchartdataRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGethistoricalpoolchartdataRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves historical candlestick data (OHLCV) for a specified liquidity pool, capturing open, high, low, close, and volume metrics over customizable intervals. This robust chart data supports detailed price analysis, liquidity tracking, and strategy development for traders, analysts, and developers.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Get Historical Pool Chart Data
   */
  async dexGethistoricalpoolchartdata(requestParameters, initOverrides) {
    const response = await this.dexGethistoricalpoolchartdataRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGethistoricaltimeseries without sending the request
   */
  async dexGethistoricaltimeseriesRequestOpts(requestParameters) {
    if (requestParameters["poolAddress"] == null) {
      throw new RequiredError(
        "poolAddress",
        'Required parameter "poolAddress" was null or undefined when calling dexGethistoricaltimeseries().'
      );
    }
    const queryParameters = {};
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["endBlock"] != null) {
      queryParameters["endBlock"] = requestParameters["endBlock"];
    }
    if (requestParameters["startBlock"] != null) {
      queryParameters["startBlock"] = requestParameters["startBlock"];
    }
    if (requestParameters["poolAddress"] != null) {
      queryParameters["poolAddress"] = requestParameters["poolAddress"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/gethistoricaltimeseries`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a chronological series of historical price data (transaction hash, timestamp, trade price) for a specified DEX pool. This time series data supports thorough price analysis, backtesting, and liquidity monitoring, enabling more informed DeFi strategies.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Get Historical Time Series
   */
  async dexGethistoricaltimeseriesRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGethistoricaltimeseriesRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a chronological series of historical price data (transaction hash, timestamp, trade price) for a specified DEX pool. This time series data supports thorough price analysis, backtesting, and liquidity monitoring, enabling more informed DeFi strategies.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Get Historical Time Series
   */
  async dexGethistoricaltimeseries(requestParameters, initOverrides) {
    const response = await this.dexGethistoricaltimeseriesRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGethistoricaltransactions without sending the request
   */
  async dexGethistoricaltransactionsRequestOpts(requestParameters) {
    if (requestParameters["poolAddress"] == null) {
      throw new RequiredError(
        "poolAddress",
        'Required parameter "poolAddress" was null or undefined when calling dexGethistoricaltransactions().'
      );
    }
    const queryParameters = {};
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["endBlock"] != null) {
      queryParameters["endBlock"] = requestParameters["endBlock"];
    }
    if (requestParameters["startBlock"] != null) {
      queryParameters["startBlock"] = requestParameters["startBlock"];
    }
    if (requestParameters["poolAddress"] != null) {
      queryParameters["poolAddress"] = requestParameters["poolAddress"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/gethistoricaltransactions`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a chronological list of swap transactions for a specified DEX pool within a defined block range, returning transaction hashes, addresses, and gas metrics. Block-level details (e.g., block number, timestamp) and transfer categorization (e.g., send, receive, contract interaction) help facilitate in-depth auditing and performance analysis, with a pagination token for navigating extended data.  > 💡  **Compute Unit Value:** `205` (Fixed)
   * Get Historical Transactions
   */
  async dexGethistoricaltransactionsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGethistoricaltransactionsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a chronological list of swap transactions for a specified DEX pool within a defined block range, returning transaction hashes, addresses, and gas metrics. Block-level details (e.g., block number, timestamp) and transfer categorization (e.g., send, receive, contract interaction) help facilitate in-depth auditing and performance analysis, with a pagination token for navigating extended data.  > 💡  **Compute Unit Value:** `205` (Fixed)
   * Get Historical Transactions
   */
  async dexGethistoricaltransactions(requestParameters, initOverrides) {
    const response = await this.dexGethistoricaltransactionsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGetindividualposition without sending the request
   */
  async dexGetindividualpositionRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling dexGetindividualposition().'
      );
    }
    const queryParameters = {};
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["poolAddresses"] != null) {
      queryParameters["poolAddresses"] = requestParameters["poolAddresses"];
    }
    if (requestParameters["poolSize"] != null) {
      queryParameters["poolSize"] = requestParameters["poolSize"];
    }
    if (requestParameters["poolAddress"] != null) {
      queryParameters["poolAddress"] = requestParameters["poolAddress"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/getindividualposition`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves aggregated liquidity metrics and pool-specific breakdowns (pool identifiers, addresses, contributed amounts) for a given wallet across multiple DEX pools. This endpoint also supports a customizable pool size limit, returning essential data to help users and developers analyze individual positions within decentralized finance.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Get Individual Position
   */
  async dexGetindividualpositionRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGetindividualpositionRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves aggregated liquidity metrics and pool-specific breakdowns (pool identifiers, addresses, contributed amounts) for a given wallet across multiple DEX pools. This endpoint also supports a customizable pool size limit, returning essential data to help users and developers analyze individual positions within decentralized finance.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Get Individual Position
   */
  async dexGetindividualposition(requestParameters, initOverrides) {
    const response = await this.dexGetindividualpositionRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGetliquiditybytick without sending the request
   */
  async dexGetliquiditybytickRequestOpts(requestParameters) {
    if (requestParameters["poolAddress"] == null) {
      throw new RequiredError(
        "poolAddress",
        'Required parameter "poolAddress" was null or undefined when calling dexGetliquiditybytick().'
      );
    }
    const queryParameters = {};
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["poolAddress"] != null) {
      queryParameters["poolAddress"] = requestParameters["poolAddress"];
    }
    if (requestParameters["tickRange"] != null) {
      queryParameters["tickRange"] = requestParameters["tickRange"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/getliquiditybytick`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves detailed liquidity distribution data for a specified pool on a DEX, segmented by individual ticks. This granular breakdown helps users analyze how liquidity is allocated across different price ranges, optimizing strategies for trades or liquidity provision.  > 💡  **Compute Unit Value:** `300` (Fixed)
   * Get Liquidity By Tick
   */
  async dexGetliquiditybytickRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGetliquiditybytickRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves detailed liquidity distribution data for a specified pool on a DEX, segmented by individual ticks. This granular breakdown helps users analyze how liquidity is allocated across different price ranges, optimizing strategies for trades or liquidity provision.  > 💡  **Compute Unit Value:** `300` (Fixed)
   * Get Liquidity By Tick
   */
  async dexGetliquiditybytick(requestParameters, initOverrides) {
    const response = await this.dexGetliquiditybytickRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGetliquiditysources without sending the request
   */
  async dexGetliquiditysourcesRequestOpts(requestParameters) {
    const queryParameters = {};
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/getliquiditysources`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Returns a list of liquidity sources available for a specified DEX aggregator, filtered by the blockchain network. This helps users identify all supported liquidity providers or pools, enabling informed trading and integration decisions.  > 💡  **Compute Unit Value:** `80` (Fixed)
   * Get Liquidity Sources
   */
  async dexGetliquiditysourcesRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGetliquiditysourcesRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Returns a list of liquidity sources available for a specified DEX aggregator, filtered by the blockchain network. This helps users identify all supported liquidity providers or pools, enabling informed trading and integration decisions.  > 💡  **Compute Unit Value:** `80` (Fixed)
   * Get Liquidity Sources
   */
  async dexGetliquiditysources(requestParameters = {}, initOverrides) {
    const response = await this.dexGetliquiditysourcesRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGetmakerorders without sending the request
   */
  async dexGetmakerordersRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling dexGetmakerorders().'
      );
    }
    const queryParameters = {};
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["pageToken"] != null) {
      queryParameters["pageToken"] = requestParameters["pageToken"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/getmakerorders`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves all active limit orders posted by a specific maker on a DEX, returning details such as order IDs, asset pairs, prices, quantities, and status. This comprehensive overview helps users efficiently monitor, analyze, and manage open orders in real time.  > 💡  **Compute Unit Value:** `35` (Fixed)
   * Get Maker Orders
   */
  async dexGetmakerordersRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGetmakerordersRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves all active limit orders posted by a specific maker on a DEX, returning details such as order IDs, asset pairs, prices, quantities, and status. This comprehensive overview helps users efficiently monitor, analyze, and manage open orders in real time.  > 💡  **Compute Unit Value:** `35` (Fixed)
   * Get Maker Orders
   */
  async dexGetmakerorders(requestParameters, initOverrides) {
    const response = await this.dexGetmakerordersRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGetorders without sending the request
   */
  async dexGetordersRequestOpts(requestParameters) {
    if (requestParameters["makerAsset"] == null) {
      throw new RequiredError(
        "makerAsset",
        'Required parameter "makerAsset" was null or undefined when calling dexGetorders().'
      );
    }
    if (requestParameters["takerAsset"] == null) {
      throw new RequiredError(
        "takerAsset",
        'Required parameter "takerAsset" was null or undefined when calling dexGetorders().'
      );
    }
    const queryParameters = {};
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["status"] != null) {
      queryParameters["status"] = requestParameters["status"];
    }
    if (requestParameters["orderHashes"] != null) {
      queryParameters["orderHashes"] = requestParameters["orderHashes"];
    }
    if (requestParameters["filler"] != null) {
      queryParameters["filler"] = requestParameters["filler"];
    }
    if (requestParameters["sort"] != null) {
      queryParameters["sort"] = requestParameters["sort"];
    }
    if (requestParameters["makerAsset"] != null) {
      queryParameters["makerAsset"] = requestParameters["makerAsset"];
    }
    if (requestParameters["takerAsset"] != null) {
      queryParameters["takerAsset"] = requestParameters["takerAsset"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/getorders`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Fetches all active orders on a specified DEX, filtered by maker and taker assets. It returns order details (IDs, asset addresses, pricing, etc.) to support real-time order book monitoring and trading integrations.  > 💡  **Compute Unit Value:** `15` (Fixed)
   * Get Orders
   */
  async dexGetordersRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGetordersRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Fetches all active orders on a specified DEX, filtered by maker and taker assets. It returns order details (IDs, asset addresses, pricing, etc.) to support real-time order book monitoring and trading integrations.  > 💡  **Compute Unit Value:** `15` (Fixed)
   * Get Orders
   */
  async dexGetorders(requestParameters, initOverrides) {
    const response = await this.dexGetordersRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGetpool without sending the request
   */
  async dexGetpoolRequestOpts(requestParameters) {
    if (requestParameters["tokenA"] == null) {
      throw new RequiredError(
        "tokenA",
        'Required parameter "tokenA" was null or undefined when calling dexGetpool().'
      );
    }
    if (requestParameters["tokenB"] == null) {
      throw new RequiredError(
        "tokenB",
        'Required parameter "tokenB" was null or undefined when calling dexGetpool().'
      );
    }
    const queryParameters = {};
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["tokenA"] != null) {
      queryParameters["tokenA"] = requestParameters["tokenA"];
    }
    if (requestParameters["tokenB"] != null) {
      queryParameters["tokenB"] = requestParameters["tokenB"];
    }
    if (requestParameters["tokenC"] != null) {
      queryParameters["tokenC"] = requestParameters["tokenC"];
    }
    if (requestParameters["tokenD"] != null) {
      queryParameters["tokenD"] = requestParameters["tokenD"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/getpool`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves pools of all tick spacing and pool fees along with the pool and tokens individual liquidity for a specific token pair.  > 💡  **Compute Unit Value:** `105` (Fixed)
   * Get Pool
   */
  async dexGetpoolRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGetpoolRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves pools of all tick spacing and pool fees along with the pool and tokens individual liquidity for a specific token pair.  > 💡  **Compute Unit Value:** `105` (Fixed)
   * Get Pool
   */
  async dexGetpool(requestParameters, initOverrides) {
    const response = await this.dexGetpoolRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGetpoolchartdata without sending the request
   */
  async dexGetpoolchartdataRequestOpts(requestParameters) {
    if (requestParameters["poolAddress"] == null) {
      throw new RequiredError(
        "poolAddress",
        'Required parameter "poolAddress" was null or undefined when calling dexGetpoolchartdata().'
      );
    }
    const queryParameters = {};
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["interval"] != null) {
      queryParameters["interval"] = requestParameters["interval"];
    }
    if (requestParameters["poolAddress"] != null) {
      queryParameters["poolAddress"] = requestParameters["poolAddress"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/getpoolchartdata`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Provides historical data (open, close, high, low) for a specified liquidity pool over defined block ranges. Detailed transaction info (e.g., block numbers, trade prices, and token amounts) is included, enabling in-depth analysis of liquidity and trading activity.  > 💡  **Compute Unit Value:** `130` (Fixed)
   * Get Pool Chart Data
   */
  async dexGetpoolchartdataRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGetpoolchartdataRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Provides historical data (open, close, high, low) for a specified liquidity pool over defined block ranges. Detailed transaction info (e.g., block numbers, trade prices, and token amounts) is included, enabling in-depth analysis of liquidity and trading activity.  > 💡  **Compute Unit Value:** `130` (Fixed)
   * Get Pool Chart Data
   */
  async dexGetpoolchartdata(requestParameters, initOverrides) {
    const response = await this.dexGetpoolchartdataRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGetpoolindividualliquidity without sending the request
   */
  async dexGetpoolindividualliquidityRequestOpts(requestParameters) {
    if (requestParameters["poolAddress"] == null) {
      throw new RequiredError(
        "poolAddress",
        'Required parameter "poolAddress" was null or undefined when calling dexGetpoolindividualliquidity().'
      );
    }
    const queryParameters = {};
    if (requestParameters["poolAddress"] != null) {
      queryParameters["poolAddress"] = requestParameters["poolAddress"];
    }
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["endBlock"] != null) {
      queryParameters["endBlock"] = requestParameters["endBlock"];
    }
    if (requestParameters["startBlock"] != null) {
      queryParameters["startBlock"] = requestParameters["startBlock"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/getpoolindividualliquidity`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a list of users contributing liquidity to a specified pool, along with their token positions and associated block numbers. The response also includes pagination details, streamlining navigation of large datasets for deeper analysis.  > 💡  **Compute Unit Value:** `55` (Fixed)
   * Get Pool Individual Liquidity
   */
  async dexGetpoolindividualliquidityRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGetpoolindividualliquidityRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a list of users contributing liquidity to a specified pool, along with their token positions and associated block numbers. The response also includes pagination details, streamlining navigation of large datasets for deeper analysis.  > 💡  **Compute Unit Value:** `55` (Fixed)
   * Get Pool Individual Liquidity
   */
  async dexGetpoolindividualliquidity(requestParameters, initOverrides) {
    const response = await this.dexGetpoolindividualliquidityRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGetpoolliquidity without sending the request
   */
  async dexGetpoolliquidityRequestOpts(requestParameters) {
    if (requestParameters["poolAddress"] == null) {
      throw new RequiredError(
        "poolAddress",
        'Required parameter "poolAddress" was null or undefined when calling dexGetpoolliquidity().'
      );
    }
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling dexGetpoolliquidity().'
      );
    }
    if (requestParameters["id"] == null) {
      throw new RequiredError(
        "id",
        'Required parameter "id" was null or undefined when calling dexGetpoolliquidity().'
      );
    }
    if (requestParameters["poolId"] == null) {
      throw new RequiredError(
        "poolId",
        'Required parameter "poolId" was null or undefined when calling dexGetpoolliquidity().'
      );
    }
    const queryParameters = {};
    if (requestParameters["poolAddress"] != null) {
      queryParameters["poolAddress"] = requestParameters["poolAddress"];
    }
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["id"] != null) {
      queryParameters["id"] = requestParameters["id"];
    }
    if (requestParameters["poolId"] != null) {
      queryParameters["poolId"] = requestParameters["poolId"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/getpoolliquidity`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves the total current liquidity in a specified DEX pool, indicating the pool’s market depth and trading capacity. This aggregated liquidity value helps users assess the pool’s overall health and potential for large trades without excessive slippage.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Get Pool Liquidity
   */
  async dexGetpoolliquidityRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGetpoolliquidityRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves the total current liquidity in a specified DEX pool, indicating the pool’s market depth and trading capacity. This aggregated liquidity value helps users assess the pool’s overall health and potential for large trades without excessive slippage.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Get Pool Liquidity
   */
  async dexGetpoolliquidity(requestParameters, initOverrides) {
    const response = await this.dexGetpoolliquidityRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGetpools without sending the request
   */
  async dexGetpoolsRequestOpts(requestParameters) {
    if (requestParameters["path"] == null) {
      throw new RequiredError(
        "path",
        'Required parameter "path" was null or undefined when calling dexGetpools().'
      );
    }
    if (requestParameters["amountIn"] == null) {
      throw new RequiredError(
        "amountIn",
        'Required parameter "amountIn" was null or undefined when calling dexGetpools().'
      );
    }
    const queryParameters = {};
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["path"] != null) {
      queryParameters["path"] = requestParameters["path"];
    }
    if (requestParameters["amountIn"] != null) {
      queryParameters["amountIn"] = requestParameters["amountIn"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/getpools`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves quotes for a specific token pair from multiple DEXs. It calculates the expected output for a given input amount (amountIn) on each DEX, enabling users to compare liquidity and pricing across platforms and choose the best option for their trades.  > 💡  **Compute Unit Value:** `80` (Fixed)
   * Get Pools
   */
  async dexGetpoolsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGetpoolsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves quotes for a specific token pair from multiple DEXs. It calculates the expected output for a given input amount (amountIn) on each DEX, enabling users to compare liquidity and pricing across platforms and choose the best option for their trades.  > 💡  **Compute Unit Value:** `80` (Fixed)
   * Get Pools
   */
  async dexGetpools(requestParameters, initOverrides) {
    const response = await this.dexGetpoolsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGetpooltradedata without sending the request
   */
  async dexGetpooltradedataRequestOpts(requestParameters) {
    if (requestParameters["poolAddress"] == null) {
      throw new RequiredError(
        "poolAddress",
        'Required parameter "poolAddress" was null or undefined when calling dexGetpooltradedata().'
      );
    }
    const queryParameters = {};
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["endBlock"] != null) {
      queryParameters["endBlock"] = requestParameters["endBlock"];
    }
    if (requestParameters["startBlock"] != null) {
      queryParameters["startBlock"] = requestParameters["startBlock"];
    }
    if (requestParameters["poolAddress"] != null) {
      queryParameters["poolAddress"] = requestParameters["poolAddress"];
    }
    if (requestParameters["eventType"] != null) {
      queryParameters["eventType"] = requestParameters["eventType"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/getpooltradedata`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Extracts detailed trading activity from a specific liquidity pool over a defined block range. It returns transaction data—including transfers, swaps, burns, and mints—with key details such as block numbers, transaction hashes, token amounts, and event types, enabling users to analyze liquidity flows and market behavior within the pool.  > 💡  **Compute Unit Value:** `145` (Fixed)
   * Get Pool Trade Data
   */
  async dexGetpooltradedataRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGetpooltradedataRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Extracts detailed trading activity from a specific liquidity pool over a defined block range. It returns transaction data—including transfers, swaps, burns, and mints—with key details such as block numbers, transaction hashes, token amounts, and event types, enabling users to analyze liquidity flows and market behavior within the pool.  > 💡  **Compute Unit Value:** `145` (Fixed)
   * Get Pool Trade Data
   */
  async dexGetpooltradedata(requestParameters, initOverrides) {
    const response = await this.dexGetpooltradedataRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGetroute without sending the request
   */
  async dexGetrouteRequestOpts(requestParameters) {
    if (requestParameters["path"] == null) {
      throw new RequiredError(
        "path",
        'Required parameter "path" was null or undefined when calling dexGetroute().'
      );
    }
    if (requestParameters["amountIn"] == null) {
      throw new RequiredError(
        "amountIn",
        'Required parameter "amountIn" was null or undefined when calling dexGetroute().'
      );
    }
    const queryParameters = {};
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["path"] != null) {
      queryParameters["path"] = requestParameters["path"];
    }
    if (requestParameters["amountIn"] != null) {
      queryParameters["amountIn"] = requestParameters["amountIn"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/getroute`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Returns detailed routing information for a token swap between a specified token pair. It provides a breakdown of the swap path, including each liquidity source involved, the corresponding swap details such as input/output amounts, and any associated fees, enabling users to assess and choose the optimal route for their trade.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Get Route
   */
  async dexGetrouteRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGetrouteRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Returns detailed routing information for a token swap between a specified token pair. It provides a breakdown of the swap path, including each liquidity source involved, the corresponding swap details such as input/output amounts, and any associated fees, enabling users to assess and choose the optimal route for their trade.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Get Route
   */
  async dexGetroute(requestParameters, initOverrides) {
    const response = await this.dexGetrouteRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGettokenidprice without sending the request
   */
  async dexGettokenidpriceRequestOpts(requestParameters) {
    if (requestParameters["poolId"] == null) {
      throw new RequiredError(
        "poolId",
        'Required parameter "poolId" was null or undefined when calling dexGettokenidprice().'
      );
    }
    const queryParameters = {};
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["poolId"] != null) {
      queryParameters["poolId"] = requestParameters["poolId"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/gettokenidprice`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves detailed mapping and pricing information for tokens on DEXs. By default, it returns data for the top 100 trending tokens, offering a snapshot of market dynamics and token popularity. Alternatively, if a specific token address is provided, the endpoint returns the mapping details for that token. The response includes key data such as the pool identifier (poolId) and the current price, enabling users and developers to assess market valuations, track trends, and integrate real-time pricing information into trading strategies and analytical dashboards.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Get Token ID Price
   */
  async dexGettokenidpriceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGettokenidpriceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves detailed mapping and pricing information for tokens on DEXs. By default, it returns data for the top 100 trending tokens, offering a snapshot of market dynamics and token popularity. Alternatively, if a specific token address is provided, the endpoint returns the mapping details for that token. The response includes key data such as the pool identifier (poolId) and the current price, enabling users and developers to assess market valuations, track trends, and integrate real-time pricing information into trading strategies and analytical dashboards.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Get Token ID Price
   */
  async dexGettokenidprice(requestParameters, initOverrides) {
    const response = await this.dexGettokenidpriceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGettokenliquidity without sending the request
   */
  async dexGettokenliquidityRequestOpts(requestParameters) {
    if (requestParameters["poolAddress"] == null) {
      throw new RequiredError(
        "poolAddress",
        'Required parameter "poolAddress" was null or undefined when calling dexGettokenliquidity().'
      );
    }
    if (requestParameters["poolId"] == null) {
      throw new RequiredError(
        "poolId",
        'Required parameter "poolId" was null or undefined when calling dexGettokenliquidity().'
      );
    }
    const queryParameters = {};
    if (requestParameters["poolAddress"] != null) {
      queryParameters["poolAddress"] = requestParameters["poolAddress"];
    }
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    if (requestParameters["poolId"] != null) {
      queryParameters["poolId"] = requestParameters["poolId"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/gettokenliquidity`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves detailed liquidity metrics for tokens in specified pools across supported DEX protocols. It returns the current liquidity amounts for each token, enabling users to assess market depth and token distribution within the pool.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Get Token Liquidity
   */
  async dexGettokenliquidityRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGettokenliquidityRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves detailed liquidity metrics for tokens in specified pools across supported DEX protocols. It returns the current liquidity amounts for each token, enabling users to assess market depth and token distribution within the pool.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Get Token Liquidity
   */
  async dexGettokenliquidity(requestParameters, initOverrides) {
    const response = await this.dexGettokenliquidityRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGetuserliquidity without sending the request
   */
  async dexGetuserliquidityRequestOpts(requestParameters) {
    if (requestParameters["tokenA"] == null) {
      throw new RequiredError(
        "tokenA",
        'Required parameter "tokenA" was null or undefined when calling dexGetuserliquidity().'
      );
    }
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling dexGetuserliquidity().'
      );
    }
    if (requestParameters["tokenB"] == null) {
      throw new RequiredError(
        "tokenB",
        'Required parameter "tokenB" was null or undefined when calling dexGetuserliquidity().'
      );
    }
    if (requestParameters["tokenId"] == null) {
      throw new RequiredError(
        "tokenId",
        'Required parameter "tokenId" was null or undefined when calling dexGetuserliquidity().'
      );
    }
    if (requestParameters["poolAddress"] == null) {
      throw new RequiredError(
        "poolAddress",
        'Required parameter "poolAddress" was null or undefined when calling dexGetuserliquidity().'
      );
    }
    if (requestParameters["id"] == null) {
      throw new RequiredError(
        "id",
        'Required parameter "id" was null or undefined when calling dexGetuserliquidity().'
      );
    }
    if (requestParameters["positionNFT"] == null) {
      throw new RequiredError(
        "positionNFT",
        'Required parameter "positionNFT" was null or undefined when calling dexGetuserliquidity().'
      );
    }
    if (requestParameters["path"] == null) {
      throw new RequiredError(
        "path",
        'Required parameter "path" was null or undefined when calling dexGetuserliquidity().'
      );
    }
    if (requestParameters["poolId"] == null) {
      throw new RequiredError(
        "poolId",
        'Required parameter "poolId" was null or undefined when calling dexGetuserliquidity().'
      );
    }
    const queryParameters = {};
    if (requestParameters["tokenA"] != null) {
      queryParameters["tokenA"] = requestParameters["tokenA"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["tokenB"] != null) {
      queryParameters["tokenB"] = requestParameters["tokenB"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    if (requestParameters["tokenId"] != null) {
      queryParameters["tokenId"] = requestParameters["tokenId"];
    }
    if (requestParameters["poolAddress"] != null) {
      queryParameters["poolAddress"] = requestParameters["poolAddress"];
    }
    if (requestParameters["tokenC"] != null) {
      queryParameters["tokenC"] = requestParameters["tokenC"];
    }
    if (requestParameters["tokenD"] != null) {
      queryParameters["tokenD"] = requestParameters["tokenD"];
    }
    if (requestParameters["id"] != null) {
      queryParameters["id"] = requestParameters["id"];
    }
    if (requestParameters["positionNFT"] != null) {
      queryParameters["positionNFT"] = requestParameters["positionNFT"];
    }
    if (requestParameters["path"] != null) {
      queryParameters["path"] = requestParameters["path"];
    }
    if (requestParameters["poolId"] != null) {
      queryParameters["poolId"] = requestParameters["poolId"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/getuserliquidity`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves detailed liquidity information for a specific token pair held by an account. It returns the pair’s unique address along with the total liquidity and the individual balances for each token in the pair. This data is essential for users monitoring their decentralized liquidity positions and managing their exposure across various protocols.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * Get User Liquidity
   */
  async dexGetuserliquidityRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGetuserliquidityRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves detailed liquidity information for a specific token pair held by an account. It returns the pair’s unique address along with the total liquidity and the individual balances for each token in the pair. This data is essential for users monitoring their decentralized liquidity positions and managing their exposure across various protocols.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * Get User Liquidity
   */
  async dexGetuserliquidity(requestParameters, initOverrides) {
    const response = await this.dexGetuserliquidityRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGetusertokenids without sending the request
   */
  async dexGetusertokenidsRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling dexGetusertokenids().'
      );
    }
    const queryParameters = {};
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/getusertokenids`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a list of NFT token IDs representing liquidity positions held in a user’s wallet. Each token is returned with its associated liquidity, enabling users to efficiently track and manage their NFT-based positions across decentralized finance protocols.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * Get User Token IDs
   */
  async dexGetusertokenidsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGetusertokenidsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a list of NFT token IDs representing liquidity positions held in a user’s wallet. Each token is returned with its associated liquidity, enabling users to efficiently track and manage their NFT-based positions across decentralized finance protocols.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * Get User Token IDs
   */
  async dexGetusertokenids(requestParameters, initOverrides) {
    const response = await this.dexGetusertokenidsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexGetwalletposition without sending the request
   */
  async dexGetwalletpositionRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling dexGetwalletposition().'
      );
    }
    const queryParameters = {};
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/getwalletposition`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves comprehensive information about a wallet’s positions across various DEX protocols. It aggregates key data—including protocol details, liquidity positions, token balances (with USD valuations), and pool metrics (such as reserve amounts and share of pool)—to provide a clear snapshot of the wallet’s DeFi exposure.   > 💡  **Compute Unit Value:** `170` (Fixed)
   * Get Wallet Position
   */
  async dexGetwalletpositionRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexGetwalletpositionRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves comprehensive information about a wallet’s positions across various DEX protocols. It aggregates key data—including protocol details, liquidity positions, token balances (with USD valuations), and pool metrics (such as reserve amounts and share of pool)—to provide a clear snapshot of the wallet’s DeFi exposure.   > 💡  **Compute Unit Value:** `170` (Fixed)
   * Get Wallet Position
   */
  async dexGetwalletposition(requestParameters, initOverrides) {
    const response = await this.dexGetwalletpositionRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexPrice without sending the request
   */
  async dexPriceRequestOpts(requestParameters) {
    if (requestParameters["path"] == null) {
      throw new RequiredError(
        "path",
        'Required parameter "path" was null or undefined when calling dexPrice().'
      );
    }
    if (requestParameters["amountIn"] == null) {
      throw new RequiredError(
        "amountIn",
        'Required parameter "amountIn" was null or undefined when calling dexPrice().'
      );
    }
    const queryParameters = {};
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["path"] != null) {
      queryParameters["path"] = requestParameters["path"];
    }
    if (requestParameters["amountIn"] != null) {
      queryParameters["amountIn"] = requestParameters["amountIn"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    if (requestParameters["poolFees"] != null) {
      queryParameters["poolFees"] = requestParameters["poolFees"];
    }
    if (requestParameters["slippage"] != null) {
      queryParameters["slippage"] = requestParameters["slippage"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/price`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a swap quotation for a given token pair or a series of tokens along a specified path. By providing an input amount (amountIn), the endpoint calculates and returns the estimated output amounts for each token in the swap path. This allows users to accurately gauge current market pricing and estimate the value of potential swaps on a decentralized exchange, facilitating more informed trading decisions.  > 💡  **Compute Unit Value:** `70` (Fixed)
   * Get DEX Swap Price
   */
  async dexPriceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexPriceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a swap quotation for a given token pair or a series of tokens along a specified path. By providing an input amount (amountIn), the endpoint calculates and returns the estimated output amounts for each token in the swap path. This allows users to accurately gauge current market pricing and estimate the value of potential swaps on a decentralized exchange, facilitating more informed trading decisions.  > 💡  **Compute Unit Value:** `70` (Fixed)
   * Get DEX Swap Price
   */
  async dexPrice(requestParameters, initOverrides) {
    const response = await this.dexPriceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexPriceLegacy without sending the request
   */
  async dexPriceLegacyRequestOpts(requestParameters) {
    if (requestParameters["path"] == null) {
      throw new RequiredError(
        "path",
        'Required parameter "path" was null or undefined when calling dexPriceLegacy().'
      );
    }
    if (requestParameters["amountIn"] == null) {
      throw new RequiredError(
        "amountIn",
        'Required parameter "amountIn" was null or undefined when calling dexPriceLegacy().'
      );
    }
    const queryParameters = {};
    if (requestParameters["dexId"] != null) {
      queryParameters["dexId"] = requestParameters["dexId"];
    }
    if (requestParameters["path"] != null) {
      queryParameters["path"] = requestParameters["path"];
    }
    if (requestParameters["amountIn"] != null) {
      queryParameters["amountIn"] = requestParameters["amountIn"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    if (requestParameters["poolFees"] != null) {
      queryParameters["poolFees"] = requestParameters["poolFees"];
    }
    if (requestParameters["slippage"] != null) {
      queryParameters["slippage"] = requestParameters["slippage"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/getprice`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a swap quotation for a given token pair or a series of tokens along a specified path. By providing an input amount (amountIn), the endpoint calculates and returns the estimated output amounts for each token in the swap path. This allows users to accurately gauge current market pricing and estimate the value of potential swaps on a decentralized exchange, facilitating more informed trading decisions.  > 💡  **Compute Unit Value:** `70` (Fixed)
   * Get DEX Swap Price
   */
  async dexPriceLegacyRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexPriceLegacyRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a swap quotation for a given token pair or a series of tokens along a specified path. By providing an input amount (amountIn), the endpoint calculates and returns the estimated output amounts for each token in the swap path. This allows users to accurately gauge current market pricing and estimate the value of potential swaps on a decentralized exchange, facilitating more informed trading decisions.  > 💡  **Compute Unit Value:** `70` (Fixed)
   * Get DEX Swap Price
   */
  async dexPriceLegacy(requestParameters, initOverrides) {
    const response = await this.dexPriceLegacyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexPublishorder without sending the request
   */
  async dexPublishorderRequestOpts(requestParameters) {
    if (requestParameters["dexPublishorderRequestPayload"] == null) {
      throw new RequiredError(
        "dexPublishorderRequestPayload",
        'Required parameter "dexPublishorderRequestPayload" was null or undefined when calling dexPublishorder().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/publishorder`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["dexPublishorderRequestPayload"]
    };
  }
  /**
   * This endpoint interacts with the Kyberswap protocol to manage limit orders on the decentralized exchange. It allows you to either create a new limit order or cancel an existing one, based on the parameters provided in your request. A successful call returns detailed information including the order ID, the current order status, and a reference ID for tracking the transaction.  > 💡  **Compute Unit Value:** `35` (Fixed)
   * Publish Order
   */
  async dexPublishorderRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexPublishorderRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * This endpoint interacts with the Kyberswap protocol to manage limit orders on the decentralized exchange. It allows you to either create a new limit order or cancel an existing one, based on the parameters provided in your request. A successful call returns detailed information including the order ID, the current order status, and a reference ID for tracking the transaction.  > 💡  **Compute Unit Value:** `35` (Fixed)
   * Publish Order
   */
  async dexPublishorder(requestParameters, initOverrides) {
    const response = await this.dexPublishorderRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexQuotes without sending the request
   */
  async dexQuotesRequestOpts(requestParameters) {
    if (requestParameters["path"] == null) {
      throw new RequiredError(
        "path",
        'Required parameter "path" was null or undefined when calling dexQuotes().'
      );
    }
    if (requestParameters["amountIn"] == null) {
      throw new RequiredError(
        "amountIn",
        'Required parameter "amountIn" was null or undefined when calling dexQuotes().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["path"] != null) {
      queryParameters["path"] = requestParameters["path"];
    }
    if (requestParameters["amountIn"] != null) {
      queryParameters["amountIn"] = requestParameters["amountIn"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["excludedDexes"] != null) {
      queryParameters["excludedDexes"] = requestParameters["excludedDexes"];
    }
    if (requestParameters["poolFees"] != null) {
      queryParameters["poolFees"] = requestParameters["poolFees"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/quotes`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a comprehensive list of swap quotations from multiple DEXs for a specified token pair pool. By aggregating swap quotes across various DEXs, it enables users to compare rates and identify the most favorable trading option. The response includes detailed information such as the DEX name and identifier, the input amount, the swap path, and the resulting output amounts from each DEX, making it a valuable tool for pricing data and swap execution decisions.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Get DEX Swap Quotes
   */
  async dexQuotesRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexQuotesRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a comprehensive list of swap quotations from multiple DEXs for a specified token pair pool. By aggregating swap quotes across various DEXs, it enables users to compare rates and identify the most favorable trading option. The response includes detailed information such as the DEX name and identifier, the input amount, the swap path, and the resulting output amounts from each DEX, making it a valuable tool for pricing data and swap execution decisions.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Get DEX Swap Quotes
   */
  async dexQuotes(requestParameters, initOverrides) {
    const response = await this.dexQuotesRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexQuotesLegacy without sending the request
   */
  async dexQuotesLegacyRequestOpts(requestParameters) {
    if (requestParameters["path"] == null) {
      throw new RequiredError(
        "path",
        'Required parameter "path" was null or undefined when calling dexQuotesLegacy().'
      );
    }
    if (requestParameters["amountIn"] == null) {
      throw new RequiredError(
        "amountIn",
        'Required parameter "amountIn" was null or undefined when calling dexQuotesLegacy().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["path"] != null) {
      queryParameters["path"] = requestParameters["path"];
    }
    if (requestParameters["amountIn"] != null) {
      queryParameters["amountIn"] = requestParameters["amountIn"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["excludedDexes"] != null) {
      queryParameters["excludedDexes"] = requestParameters["excludedDexes"];
    }
    if (requestParameters["poolFees"] != null) {
      queryParameters["poolFees"] = requestParameters["poolFees"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/quoteaggregator`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a comprehensive list of swap quotations from multiple DEXs for a specified token pair pool. By aggregating swap quotes across various DEXs, it enables users to compare rates and identify the most favorable trading option. The response includes detailed information such as the DEX name and identifier, the input amount, the swap path, and the resulting output amounts from each DEX, making it a valuable tool for pricing data and swap execution decisions.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Get DEX Swap Quotes
   */
  async dexQuotesLegacyRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexQuotesLegacyRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a comprehensive list of swap quotations from multiple DEXs for a specified token pair pool. By aggregating swap quotes across various DEXs, it enables users to compare rates and identify the most favorable trading option. The response includes detailed information such as the DEX name and identifier, the input amount, the swap path, and the resulting output amounts from each DEX, making it a valuable tool for pricing data and swap execution decisions.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Get DEX Swap Quotes
   */
  async dexQuotesLegacy(requestParameters, initOverrides) {
    const response = await this.dexQuotesLegacyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexRemoveliquidity without sending the request
   */
  async dexRemoveliquidityRequestOpts(requestParameters) {
    if (requestParameters["dexRemoveliquidityRequestPayload"] == null) {
      throw new RequiredError(
        "dexRemoveliquidityRequestPayload",
        'Required parameter "dexRemoveliquidityRequestPayload" was null or undefined when calling dexRemoveliquidity().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/removeliquidity`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["dexRemoveliquidityRequestPayload"]
    };
  }
  /**
   * Facilitates the withdrawal of liquidity from cross-chain bridges with customizable gas settings and amount specifications.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Remove Liquidity
   */
  async dexRemoveliquidityRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexRemoveliquidityRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Facilitates the withdrawal of liquidity from cross-chain bridges with customizable gas settings and amount specifications.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Remove Liquidity
   */
  async dexRemoveliquidity(requestParameters, initOverrides) {
    const response = await this.dexRemoveliquidityRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexSwap without sending the request
   */
  async dexSwapRequestOpts(requestParameters) {
    if (requestParameters["dexSwapRequestPayload"] == null) {
      throw new RequiredError(
        "dexSwapRequestPayload",
        'Required parameter "dexSwapRequestPayload" was null or undefined when calling dexSwap().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/swap`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["dexSwapRequestPayload"]
    };
  }
  /**
   * Enables users to perform token swaps on a DEX by supplying source tokens, desired output tokens, and other trade parameters. This endpoint calculates the optimal path (or uses a given path) across liquidity pools, then returns key details such as the route taken, final amounts, and any additional transaction data needed to finalize the swap on-chain. It is particularly useful for DeFi integrations that require seamless conversions between multiple ERC20 tokens within a single request.  > 💡  **Compute Unit Value:** `55` (Fixed)
   * Create DEX Swap Transaction
   */
  async dexSwapRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexSwapRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Enables users to perform token swaps on a DEX by supplying source tokens, desired output tokens, and other trade parameters. This endpoint calculates the optimal path (or uses a given path) across liquidity pools, then returns key details such as the route taken, final amounts, and any additional transaction data needed to finalize the swap on-chain. It is particularly useful for DeFi integrations that require seamless conversions between multiple ERC20 tokens within a single request.  > 💡  **Compute Unit Value:** `55` (Fixed)
   * Create DEX Swap Transaction
   */
  async dexSwap(requestParameters, initOverrides) {
    const response = await this.dexSwapRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexSwapWithApproval without sending the request
   */
  async dexSwapWithApprovalRequestOpts(requestParameters) {
    if (requestParameters["dexSwapwithapprovalRequestPayload"] == null) {
      throw new RequiredError(
        "dexSwapwithapprovalRequestPayload",
        'Required parameter "dexSwapwithapprovalRequestPayload" was null or undefined when calling dexSwapWithApproval().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/swap-with-approval`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["dexSwapwithapprovalRequestPayload"]
    };
  }
  /**
   * Generates both token approval and swap transactions in a single call with configurable slippage protection.  > 💡  **Compute Unit Value:** `85` (Fixed)
   * Create DEX Swap Transaction with Approval
   */
  async dexSwapWithApprovalRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexSwapWithApprovalRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Generates both token approval and swap transactions in a single call with configurable slippage protection.  > 💡  **Compute Unit Value:** `85` (Fixed)
   * Create DEX Swap Transaction with Approval
   */
  async dexSwapWithApproval(requestParameters, initOverrides) {
    const response = await this.dexSwapWithApprovalRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexSwapWithApprovalLegacy without sending the request
   */
  async dexSwapWithApprovalLegacyRequestOpts(requestParameters) {
    if (requestParameters["dexSwapwithapprovalRequestPayload"] == null) {
      throw new RequiredError(
        "dexSwapwithapprovalRequestPayload",
        'Required parameter "dexSwapwithapprovalRequestPayload" was null or undefined when calling dexSwapWithApprovalLegacy().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/swapwithapproval`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["dexSwapwithapprovalRequestPayload"]
    };
  }
  /**
   * Generates both token approval and swap transactions in a single call with configurable slippage protection.  > 💡  **Compute Unit Value:** `85` (Fixed)
   * Create DEX Swap Transaction with Approval
   */
  async dexSwapWithApprovalLegacyRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexSwapWithApprovalLegacyRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Generates both token approval and swap transactions in a single call with configurable slippage protection.  > 💡  **Compute Unit Value:** `85` (Fixed)
   * Create DEX Swap Transaction with Approval
   */
  async dexSwapWithApprovalLegacy(requestParameters, initOverrides) {
    const response = await this.dexSwapWithApprovalLegacyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexSwaps without sending the request
   */
  async dexSwapsRequestOpts(requestParameters) {
    if (requestParameters["dexSwapaggregatorRequestPayload"] == null) {
      throw new RequiredError(
        "dexSwapaggregatorRequestPayload",
        'Required parameter "dexSwapaggregatorRequestPayload" was null or undefined when calling dexSwaps().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/swaps`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["dexSwapaggregatorRequestPayload"]
    };
  }
  /**
   * Aggregates swap quotes from all DEXs supporting a specified token pair. It allows users to customize their query to prioritize either the best output or the lowest gas costs. The response provides detailed transaction data including DEX identifiers, output amounts, swap routing parameters (such as call data), gas estimates, and a reference, enabling traders to compare and select the most efficient swap route for their needs.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Aggregate DEX Swap
   */
  async dexSwapsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexSwapsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Aggregates swap quotes from all DEXs supporting a specified token pair. It allows users to customize their query to prioritize either the best output or the lowest gas costs. The response provides detailed transaction data including DEX identifiers, output amounts, swap routing parameters (such as call data), gas estimates, and a reference, enabling traders to compare and select the most efficient swap route for their needs.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Aggregate DEX Swap
   */
  async dexSwaps(requestParameters, initOverrides) {
    const response = await this.dexSwapsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for dexSwapsLegacy without sending the request
   */
  async dexSwapsLegacyRequestOpts(requestParameters) {
    if (requestParameters["dexSwapaggregatorRequestPayload"] == null) {
      throw new RequiredError(
        "dexSwapaggregatorRequestPayload",
        'Required parameter "dexSwapaggregatorRequestPayload" was null or undefined when calling dexSwapsLegacy().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/dex/swapaggregator`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["dexSwapaggregatorRequestPayload"]
    };
  }
  /**
   * Aggregates swap quotes from all DEXs supporting a specified token pair. It allows users to customize their query to prioritize either the best output or the lowest gas costs. The response provides detailed transaction data including DEX identifiers, output amounts, swap routing parameters (such as call data), gas estimates, and a reference, enabling traders to compare and select the most efficient swap route for their needs.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Aggregate DEX Swap
   */
  async dexSwapsLegacyRaw(requestParameters, initOverrides) {
    const requestOptions = await this.dexSwapsLegacyRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Aggregates swap quotes from all DEXs supporting a specified token pair. It allows users to customize their query to prioritize either the best output or the lowest gas costs. The response provides detailed transaction data including DEX identifiers, output amounts, swap routing parameters (such as call data), gas estimates, and a reference, enabling traders to compare and select the most efficient swap route for their needs.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Aggregate DEX Swap
   */
  async dexSwapsLegacy(requestParameters, initOverrides) {
    const response = await this.dexSwapsLegacyRaw(requestParameters, initOverrides);
    return await response.value();
  }
};
var DexGetactiveamountDexIdEnum = {
  _2200: "2200",
  _2201: "2201",
  _2202: "2202",
  _2203: "2203",
  _2204: "2204",
  _2205: "2205",
  _2206: "2206"
};
var DexGethistoricalpoolchartdataDexIdEnum = {
  _1000: "1000",
  _1300: "1300",
  _1100: "1100",
  _1500: "1500",
  _1400: "1400"
};
var DexGethistoricaltimeseriesDexIdEnum = {
  _1000: "1000",
  _1300: "1300",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _1100: "1100",
  _1200: "1200",
  _1201: "1201"
};
var DexGethistoricaltransactionsDexIdEnum = {
  _1000: "1000",
  _1300: "1300",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _1100: "1100",
  _1200: "1200",
  _1201: "1201"
};
var DexGetindividualpositionDexIdEnum = {
  _1000: "1000",
  _1300: "1300",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _1100: "1100",
  _1200: "1200",
  _1201: "1201",
  _2800: "2800",
  _2808: "2808",
  _2809: "2809",
  _1500: "1500",
  _1400: "1400",
  _1402: "1402",
  _3800: "3800",
  _3700: "3700"
};
var DexGetliquiditybytickDexIdEnum = {
  _1300: "1300",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _2100: "2100",
  _2800: "2800",
  _2808: "2808",
  _2809: "2809"
};
var DexGetliquiditysourcesDexIdEnum = {
  _2600: "2600",
  _1600: "1600",
  _1602: "1602",
  _1604: "1604",
  _1605: "1605",
  _1607: "1607",
  _1608: "1608",
  _1700: "1700",
  _1701: "1701",
  _1702: "1702",
  _1703: "1703",
  _1704: "1704",
  _1706: "1706",
  _1707: "1707",
  _2200: "2200",
  _2201: "2201",
  _2202: "2202",
  _2203: "2203",
  _2204: "2204",
  _2205: "2205",
  _2206: "2206"
};
var DexGetmakerordersDexIdEnum = {
  _2200: "2200",
  _2201: "2201",
  _2202: "2202",
  _2203: "2203",
  _2204: "2204",
  _2205: "2205",
  _2206: "2206"
};
var DexGetordersDexIdEnum = {
  _1900: "1900",
  _2200: "2200",
  _2201: "2201",
  _2202: "2202",
  _2203: "2203",
  _2204: "2204",
  _2205: "2205",
  _2206: "2206"
};
var DexGetordersSortEnum = {
  Desc: "desc",
  Asc: "asc"
};
var DexGetpoolDexIdEnum = {
  _1000: "1000",
  _1001: "1001",
  _1300: "1300",
  _1301: "1301",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _1310: "1310",
  _1100: "1100",
  _1101: "1101",
  _1200: "1200",
  _1201: "1201",
  _2800: "2800",
  _2808: "2808",
  _2809: "2809",
  _1500: "1500",
  _1400: "1400",
  _1402: "1402",
  _2100: "2100",
  _2101: "2101",
  _3000: "3000",
  _3001: "3001",
  _3600: "3600",
  _3800: "3800",
  _3700: "3700"
};
var DexGetpoolchartdataDexIdEnum = {
  _1000: "1000",
  _1300: "1300",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _1100: "1100"
};
var DexGetpoolindividualliquidityDexIdEnum = {
  _1000: "1000",
  _1001: "1001",
  _1300: "1300",
  _1301: "1301",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _1310: "1310",
  _1100: "1100",
  _1101: "1101",
  _1200: "1200",
  _1201: "1201",
  _2800: "2800",
  _2808: "2808",
  _2809: "2809"
};
var DexGetpoolliquidityDexIdEnum = {
  _1000: "1000",
  _1001: "1001",
  _1300: "1300",
  _1301: "1301",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _1310: "1310",
  _1100: "1100",
  _1101: "1101",
  _1200: "1200",
  _1201: "1201",
  _2800: "2800",
  _2808: "2808",
  _2809: "2809",
  _1500: "1500",
  _1400: "1400",
  _1402: "1402",
  _2300: "2300",
  _2100: "2100",
  _2101: "2101",
  _2500: "2500",
  _2501: "2501",
  _2700: "2700",
  _2400: "2400",
  _2401: "2401",
  _3000: "3000",
  _3001: "3001",
  _3100: "3100",
  _3300: "3300",
  _3301: "3301",
  _3500: "3500",
  _3501: "3501",
  _3400: "3400",
  _3401: "3401",
  _3600: "3600",
  _3800: "3800",
  _3700: "3700"
};
var DexGetpoolsDexIdEnum = {
  _1000: "1000",
  _1900: "1900",
  _1300: "1300",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _1310: "1310",
  _1100: "1100",
  _1200: "1200",
  _1201: "1201",
  _2900: "2900",
  _2901: "2901",
  _2800: "2800",
  _2808: "2808",
  _2809: "2809",
  _1500: "1500",
  _1400: "1400",
  _1402: "1402",
  _2300: "2300",
  _2100: "2100",
  _2101: "2101",
  _2500: "2500",
  _2501: "2501",
  _2700: "2700",
  _2600: "2600",
  _2400: "2400",
  _2401: "2401",
  _1600: "1600",
  _1602: "1602",
  _1604: "1604",
  _1605: "1605",
  _1607: "1607",
  _1608: "1608",
  _1700: "1700",
  _1701: "1701",
  _1702: "1702",
  _1703: "1703",
  _1704: "1704",
  _1706: "1706",
  _1707: "1707",
  _2200: "2200",
  _2201: "2201",
  _2202: "2202",
  _2203: "2203",
  _2204: "2204",
  _2205: "2205",
  _2206: "2206",
  _3000: "3000",
  _3001: "3001",
  _3100: "3100",
  _3300: "3300",
  _3301: "3301"
};
var DexGetpooltradedataDexIdEnum = {
  _1000: "1000",
  _1300: "1300",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _1310: "1310",
  _1100: "1100",
  _1200: "1200",
  _1201: "1201"
};
var DexGetpooltradedataEventTypeEnum = {
  Swap: "Swap",
  Burn: "Burn",
  Mint: "Mint"
};
var DexGetrouteDexIdEnum = {
  _2600: "2600"
};
var DexGettokenidpriceDexIdEnum = {
  _3100: "3100"
};
var DexGettokenliquidityDexIdEnum = {
  _1000: "1000",
  _1001: "1001",
  _1300: "1300",
  _1301: "1301",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _1310: "1310",
  _1100: "1100",
  _1101: "1101",
  _1200: "1200",
  _1201: "1201",
  _2900: "2900",
  _2901: "2901",
  _2800: "2800",
  _2808: "2808",
  _2809: "2809",
  _1500: "1500",
  _1400: "1400",
  _1402: "1402",
  _2300: "2300",
  _2100: "2100",
  _2101: "2101",
  _2500: "2500",
  _2501: "2501",
  _2700: "2700",
  _2400: "2400",
  _2401: "2401",
  _3000: "3000",
  _3001: "3001",
  _3100: "3100",
  _3300: "3300",
  _3301: "3301",
  _3500: "3500",
  _3501: "3501",
  _3400: "3400",
  _3401: "3401",
  _3600: "3600",
  _3800: "3800",
  _3700: "3700"
};
var DexGetuserliquidityDexIdEnum = {
  _1000: "1000",
  _1001: "1001",
  _1300: "1300",
  _1301: "1301",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _1310: "1310",
  _1100: "1100",
  _1101: "1101",
  _1200: "1200",
  _1201: "1201",
  _2900: "2900",
  _2901: "2901",
  _2800: "2800",
  _2808: "2808",
  _2809: "2809",
  _1500: "1500",
  _1400: "1400",
  _1402: "1402",
  _2300: "2300",
  _2100: "2100",
  _2101: "2101",
  _2500: "2500",
  _2501: "2501",
  _2700: "2700",
  _2400: "2400",
  _2401: "2401",
  _3000: "3000",
  _3001: "3001",
  _3100: "3100",
  _3500: "3500",
  _3501: "3501",
  _3400: "3400",
  _3401: "3401",
  _3600: "3600"
};
var DexGetusertokenidsDexIdEnum = {
  _1300: "1300",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _2800: "2800",
  _2808: "2808",
  _2809: "2809"
};
var DexPriceDexIdEnum = {
  _1000: "1000",
  _1001: "1001",
  _1300: "1300",
  _1301: "1301",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _1310: "1310",
  _1100: "1100",
  _1101: "1101",
  _1200: "1200",
  _1201: "1201",
  _2900: "2900",
  _2901: "2901",
  _2800: "2800",
  _2808: "2808",
  _2809: "2809",
  _1500: "1500",
  _1400: "1400",
  _1402: "1402",
  _2300: "2300",
  _2100: "2100",
  _2101: "2101",
  _2500: "2500",
  _2501: "2501",
  _2700: "2700",
  _2600: "2600",
  _2400: "2400",
  _2401: "2401",
  _1600: "1600",
  _1602: "1602",
  _1604: "1604",
  _1605: "1605",
  _1607: "1607",
  _1608: "1608",
  _1700: "1700",
  _1701: "1701",
  _1702: "1702",
  _1703: "1703",
  _1704: "1704",
  _1706: "1706",
  _1707: "1707",
  _2200: "2200",
  _2201: "2201",
  _2202: "2202",
  _2203: "2203",
  _2204: "2204",
  _2205: "2205",
  _2206: "2206",
  _3000: "3000",
  _3001: "3001",
  _3100: "3100",
  _3300: "3300",
  _3301: "3301",
  _3500: "3500",
  _3501: "3501",
  _3400: "3400",
  _3401: "3401",
  _3600: "3600",
  _3800: "3800",
  _3700: "3700"
};
var DexPriceLegacyDexIdEnum = {
  _1000: "1000",
  _1001: "1001",
  _1300: "1300",
  _1301: "1301",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _1310: "1310",
  _1100: "1100",
  _1101: "1101",
  _1200: "1200",
  _1201: "1201",
  _2900: "2900",
  _2901: "2901",
  _2800: "2800",
  _2808: "2808",
  _2809: "2809",
  _1500: "1500",
  _1400: "1400",
  _1402: "1402",
  _2300: "2300",
  _2100: "2100",
  _2101: "2101",
  _2500: "2500",
  _2501: "2501",
  _2700: "2700",
  _2600: "2600",
  _2400: "2400",
  _2401: "2401",
  _1600: "1600",
  _1602: "1602",
  _1604: "1604",
  _1605: "1605",
  _1607: "1607",
  _1608: "1608",
  _1700: "1700",
  _1701: "1701",
  _1702: "1702",
  _1703: "1703",
  _1704: "1704",
  _1706: "1706",
  _1707: "1707",
  _2200: "2200",
  _2201: "2201",
  _2202: "2202",
  _2203: "2203",
  _2204: "2204",
  _2205: "2205",
  _2206: "2206",
  _3000: "3000",
  _3001: "3001",
  _3100: "3100",
  _3300: "3300",
  _3301: "3301",
  _3500: "3500",
  _3501: "3501",
  _3400: "3400",
  _3401: "3401",
  _3600: "3600",
  _3800: "3800",
  _3700: "3700"
};
var DexQuotesChainIdEnum = {
  _1: "1",
  _43114: "43114",
  _56: "56",
  _137: "137",
  _42161: "42161",
  _10: "10",
  _8453: "8453",
  _324: "324",
  _5000: "5000",
  _900: "900",
  _1400: "1400",
  _1200: "1200",
  _101: "101",
  _1700: "1700",
  _1500: "1500",
  _1100: "1100",
  _1600: "1600",
  _8217: "8217"
};
var DexQuotesLegacyChainIdEnum = {
  _1: "1",
  _43114: "43114",
  _56: "56",
  _137: "137",
  _42161: "42161",
  _10: "10",
  _8453: "8453",
  _324: "324",
  _5000: "5000",
  _900: "900",
  _1400: "1400",
  _1200: "1200",
  _101: "101",
  _1700: "1700",
  _1500: "1500",
  _1100: "1100",
  _1600: "1600",
  _8217: "8217"
};

// src/apis/FungibletokenApi.ts
var FungibletokenApi = class extends BaseAPI {
  /**
   * Creates request options for accountV1AllowanceLegacy without sending the request
   */
  async accountV1AllowanceLegacyRequestOpts(requestParameters) {
    if (requestParameters["tokenAddress"] == null) {
      throw new RequiredError(
        "tokenAddress",
        'Required parameter "tokenAddress" was null or undefined when calling accountV1AllowanceLegacy().'
      );
    }
    if (requestParameters["owner"] == null) {
      throw new RequiredError(
        "owner",
        'Required parameter "owner" was null or undefined when calling accountV1AllowanceLegacy().'
      );
    }
    if (requestParameters["spender"] == null) {
      throw new RequiredError(
        "spender",
        'Required parameter "spender" was null or undefined when calling accountV1AllowanceLegacy().'
      );
    }
    const queryParameters = {};
    if (requestParameters["tokenAddress"] != null) {
      queryParameters["tokenAddress"] = requestParameters["tokenAddress"];
    }
    if (requestParameters["owner"] != null) {
      queryParameters["owner"] = requestParameters["owner"];
    }
    if (requestParameters["spender"] != null) {
      queryParameters["spender"] = requestParameters["spender"];
    }
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/fungibletoken/getuserallowance`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves the approved allowance for a specific ERC-20 token, indicating how many tokens a designated spender is authorized to withdraw from an owner’s wallet. It is especially useful for verifying spending limits before initiating transactions, ensuring that decentralized applications or swap interfaces can confirm the available allowance for token transfers.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * Get User Allowance
   */
  async accountV1AllowanceLegacyRaw(requestParameters, initOverrides) {
    const requestOptions = await this.accountV1AllowanceLegacyRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves the approved allowance for a specific ERC-20 token, indicating how many tokens a designated spender is authorized to withdraw from an owner’s wallet. It is especially useful for verifying spending limits before initiating transactions, ensuring that decentralized applications or swap interfaces can confirm the available allowance for token transfers.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * Get User Allowance
   */
  async accountV1AllowanceLegacy(requestParameters, initOverrides) {
    const response = await this.accountV1AllowanceLegacyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for fungibletokenApprove without sending the request
   */
  async fungibletokenApproveRequestOpts(requestParameters) {
    if (requestParameters["fungibletokenApproveRequestPayload"] == null) {
      throw new RequiredError(
        "fungibletokenApproveRequestPayload",
        'Required parameter "fungibletokenApproveRequestPayload" was null or undefined when calling fungibletokenApprove().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/fungibletoken/approve`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["fungibletokenApproveRequestPayload"]
    };
  }
  /**
   * This endpoint is used to grant permission for a designated spender to withdraw tokens from a user\'s ERC-20 token balance. By invoking the standard ERC-20 approve function, it sets an allowance that enables the spender to access up to a specified number of tokens on behalf of the user.  > 💡  **Compute Unit Value:** `80` (Fixed)
   * Approve
   */
  async fungibletokenApproveRaw(requestParameters, initOverrides) {
    const requestOptions = await this.fungibletokenApproveRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * This endpoint is used to grant permission for a designated spender to withdraw tokens from a user\'s ERC-20 token balance. By invoking the standard ERC-20 approve function, it sets an allowance that enables the spender to access up to a specified number of tokens on behalf of the user.  > 💡  **Compute Unit Value:** `80` (Fixed)
   * Approve
   */
  async fungibletokenApprove(requestParameters, initOverrides) {
    const response = await this.fungibletokenApproveRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for fungibletokenConvertbasetokentowraptoken without sending the request
   */
  async fungibletokenConvertbasetokentowraptokenRequestOpts(requestParameters) {
    if (requestParameters["fungibletokenConvertbasetokentowraptokenRequestPayload"] == null) {
      throw new RequiredError(
        "fungibletokenConvertbasetokentowraptokenRequestPayload",
        'Required parameter "fungibletokenConvertbasetokentowraptokenRequestPayload" was null or undefined when calling fungibletokenConvertbasetokentowraptoken().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/fungibletoken/convertbasetokentowraptoken`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["fungibletokenConvertbasetokentowraptokenRequestPayload"]
    };
  }
  /**
   * Facilitates the conversion of native blockchain tokens into their corresponding wrapped token versions. Wrapped tokens are standardized ERC-20 (or similar) representations of native tokens, enabling them to interact seamlessly with decentralized applications, DeFi protocols, and smart contracts.  > 💡  **Compute Unit Value:** `140` (Fixed)
   * Convert Base Token to Wrap Token
   */
  async fungibletokenConvertbasetokentowraptokenRaw(requestParameters, initOverrides) {
    const requestOptions = await this.fungibletokenConvertbasetokentowraptokenRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Facilitates the conversion of native blockchain tokens into their corresponding wrapped token versions. Wrapped tokens are standardized ERC-20 (or similar) representations of native tokens, enabling them to interact seamlessly with decentralized applications, DeFi protocols, and smart contracts.  > 💡  **Compute Unit Value:** `140` (Fixed)
   * Convert Base Token to Wrap Token
   */
  async fungibletokenConvertbasetokentowraptoken(requestParameters, initOverrides) {
    const response = await this.fungibletokenConvertbasetokentowraptokenRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for fungibletokenConvertwraptokentobasetoken without sending the request
   */
  async fungibletokenConvertwraptokentobasetokenRequestOpts(requestParameters) {
    if (requestParameters["fungibletokenConvertwraptokentobasetokenRequestPayload"] == null) {
      throw new RequiredError(
        "fungibletokenConvertwraptokentobasetokenRequestPayload",
        'Required parameter "fungibletokenConvertwraptokentobasetokenRequestPayload" was null or undefined when calling fungibletokenConvertwraptokentobasetoken().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/fungibletoken/convertwraptokentobasetoken`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["fungibletokenConvertwraptokentobasetokenRequestPayload"]
    };
  }
  /**
   * Enables users to convert wrapped ERC-20 tokens back to their underlying base tokens. For example, it allows for the conversion of wrapped staked Ethereum (wstETH) back to its base version (stETH). This unwrapping process is essential for users who need to access the native properties of a token for activities that require the base token format.  > 💡  **Compute Unit Value:** `145` (Fixed)
   * Convert Wrap Token to Base Token
   */
  async fungibletokenConvertwraptokentobasetokenRaw(requestParameters, initOverrides) {
    const requestOptions = await this.fungibletokenConvertwraptokentobasetokenRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Enables users to convert wrapped ERC-20 tokens back to their underlying base tokens. For example, it allows for the conversion of wrapped staked Ethereum (wstETH) back to its base version (stETH). This unwrapping process is essential for users who need to access the native properties of a token for activities that require the base token format.  > 💡  **Compute Unit Value:** `145` (Fixed)
   * Convert Wrap Token to Base Token
   */
  async fungibletokenConvertwraptokentobasetoken(requestParameters, initOverrides) {
    const response = await this.fungibletokenConvertwraptokentobasetokenRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for fungibletokenGettokendetails without sending the request
   */
  async fungibletokenGettokendetailsRequestOpts(requestParameters) {
    if (requestParameters["tokenAddress"] == null) {
      throw new RequiredError(
        "tokenAddress",
        'Required parameter "tokenAddress" was null or undefined when calling fungibletokenGettokendetails().'
      );
    }
    const queryParameters = {};
    if (requestParameters["tokenAddress"] != null) {
      queryParameters["tokenAddress"] = requestParameters["tokenAddress"];
    }
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/fungibletoken/gettokendetails`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves fundamental metadata about a specified ERC-20 token. It provides essential token details such as the token’s name, symbol, and decimal precision, which are critical for accurately displaying token information and performing token arithmetic in decentralized applications.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Get Token Details
   */
  async fungibletokenGettokendetailsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.fungibletokenGettokendetailsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves fundamental metadata about a specified ERC-20 token. It provides essential token details such as the token’s name, symbol, and decimal precision, which are critical for accurately displaying token information and performing token arithmetic in decentralized applications.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Get Token Details
   */
  async fungibletokenGettokendetails(requestParameters, initOverrides) {
    const response = await this.fungibletokenGettokendetailsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for fungibletokenGettokensupply without sending the request
   */
  async fungibletokenGettokensupplyRequestOpts(requestParameters) {
    if (requestParameters["asset"] == null) {
      throw new RequiredError(
        "asset",
        'Required parameter "asset" was null or undefined when calling fungibletokenGettokensupply().'
      );
    }
    const queryParameters = {};
    if (requestParameters["asset"] != null) {
      queryParameters["asset"] = requestParameters["asset"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/fungibletoken/gettokensupply`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieve the returns the supply metrics of a token across different chains.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Get Token Supply
   */
  async fungibletokenGettokensupplyRaw(requestParameters, initOverrides) {
    const requestOptions = await this.fungibletokenGettokensupplyRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieve the returns the supply metrics of a token across different chains.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Get Token Supply
   */
  async fungibletokenGettokensupply(requestParameters, initOverrides) {
    const response = await this.fungibletokenGettokensupplyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for fungibletokenGetuserbalance without sending the request
   */
  async fungibletokenGetuserbalanceRequestOpts(requestParameters) {
    if (requestParameters["tokenAddress"] == null) {
      throw new RequiredError(
        "tokenAddress",
        'Required parameter "tokenAddress" was null or undefined when calling fungibletokenGetuserbalance().'
      );
    }
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling fungibletokenGetuserbalance().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["tokenAddress"] != null) {
      queryParameters["tokenAddress"] = requestParameters["tokenAddress"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/fungibletoken/getuserbalance`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieve the balance of a specified ERC-20 token for a given user address. It provides an essential function for wallet management and decentralized finance applications by offering real-time balance information across multiple blockchain networks.  > 💡  **Compute Unit Value:** `55` (Fixed)
   * Get User Balance
   */
  async fungibletokenGetuserbalanceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.fungibletokenGetuserbalanceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieve the balance of a specified ERC-20 token for a given user address. It provides an essential function for wallet management and decentralized finance applications by offering real-time balance information across multiple blockchain networks.  > 💡  **Compute Unit Value:** `55` (Fixed)
   * Get User Balance
   */
  async fungibletokenGetuserbalance(requestParameters, initOverrides) {
    const response = await this.fungibletokenGetuserbalanceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for fungibletokenHistoricalWeth without sending the request
   */
  async fungibletokenHistoricalWethRequestOpts(requestParameters) {
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["startBlock"] != null) {
      queryParameters["startBlock"] = requestParameters["startBlock"];
    }
    if (requestParameters["endBlock"] != null) {
      queryParameters["endBlock"] = requestParameters["endBlock"];
    }
    if (requestParameters["page"] != null) {
      queryParameters["page"] = requestParameters["page"];
    }
    if (requestParameters["sort"] != null) {
      queryParameters["sort"] = requestParameters["sort"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/fungibletoken/historical/weth`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Fetches all Wrapped ETH (WETH) transfer transactions for a specified user address within a designated block range across supported blockchain networks.  > 💡  **Compute Unit Value:** `155` (Fixed)
   * Historical WETH
   */
  async fungibletokenHistoricalWethRaw(requestParameters, initOverrides) {
    const requestOptions = await this.fungibletokenHistoricalWethRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Fetches all Wrapped ETH (WETH) transfer transactions for a specified user address within a designated block range across supported blockchain networks.  > 💡  **Compute Unit Value:** `155` (Fixed)
   * Historical WETH
   */
  async fungibletokenHistoricalWeth(requestParameters = {}, initOverrides) {
    const response = await this.fungibletokenHistoricalWethRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for fungibletokenHistoricallogs without sending the request
   */
  async fungibletokenHistoricallogsRequestOpts(requestParameters) {
    if (requestParameters["tokenAddress"] == null) {
      throw new RequiredError(
        "tokenAddress",
        'Required parameter "tokenAddress" was null or undefined when calling fungibletokenHistoricallogs().'
      );
    }
    if (requestParameters["type"] == null) {
      throw new RequiredError(
        "type",
        'Required parameter "type" was null or undefined when calling fungibletokenHistoricallogs().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["startBlock"] != null) {
      queryParameters["startBlock"] = requestParameters["startBlock"];
    }
    if (requestParameters["endBlock"] != null) {
      queryParameters["endBlock"] = requestParameters["endBlock"];
    }
    if (requestParameters["tokenAddress"] != null) {
      queryParameters["tokenAddress"] = requestParameters["tokenAddress"];
    }
    if (requestParameters["type"] != null) {
      queryParameters["type"] = requestParameters["type"];
    }
    if (requestParameters["page"] != null) {
      queryParameters["page"] = requestParameters["page"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/fungibletoken/historicallogs`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves historical blockchain logs related to an ERC-20 token for a specified user address within a defined block range. It is designed to capture events such as token approvals and transfers, allowing developers and users to audit past activity, verify transaction histories, and analyze token behavior over time.  > 💡  **Compute Unit Value:** `70` (Fixed)
   * Historical Logs
   */
  async fungibletokenHistoricallogsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.fungibletokenHistoricallogsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves historical blockchain logs related to an ERC-20 token for a specified user address within a defined block range. It is designed to capture events such as token approvals and transfers, allowing developers and users to audit past activity, verify transaction histories, and analyze token behavior over time.  > 💡  **Compute Unit Value:** `70` (Fixed)
   * Historical Logs
   */
  async fungibletokenHistoricallogs(requestParameters, initOverrides) {
    const response = await this.fungibletokenHistoricallogsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for fungibletokenHistoricaltransactions without sending the request
   */
  async fungibletokenHistoricaltransactionsRequestOpts(requestParameters) {
    if (requestParameters["tokenAddress"] == null) {
      throw new RequiredError(
        "tokenAddress",
        'Required parameter "tokenAddress" was null or undefined when calling fungibletokenHistoricaltransactions().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["startBlock"] != null) {
      queryParameters["startBlock"] = requestParameters["startBlock"];
    }
    if (requestParameters["endBlock"] != null) {
      queryParameters["endBlock"] = requestParameters["endBlock"];
    }
    if (requestParameters["page"] != null) {
      queryParameters["page"] = requestParameters["page"];
    }
    if (requestParameters["sort"] != null) {
      queryParameters["sort"] = requestParameters["sort"];
    }
    if (requestParameters["tokenAddress"] != null) {
      queryParameters["tokenAddress"] = requestParameters["tokenAddress"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/fungibletoken/historicaltransactions`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Provides a detailed history of ERC-20 token transfer transactions associated with a specific user address within a defined block range. It is designed for wallet-related data analytics, enabling users and developers to review past token movements, monitor account activity, and perform transaction audits.  > 💡  **Compute Unit Value:** `195` (Fixed)
   * Historical Transactions
   */
  async fungibletokenHistoricaltransactionsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.fungibletokenHistoricaltransactionsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Provides a detailed history of ERC-20 token transfer transactions associated with a specific user address within a defined block range. It is designed for wallet-related data analytics, enabling users and developers to review past token movements, monitor account activity, and perform transaction audits.  > 💡  **Compute Unit Value:** `195` (Fixed)
   * Historical Transactions
   */
  async fungibletokenHistoricaltransactions(requestParameters, initOverrides) {
    const response = await this.fungibletokenHistoricaltransactionsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for fungibletokenHistoricalusertransactions without sending the request
   */
  async fungibletokenHistoricalusertransactionsRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling fungibletokenHistoricalusertransactions().'
      );
    }
    if (requestParameters["tokenAddress"] == null) {
      throw new RequiredError(
        "tokenAddress",
        'Required parameter "tokenAddress" was null or undefined when calling fungibletokenHistoricalusertransactions().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["tokenAddress"] != null) {
      queryParameters["tokenAddress"] = requestParameters["tokenAddress"];
    }
    if (requestParameters["startBlock"] != null) {
      queryParameters["startBlock"] = requestParameters["startBlock"];
    }
    if (requestParameters["endBlock"] != null) {
      queryParameters["endBlock"] = requestParameters["endBlock"];
    }
    if (requestParameters["pageSize"] != null) {
      queryParameters["pageSize"] = requestParameters["pageSize"];
    }
    if (requestParameters["pageToken"] != null) {
      queryParameters["pageToken"] = requestParameters["pageToken"];
    }
    if (requestParameters["sortOrder"] != null) {
      queryParameters["sortOrder"] = requestParameters["sortOrder"];
    }
    if (requestParameters["fromTimestamp"] != null) {
      queryParameters["fromTimestamp"] = requestParameters["fromTimestamp"];
    }
    if (requestParameters["toTimestamp"] != null) {
      queryParameters["toTimestamp"] = requestParameters["toTimestamp"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/fungibletoken/historicalusertransactions`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves all the ERC-20 transfers for the given address.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Historical User Transactions
   */
  async fungibletokenHistoricalusertransactionsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.fungibletokenHistoricalusertransactionsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves all the ERC-20 transfers for the given address.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Historical User Transactions
   */
  async fungibletokenHistoricalusertransactions(requestParameters, initOverrides) {
    const response = await this.fungibletokenHistoricalusertransactionsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for fungibletokenTransfer without sending the request
   */
  async fungibletokenTransferRequestOpts(requestParameters) {
    if (requestParameters["fungibletokenTransferRequestPayload"] == null) {
      throw new RequiredError(
        "fungibletokenTransferRequestPayload",
        'Required parameter "fungibletokenTransferRequestPayload" was null or undefined when calling fungibletokenTransfer().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/fungibletoken/transfer`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["fungibletokenTransferRequestPayload"]
    };
  }
  /**
   * Specifically designed to fetch all Wrapped Ether (WETH) transfer transactions for a given user address. It focuses on historical WETH activity within a user-defined block range, enabling users and developers to track and analyze the flow of WETH across supported blockchain networks.  > 💡  **Compute Unit Value:** `70` (Fixed)
   * Transfer
   */
  async fungibletokenTransferRaw(requestParameters, initOverrides) {
    const requestOptions = await this.fungibletokenTransferRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Specifically designed to fetch all Wrapped Ether (WETH) transfer transactions for a given user address. It focuses on historical WETH activity within a user-defined block range, enabling users and developers to track and analyze the flow of WETH across supported blockchain networks.  > 💡  **Compute Unit Value:** `70` (Fixed)
   * Transfer
   */
  async fungibletokenTransfer(requestParameters, initOverrides) {
    const response = await this.fungibletokenTransferRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for fungibletokenTransferfrom without sending the request
   */
  async fungibletokenTransferfromRequestOpts(requestParameters) {
    if (requestParameters["fungibletokenTransferfromRequestPayload"] == null) {
      throw new RequiredError(
        "fungibletokenTransferfromRequestPayload",
        'Required parameter "fungibletokenTransferfromRequestPayload" was null or undefined when calling fungibletokenTransferfrom().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/fungibletoken/transferfrom`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["fungibletokenTransferfromRequestPayload"]
    };
  }
  /**
   * Facilitates the transfer of ERC-20 tokens from one address to another using the ERC-20 allowance mechanism. It leverages the standard transferFrom function, enabling a spender (previously authorized by the token owner) to move tokens on the owner’s behalf.  > 💡  **Compute Unit Value:** `140` (Fixed)
   * Transfer From
   */
  async fungibletokenTransferfromRaw(requestParameters, initOverrides) {
    const requestOptions = await this.fungibletokenTransferfromRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Facilitates the transfer of ERC-20 tokens from one address to another using the ERC-20 allowance mechanism. It leverages the standard transferFrom function, enabling a spender (previously authorized by the token owner) to move tokens on the owner’s behalf.  > 💡  **Compute Unit Value:** `140` (Fixed)
   * Transfer From
   */
  async fungibletokenTransferfrom(requestParameters, initOverrides) {
    const response = await this.fungibletokenTransferfromRaw(requestParameters, initOverrides);
    return await response.value();
  }
};
var AccountV1AllowanceLegacyChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _137: "137",
  _250: "250",
  _10: "10",
  _11155420: "11155420",
  _42161: "42161",
  _421614: "421614",
  _25: "25",
  _338: "338",
  _84532: "84532",
  _8453: "8453",
  _43113: "43113",
  _97: "97",
  _80002: "80002",
  _300: "300",
  _324: "324",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636",
  _8217: "8217",
  _1001: "1001"
};
var FungibletokenGettokendetailsChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _137: "137",
  _250: "250",
  _10: "10",
  _42161: "42161",
  _25: "25",
  _8453: "8453"
};
var FungibletokenGetuserbalanceChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _137: "137",
  _250: "250",
  _10: "10",
  _11155420: "11155420",
  _42161: "42161",
  _421614: "421614",
  _25: "25",
  _338: "338",
  _84532: "84532",
  _8453: "8453",
  _43113: "43113",
  _97: "97",
  _80002: "80002",
  _300: "300",
  _324: "324",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636"
};
var FungibletokenHistoricalWethChainIdEnum = {
  _1: "1",
  _137: "137",
  _56: "56",
  _25: "25",
  _10: "10",
  _250: "250",
  _43114: "43114",
  _42161: "42161",
  _8453: "8453"
};
var FungibletokenHistoricalWethSortEnum = {
  Desc: "desc",
  Asc: "asc"
};
var FungibletokenHistoricallogsTypeEnum = {
  Approval: "Approval",
  Transfer: "Transfer"
};
var FungibletokenHistoricallogsChainIdEnum = {
  _1: "1",
  _137: "137",
  _56: "56",
  _25: "25",
  _10: "10",
  _250: "250",
  _43114: "43114",
  _42161: "42161",
  _8453: "8453"
};
var FungibletokenHistoricaltransactionsChainIdEnum = {
  _1: "1"
};
var FungibletokenHistoricaltransactionsSortEnum = {
  Desc: "desc",
  Asc: "asc"
};
var FungibletokenHistoricalusertransactionsChainIdEnum = {
  _1: "1",
  _137: "137",
  _56: "56",
  _25: "25",
  _10: "10",
  _250: "250",
  _43114: "43114",
  _42161: "42161",
  _8453: "8453"
};
var FungibletokenHistoricalusertransactionsSortOrderEnum = {
  Desc: "desc",
  Asc: "asc"
};

// src/apis/Getapimetadatav2Api.ts
var Getapimetadatav2Api = class extends BaseAPI {
  /**
   * Creates request options for getapimetadatav2Legacy without sending the request
   */
  async getapimetadatav2LegacyRequestOpts(requestParameters) {
    if (requestParameters["category"] == null) {
      throw new RequiredError(
        "category",
        'Required parameter "category" was null or undefined when calling getapimetadatav2Legacy().'
      );
    }
    if (requestParameters["endpoint"] == null) {
      throw new RequiredError(
        "endpoint",
        'Required parameter "endpoint" was null or undefined when calling getapimetadatav2Legacy().'
      );
    }
    const queryParameters = {};
    if (requestParameters["category"] != null) {
      queryParameters["category"] = requestParameters["category"];
    }
    if (requestParameters["endpoint"] != null) {
      queryParameters["endpoint"] = requestParameters["endpoint"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/getapimetadatav2`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Return the breakdown a breakdown of supported chains and protocol IDs for a given DeFi API endpoint.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Get API Metadata
   */
  async getapimetadatav2LegacyRaw(requestParameters, initOverrides) {
    const requestOptions = await this.getapimetadatav2LegacyRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Return the breakdown a breakdown of supported chains and protocol IDs for a given DeFi API endpoint.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Get API Metadata
   */
  async getapimetadatav2Legacy(requestParameters, initOverrides) {
    const response = await this.getapimetadatav2LegacyRaw(requestParameters, initOverrides);
    return await response.value();
  }
};

// src/apis/LendborrowApi.ts
var LendborrowApi = class extends BaseAPI {
  /**
   * Creates request options for lendborrowAllow without sending the request
   */
  async lendborrowAllowRequestOpts(requestParameters) {
    if (requestParameters["lendborrowAllowRequestPayload"] == null) {
      throw new RequiredError(
        "lendborrowAllowRequestPayload",
        'Required parameter "lendborrowAllowRequestPayload" was null or undefined when calling lendborrowAllow().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/allow`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["lendborrowAllowRequestPayload"]
    };
  }
  /**
   * Grants or revokes permission for designated manager addresses to operate on your account. You specify a manager\'s address along with a flag indicating whether they\'re allowed or disallowed, and the transaction is executed with customizable gas settings.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Allow
   */
  async lendborrowAllowRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowAllowRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Grants or revokes permission for designated manager addresses to operate on your account. You specify a manager\'s address along with a flag indicating whether they\'re allowed or disallowed, and the transaction is executed with customizable gas settings.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Allow
   */
  async lendborrowAllow(requestParameters, initOverrides) {
    const response = await this.lendborrowAllowRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowBorrow without sending the request
   */
  async lendborrowBorrowRequestOpts(requestParameters) {
    if (requestParameters["lendborrowBorrowRequestPayload"] == null) {
      throw new RequiredError(
        "lendborrowBorrowRequestPayload",
        'Required parameter "lendborrowBorrowRequestPayload" was null or undefined when calling lendborrowBorrow().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/borrow`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["lendborrowBorrowRequestPayload"]
    };
  }
  /**
   * Initiates a borrowing transaction for a specified amount of tokens from supported lending markets (Aave V2/V3, Compound V2/V3), allowing configuration of asset, amount, interest rate mode, on-behalf-of address, and gas parameters for an optimized borrowing process.  > 💡  **Compute Unit Value:** `225` (Fixed)
   * Borrow
   */
  async lendborrowBorrowRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowBorrowRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Initiates a borrowing transaction for a specified amount of tokens from supported lending markets (Aave V2/V3, Compound V2/V3), allowing configuration of asset, amount, interest rate mode, on-behalf-of address, and gas parameters for an optimized borrowing process.  > 💡  **Compute Unit Value:** `225` (Fixed)
   * Borrow
   */
  async lendborrowBorrow(requestParameters, initOverrides) {
    const response = await this.lendborrowBorrowRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowBundleactions without sending the request
   */
  async lendborrowBundleactionsRequestOpts(requestParameters) {
    if (requestParameters["lendborrowBundleactionsRequestPayload"] == null) {
      throw new RequiredError(
        "lendborrowBundleactionsRequestPayload",
        'Required parameter "lendborrowBundleactionsRequestPayload" was null or undefined when calling lendborrowBundleactions().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/bundleactions`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["lendborrowBundleactionsRequestPayload"]
    };
  }
  /**
   * Combine multiple lending and borrowing operations into one atomic blockchain transaction. This endpoint bundles several protocol actions—each defined by its specific call data—so that they all execute together (or not at all). The approach improves gas efficiency and minimizes the risk of partial execution, making it ideal for complex, multi-step transactions.  > 💡  **Compute Unit Value:** `45` (Fixed)
   * Bundle Actions
   */
  async lendborrowBundleactionsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowBundleactionsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Combine multiple lending and borrowing operations into one atomic blockchain transaction. This endpoint bundles several protocol actions—each defined by its specific call data—so that they all execute together (or not at all). The approach improves gas efficiency and minimizes the risk of partial execution, making it ideal for complex, multi-step transactions.  > 💡  **Compute Unit Value:** `45` (Fixed)
   * Bundle Actions
   */
  async lendborrowBundleactions(requestParameters, initOverrides) {
    const response = await this.lendborrowBundleactionsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowClaimrewards without sending the request
   */
  async lendborrowClaimrewardsRequestOpts(requestParameters) {
    if (requestParameters["lendborrowClaimrewardsRequestPayload"] == null) {
      throw new RequiredError(
        "lendborrowClaimrewardsRequestPayload",
        'Required parameter "lendborrowClaimrewardsRequestPayload" was null or undefined when calling lendborrowClaimrewards().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/claimrewards`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["lendborrowClaimrewardsRequestPayload"]
    };
  }
  /**
   * Trigger a transaction to claim any accrued rewards from your participation in lending protocols. This endpoint collects your earned rewards and returns detailed transaction data, such as sender/receiver addresses, gas usage, and a reference ID. Customizable gas parameters allow you to optimize for transaction speed and cost.  > 💡  **Compute Unit Value:** `125` (Fixed)
   * Claim Rewards
   */
  async lendborrowClaimrewardsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowClaimrewardsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Trigger a transaction to claim any accrued rewards from your participation in lending protocols. This endpoint collects your earned rewards and returns detailed transaction data, such as sender/receiver addresses, gas usage, and a reference ID. Customizable gas parameters allow you to optimize for transaction speed and cost.  > 💡  **Compute Unit Value:** `125` (Fixed)
   * Claim Rewards
   */
  async lendborrowClaimrewards(requestParameters, initOverrides) {
    const response = await this.lendborrowClaimrewardsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowDeposit without sending the request
   */
  async lendborrowDepositRequestOpts(requestParameters) {
    if (requestParameters["lendborrowDepositRequestPayload"] == null) {
      throw new RequiredError(
        "lendborrowDepositRequestPayload",
        'Required parameter "lendborrowDepositRequestPayload" was null or undefined when calling lendborrowDeposit().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/deposit`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["lendborrowDepositRequestPayload"]
    };
  }
  /**
   * Facilitate the deposit of assets into lending protocols by specifying the asset, deposit amount, and optionally, a recipient address. This endpoint is designed for users to start earning interest or to use their deposited collateral for borrowing. Gas optimization settings ensure that the deposit transaction is both efficient and cost‐effective.  > 💡  **Compute Unit Value:** `230` (Fixed)
   * Deposit
   */
  async lendborrowDepositRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowDepositRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Facilitate the deposit of assets into lending protocols by specifying the asset, deposit amount, and optionally, a recipient address. This endpoint is designed for users to start earning interest or to use their deposited collateral for borrowing. Gas optimization settings ensure that the deposit transaction is both efficient and cost‐effective.  > 💡  **Compute Unit Value:** `230` (Fixed)
   * Deposit
   */
  async lendborrowDeposit(requestParameters, initOverrides) {
    const response = await this.lendborrowDepositRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowEntermarket without sending the request
   */
  async lendborrowEntermarketRequestOpts(requestParameters) {
    if (requestParameters["lendborrowEntermarketRequestPayload"] == null) {
      throw new RequiredError(
        "lendborrowEntermarketRequestPayload",
        'Required parameter "lendborrowEntermarketRequestPayload" was null or undefined when calling lendborrowEntermarket().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/entermarket`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["lendborrowEntermarketRequestPayload"]
    };
  }
  /**
   * Initiate the approval process required for participating in a specific lending market. For example, in Compound, entering a market is a prerequisite before depositing assets and borrowing funds. This endpoint configures your account with the necessary permissions using customizable gas parameters to ensure a smooth setup.  > 💡  **Compute Unit Value:** `90` (Fixed)
   * Enter Market
   */
  async lendborrowEntermarketRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowEntermarketRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Initiate the approval process required for participating in a specific lending market. For example, in Compound, entering a market is a prerequisite before depositing assets and borrowing funds. This endpoint configures your account with the necessary permissions using customizable gas parameters to ensure a smooth setup.  > 💡  **Compute Unit Value:** `90` (Fixed)
   * Enter Market
   */
  async lendborrowEntermarket(requestParameters, initOverrides) {
    const response = await this.lendborrowEntermarketRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowEntermarketstatus without sending the request
   */
  async lendborrowEntermarketstatusRequestOpts(requestParameters) {
    if (requestParameters["account"] == null) {
      throw new RequiredError(
        "account",
        'Required parameter "account" was null or undefined when calling lendborrowEntermarketstatus().'
      );
    }
    const queryParameters = {};
    if (requestParameters["lendborrowId"] != null) {
      queryParameters["lendborrowId"] = requestParameters["lendborrowId"];
    }
    if (requestParameters["account"] != null) {
      queryParameters["account"] = requestParameters["account"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/entermarketstatus`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves the approval status for a user’s participation in Compound protocol markets. It checks whether the necessary permissions have been granted for depositing assets into these markets and returns a list of contract addresses representing the approved markets. Use this endpoint to verify that the user is properly set up for deposit transactions before proceeding.  > 💡  **Compute Unit Value:** `120` (Fixed)
   * Enter Market Status
   */
  async lendborrowEntermarketstatusRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowEntermarketstatusRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves the approval status for a user’s participation in Compound protocol markets. It checks whether the necessary permissions have been granted for depositing assets into these markets and returns a list of contract addresses representing the approved markets. Use this endpoint to verify that the user is properly set up for deposit transactions before proceeding.  > 💡  **Compute Unit Value:** `120` (Fixed)
   * Enter Market Status
   */
  async lendborrowEntermarketstatus(requestParameters, initOverrides) {
    const response = await this.lendborrowEntermarketstatusRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowExitisolationmode without sending the request
   */
  async lendborrowExitisolationmodeRequestOpts(requestParameters) {
    if (requestParameters["lendborrowExitisolationmodeRequestPayload"] == null) {
      throw new RequiredError(
        "lendborrowExitisolationmodeRequestPayload",
        'Required parameter "lendborrowExitisolationmodeRequestPayload" was null or undefined when calling lendborrowExitisolationmode().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/exitisolationmode`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["lendborrowExitisolationmodeRequestPayload"]
    };
  }
  /**
   * Remove isolation mode restrictions on specific assets. If an asset is isolated due to risk management settings, this endpoint allows you to exit isolation mode—restoring full flexibility in its use as collateral. Customizable gas parameters help ensure the operation executes efficiently on-chain.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Exit Isolation Mode
   */
  async lendborrowExitisolationmodeRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowExitisolationmodeRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Remove isolation mode restrictions on specific assets. If an asset is isolated due to risk management settings, this endpoint allows you to exit isolation mode—restoring full flexibility in its use as collateral. Customizable gas parameters help ensure the operation executes efficiently on-chain.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Exit Isolation Mode
   */
  async lendborrowExitisolationmode(requestParameters, initOverrides) {
    const response = await this.lendborrowExitisolationmodeRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowExitmarket without sending the request
   */
  async lendborrowExitmarketRequestOpts(requestParameters) {
    if (requestParameters["lendborrowExitmarketRequestPayload"] == null) {
      throw new RequiredError(
        "lendborrowExitmarketRequestPayload",
        'Required parameter "lendborrowExitmarketRequestPayload" was null or undefined when calling lendborrowExitmarket().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/exitmarket`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["lendborrowExitmarketRequestPayload"]
    };
  }
  /**
   * Process your exit from a lending market. By executing this transaction, you effectively remove your participation (and any associated collateral) from the market, thereby ceasing deposit or borrowing operations on that asset. The endpoint supports customizable gas settings to optimize the exit process.  > 💡  **Compute Unit Value:** `10` (Fixed)
   * Exit Market
   */
  async lendborrowExitmarketRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowExitmarketRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Process your exit from a lending market. By executing this transaction, you effectively remove your participation (and any associated collateral) from the market, thereby ceasing deposit or borrowing operations on that asset. The endpoint supports customizable gas settings to optimize the exit process.  > 💡  **Compute Unit Value:** `10` (Fixed)
   * Exit Market
   */
  async lendborrowExitmarket(requestParameters, initOverrides) {
    const response = await this.lendborrowExitmarketRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowGetassetinfo without sending the request
   */
  async lendborrowGetassetinfoRequestOpts(requestParameters) {
    if (requestParameters["asset"] == null) {
      throw new RequiredError(
        "asset",
        'Required parameter "asset" was null or undefined when calling lendborrowGetassetinfo().'
      );
    }
    const queryParameters = {};
    if (requestParameters["lendborrowId"] != null) {
      queryParameters["lendborrowId"] = requestParameters["lendborrowId"];
    }
    if (requestParameters["asset"] != null) {
      queryParameters["asset"] = requestParameters["asset"];
    }
    if (requestParameters["market"] != null) {
      queryParameters["market"] = requestParameters["market"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/getassetinfo`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Returns comprehensive asset metrics including liquidation thresholds, collateral factors, and price feed data for specified tokens in lending markets.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Get Asset Info
   */
  async lendborrowGetassetinfoRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowGetassetinfoRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Returns comprehensive asset metrics including liquidation thresholds, collateral factors, and price feed data for specified tokens in lending markets.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Get Asset Info
   */
  async lendborrowGetassetinfo(requestParameters, initOverrides) {
    const response = await this.lendborrowGetassetinfoRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowGetclaimedrewards without sending the request
   */
  async lendborrowGetclaimedrewardsRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling lendborrowGetclaimedrewards().'
      );
    }
    const queryParameters = {};
    if (requestParameters["lendborrowId"] != null) {
      queryParameters["lendborrowId"] = requestParameters["lendborrowId"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["market"] != null) {
      queryParameters["market"] = requestParameters["market"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/getclaimedrewards`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Fetch the history and cumulative amount of protocol rewards you have claimed. This endpoint provides insight into your reward accrual over time, helping you track your earnings and assess the overall profitability of your lending activities.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Get Claimed Rewards
   */
  async lendborrowGetclaimedrewardsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowGetclaimedrewardsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Fetch the history and cumulative amount of protocol rewards you have claimed. This endpoint provides insight into your reward accrual over time, helping you track your earnings and assess the overall profitability of your lending activities.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Get Claimed Rewards
   */
  async lendborrowGetclaimedrewards(requestParameters, initOverrides) {
    const response = await this.lendborrowGetclaimedrewardsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowGetgovernordata without sending the request
   */
  async lendborrowGetgovernordataRequestOpts(requestParameters) {
    const queryParameters = {};
    if (requestParameters["lendborrowId"] != null) {
      queryParameters["lendborrowId"] = requestParameters["lendborrowId"];
    }
    if (requestParameters["market"] != null) {
      queryParameters["market"] = requestParameters["market"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/getgovernordata`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieve governance and market configuration data for the lending protocol. The returned data covers the protocol’s pause statuses, configuration parameters, and asset-specific settings. This information is vital for understanding how governance decisions and protocol parameters may impact your lending and borrowing positions.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Get Governor Data
   */
  async lendborrowGetgovernordataRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowGetgovernordataRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieve governance and market configuration data for the lending protocol. The returned data covers the protocol’s pause statuses, configuration parameters, and asset-specific settings. This information is vital for understanding how governance decisions and protocol parameters may impact your lending and borrowing positions.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Get Governor Data
   */
  async lendborrowGetgovernordata(requestParameters = {}, initOverrides) {
    const response = await this.lendborrowGetgovernordataRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowGetmaxamounts without sending the request
   */
  async lendborrowGetmaxamountsRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling lendborrowGetmaxamounts().'
      );
    }
    if (requestParameters["asset"] == null) {
      throw new RequiredError(
        "asset",
        'Required parameter "asset" was null or undefined when calling lendborrowGetmaxamounts().'
      );
    }
    const queryParameters = {};
    if (requestParameters["lendborrowId"] != null) {
      queryParameters["lendborrowId"] = requestParameters["lendborrowId"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["asset"] != null) {
      queryParameters["asset"] = requestParameters["asset"];
    }
    if (requestParameters["market"] != null) {
      queryParameters["market"] = requestParameters["market"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/getmaxamounts`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Calculate the maximum amounts you can withdraw or borrow for a specific asset. Based on your collateral and current market conditions, this endpoint determines your borrowing and withdrawal limits, empowering you to manage risk and plan your next steps effectively.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Get Max Amounts
   */
  async lendborrowGetmaxamountsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowGetmaxamountsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Calculate the maximum amounts you can withdraw or borrow for a specific asset. Based on your collateral and current market conditions, this endpoint determines your borrowing and withdrawal limits, empowering you to manage risk and plan your next steps effectively.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Get Max Amounts
   */
  async lendborrowGetmaxamounts(requestParameters, initOverrides) {
    const response = await this.lendborrowGetmaxamountsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowGetuseraccountdata without sending the request
   */
  async lendborrowGetuseraccountdataRequestOpts(requestParameters) {
    if (requestParameters["asset"] == null) {
      throw new RequiredError(
        "asset",
        'Required parameter "asset" was null or undefined when calling lendborrowGetuseraccountdata().'
      );
    }
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling lendborrowGetuseraccountdata().'
      );
    }
    if (requestParameters["interestRateMode"] == null) {
      throw new RequiredError(
        "interestRateMode",
        'Required parameter "interestRateMode" was null or undefined when calling lendborrowGetuseraccountdata().'
      );
    }
    if (requestParameters["marketId"] == null) {
      throw new RequiredError(
        "marketId",
        'Required parameter "marketId" was null or undefined when calling lendborrowGetuseraccountdata().'
      );
    }
    const queryParameters = {};
    if (requestParameters["lendborrowId"] != null) {
      queryParameters["lendborrowId"] = requestParameters["lendborrowId"];
    }
    if (requestParameters["asset"] != null) {
      queryParameters["asset"] = requestParameters["asset"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["interestRateMode"] != null) {
      queryParameters["interestRateMode"] = requestParameters["interestRateMode"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    if (requestParameters["useCustomVaults"] != null) {
      queryParameters["useCustomVaults"] = requestParameters["useCustomVaults"];
    }
    if (requestParameters["market"] != null) {
      queryParameters["market"] = requestParameters["market"];
    }
    if (requestParameters["marketId"] != null) {
      queryParameters["marketId"] = requestParameters["marketId"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/getuseraccountdata`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Provide detailed account metrics for a user, including available amounts for repayment, borrowing, and withdrawal, as well as the health factor—a key indicator of account risk. This endpoint is crucial for monitoring your overall position and managing risk within the lending protocol.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Get User Account Data
   */
  async lendborrowGetuseraccountdataRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowGetuseraccountdataRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Provide detailed account metrics for a user, including available amounts for repayment, borrowing, and withdrawal, as well as the health factor—a key indicator of account risk. This endpoint is crucial for monitoring your overall position and managing risk within the lending protocol.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Get User Account Data
   */
  async lendborrowGetuseraccountdata(requestParameters, initOverrides) {
    const response = await this.lendborrowGetuseraccountdataRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowMigrate without sending the request
   */
  async lendborrowMigrateRequestOpts(requestParameters) {
    if (requestParameters["lendborrowMigrateRequestPayload"] == null) {
      throw new RequiredError(
        "lendborrowMigrateRequestPayload",
        'Required parameter "lendborrowMigrateRequestPayload" was null or undefined when calling lendborrowMigrate().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/migrate`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["lendborrowMigrateRequestPayload"]
    };
  }
  /**
   * Facilitate the migration of your positions between different lending protocols. This endpoint enables you to seamlessly transfer your assets from one protocol to another, ensuring continuity of your positions while optimizing gas usage and minimizing downtime.  > 💡  **Compute Unit Value:** `105` (Fixed)
   * Migrate
   */
  async lendborrowMigrateRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowMigrateRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Facilitate the migration of your positions between different lending protocols. This endpoint enables you to seamlessly transfer your assets from one protocol to another, ensuring continuity of your positions while optimizing gas usage and minimizing downtime.  > 💡  **Compute Unit Value:** `105` (Fixed)
   * Migrate
   */
  async lendborrowMigrate(requestParameters, initOverrides) {
    const response = await this.lendborrowMigrateRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowPool without sending the request
   */
  async lendborrowPoolRequestOpts(requestParameters) {
    if (requestParameters["asset"] == null) {
      throw new RequiredError(
        "asset",
        'Required parameter "asset" was null or undefined when calling lendborrowPool().'
      );
    }
    const queryParameters = {};
    if (requestParameters["lendborrowId"] != null) {
      queryParameters["lendborrowId"] = requestParameters["lendborrowId"];
    }
    if (requestParameters["asset"] != null) {
      queryParameters["asset"] = requestParameters["asset"];
    }
    if (requestParameters["useCustomVaults"] != null) {
      queryParameters["useCustomVaults"] = requestParameters["useCustomVaults"];
    }
    if (requestParameters["marketId"] != null) {
      queryParameters["marketId"] = requestParameters["marketId"];
    }
    if (requestParameters["vaultAddress"] != null) {
      queryParameters["vaultAddress"] = requestParameters["vaultAddress"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/pool`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieve current lending and borrowing rates (APYs) for a specified asset within a pool. You receive key metrics such as variable/stable borrow and supply rates, loan-to-value ratios, available liquidity, and reserve size. This data is essential for evaluating the attractiveness and risk of entering or exiting a market.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Get Pool
   */
  async lendborrowPoolRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowPoolRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieve current lending and borrowing rates (APYs) for a specified asset within a pool. You receive key metrics such as variable/stable borrow and supply rates, loan-to-value ratios, available liquidity, and reserve size. This data is essential for evaluating the attractiveness and risk of entering or exiting a market.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Get Pool
   */
  async lendborrowPool(requestParameters, initOverrides) {
    const response = await this.lendborrowPoolRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowPoolLegacy without sending the request
   */
  async lendborrowPoolLegacyRequestOpts(requestParameters) {
    if (requestParameters["asset"] == null) {
      throw new RequiredError(
        "asset",
        'Required parameter "asset" was null or undefined when calling lendborrowPoolLegacy().'
      );
    }
    const queryParameters = {};
    if (requestParameters["lendborrowId"] != null) {
      queryParameters["lendborrowId"] = requestParameters["lendborrowId"];
    }
    if (requestParameters["asset"] != null) {
      queryParameters["asset"] = requestParameters["asset"];
    }
    if (requestParameters["useCustomVaults"] != null) {
      queryParameters["useCustomVaults"] = requestParameters["useCustomVaults"];
    }
    if (requestParameters["marketId"] != null) {
      queryParameters["marketId"] = requestParameters["marketId"];
    }
    if (requestParameters["vaultAddress"] != null) {
      queryParameters["vaultAddress"] = requestParameters["vaultAddress"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/getpool`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieve current lending and borrowing rates (APYs) for a specified asset within a pool. You receive key metrics such as variable/stable borrow and supply rates, loan-to-value ratios, available liquidity, and reserve size. This data is essential for evaluating the attractiveness and risk of entering or exiting a market.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Get Pool
   */
  async lendborrowPoolLegacyRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowPoolLegacyRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieve current lending and borrowing rates (APYs) for a specified asset within a pool. You receive key metrics such as variable/stable borrow and supply rates, loan-to-value ratios, available liquidity, and reserve size. This data is essential for evaluating the attractiveness and risk of entering or exiting a market.  > 💡  **Compute Unit Value:** `110` (Fixed)
   * Get Pool
   */
  async lendborrowPoolLegacy(requestParameters, initOverrides) {
    const response = await this.lendborrowPoolLegacyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowPools without sending the request
   */
  async lendborrowPoolsRequestOpts(requestParameters) {
    if (requestParameters["assets"] == null) {
      throw new RequiredError(
        "assets",
        'Required parameter "assets" was null or undefined when calling lendborrowPools().'
      );
    }
    const queryParameters = {};
    if (requestParameters["lendborrowId"] != null) {
      queryParameters["lendborrowId"] = requestParameters["lendborrowId"];
    }
    if (requestParameters["assets"] != null) {
      queryParameters["assets"] = requestParameters["assets"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    if (requestParameters["useCustomVaults"] != null) {
      queryParameters["useCustomVaults"] = requestParameters["useCustomVaults"];
    }
    if (requestParameters["markets"] != null) {
      queryParameters["markets"] = requestParameters["markets"];
    }
    if (requestParameters["vaults"] != null) {
      queryParameters["vaults"] = requestParameters["vaults"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/pools`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Fetch a comprehensive overview of supply and borrow APYs across multiple assets in lending pools. The endpoint aggregates data such as interest rates, liquidity, reserve sizes, and token-specific details (like symbols and addresses) to help you compare opportunities across different markets.  > 💡  **Compute Unit Value:** `135` (Fixed)
   * Get Pools
   */
  async lendborrowPoolsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowPoolsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Fetch a comprehensive overview of supply and borrow APYs across multiple assets in lending pools. The endpoint aggregates data such as interest rates, liquidity, reserve sizes, and token-specific details (like symbols and addresses) to help you compare opportunities across different markets.  > 💡  **Compute Unit Value:** `135` (Fixed)
   * Get Pools
   */
  async lendborrowPools(requestParameters, initOverrides) {
    const response = await this.lendborrowPoolsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowPoolsLegacy without sending the request
   */
  async lendborrowPoolsLegacyRequestOpts(requestParameters) {
    if (requestParameters["assets"] == null) {
      throw new RequiredError(
        "assets",
        'Required parameter "assets" was null or undefined when calling lendborrowPoolsLegacy().'
      );
    }
    const queryParameters = {};
    if (requestParameters["lendborrowId"] != null) {
      queryParameters["lendborrowId"] = requestParameters["lendborrowId"];
    }
    if (requestParameters["assets"] != null) {
      queryParameters["assets"] = requestParameters["assets"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    if (requestParameters["useCustomVaults"] != null) {
      queryParameters["useCustomVaults"] = requestParameters["useCustomVaults"];
    }
    if (requestParameters["markets"] != null) {
      queryParameters["markets"] = requestParameters["markets"];
    }
    if (requestParameters["vaults"] != null) {
      queryParameters["vaults"] = requestParameters["vaults"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/getpools`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Fetch a comprehensive overview of supply and borrow APYs across multiple assets in lending pools. The endpoint aggregates data such as interest rates, liquidity, reserve sizes, and token-specific details (like symbols and addresses) to help you compare opportunities across different markets.  > 💡  **Compute Unit Value:** `135` (Fixed)
   * Get Pools
   */
  async lendborrowPoolsLegacyRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowPoolsLegacyRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Fetch a comprehensive overview of supply and borrow APYs across multiple assets in lending pools. The endpoint aggregates data such as interest rates, liquidity, reserve sizes, and token-specific details (like symbols and addresses) to help you compare opportunities across different markets.  > 💡  **Compute Unit Value:** `135` (Fixed)
   * Get Pools
   */
  async lendborrowPoolsLegacy(requestParameters, initOverrides) {
    const response = await this.lendborrowPoolsLegacyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowRepay without sending the request
   */
  async lendborrowRepayRequestOpts(requestParameters) {
    if (requestParameters["lendborrowRepayRequestPayload"] == null) {
      throw new RequiredError(
        "lendborrowRepayRequestPayload",
        'Required parameter "lendborrowRepayRequestPayload" was null or undefined when calling lendborrowRepay().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/repay`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["lendborrowRepayRequestPayload"]
    };
  }
  /**
   * Process the repayment of borrowed assets to the lending protocol. Whether you’re reducing your debt or fully repaying a loan, this endpoint lets you specify the asset, amount, and repayment mode. Customizable gas parameters allow you to tailor the transaction for cost efficiency and timely execution.  > 💡  **Compute Unit Value:** `255` (Fixed)
   * Repay
   */
  async lendborrowRepayRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowRepayRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Process the repayment of borrowed assets to the lending protocol. Whether you’re reducing your debt or fully repaying a loan, this endpoint lets you specify the asset, amount, and repayment mode. Customizable gas parameters allow you to tailor the transaction for cost efficiency and timely execution.  > 💡  **Compute Unit Value:** `255` (Fixed)
   * Repay
   */
  async lendborrowRepay(requestParameters, initOverrides) {
    const response = await this.lendborrowRepayRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowSetuseremode without sending the request
   */
  async lendborrowSetuseremodeRequestOpts(requestParameters) {
    if (requestParameters["lendborrowSetuseremodeRequestPayload"] == null) {
      throw new RequiredError(
        "lendborrowSetuseremodeRequestPayload",
        'Required parameter "lendborrowSetuseremodeRequestPayload" was null or undefined when calling lendborrowSetuseremode().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/setuseremode`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["lendborrowSetuseremodeRequestPayload"]
    };
  }
  /**
   * Configure your E-Mode (efficiency mode) settings within the lending protocol. By selecting the appropriate category (such as default or stablecoin mode), you can optimize your borrowing power and interest rates. Customizable gas settings ensure that the adjustment is processed quickly and efficiently.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Set User E-Mode
   */
  async lendborrowSetuseremodeRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowSetuseremodeRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Configure your E-Mode (efficiency mode) settings within the lending protocol. By selecting the appropriate category (such as default or stablecoin mode), you can optimize your borrowing power and interest rates. Customizable gas settings ensure that the adjustment is processed quickly and efficiently.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Set User E-Mode
   */
  async lendborrowSetuseremode(requestParameters, initOverrides) {
    const response = await this.lendborrowSetuseremodeRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowTransfer without sending the request
   */
  async lendborrowTransferRequestOpts(requestParameters) {
    if (requestParameters["lendborrowTransferRequestPayload"] == null) {
      throw new RequiredError(
        "lendborrowTransferRequestPayload",
        'Required parameter "lendborrowTransferRequestPayload" was null or undefined when calling lendborrowTransfer().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/transfer`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["lendborrowTransferRequestPayload"]
    };
  }
  /**
   * Execute an internal transfer of deposited assets between accounts within the protocol. This operation enables you to reassign collateral or reallocate funds without exiting the system, all while optimizing gas usage to keep costs low.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Transfer
   */
  async lendborrowTransferRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowTransferRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Execute an internal transfer of deposited assets between accounts within the protocol. This operation enables you to reassign collateral or reallocate funds without exiting the system, all while optimizing gas usage to keep costs low.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Transfer
   */
  async lendborrowTransfer(requestParameters, initOverrides) {
    const response = await this.lendborrowTransferRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowUserpositions without sending the request
   */
  async lendborrowUserpositionsRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling lendborrowUserpositions().'
      );
    }
    const queryParameters = {};
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["lendborrowId"] != null) {
      queryParameters["lendborrowId"] = requestParameters["lendborrowId"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    if (requestParameters["assets"] != null) {
      queryParameters["assets"] = requestParameters["assets"];
    }
    if (requestParameters["useCustomVaults"] != null) {
      queryParameters["useCustomVaults"] = requestParameters["useCustomVaults"];
    }
    if (requestParameters["marketId"] != null) {
      queryParameters["marketId"] = requestParameters["marketId"];
    }
    if (requestParameters["vaultAddress"] != null) {
      queryParameters["vaultAddress"] = requestParameters["vaultAddress"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/userpositions`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Deliver a comprehensive snapshot of your active lending and borrowing positions across supported markets. The response details each position’s collateral (such as aTokens and underlying assets), balance scales, and collateral usage status, offering a clear picture of your overall exposure and risk profile.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Get User Positions
   */
  async lendborrowUserpositionsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowUserpositionsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Deliver a comprehensive snapshot of your active lending and borrowing positions across supported markets. The response details each position’s collateral (such as aTokens and underlying assets), balance scales, and collateral usage status, offering a clear picture of your overall exposure and risk profile.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Get User Positions
   */
  async lendborrowUserpositions(requestParameters, initOverrides) {
    const response = await this.lendborrowUserpositionsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowUserpositionsLegacy without sending the request
   */
  async lendborrowUserpositionsLegacyRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling lendborrowUserpositionsLegacy().'
      );
    }
    const queryParameters = {};
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["lendborrowId"] != null) {
      queryParameters["lendborrowId"] = requestParameters["lendborrowId"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    if (requestParameters["assets"] != null) {
      queryParameters["assets"] = requestParameters["assets"];
    }
    if (requestParameters["useCustomVaults"] != null) {
      queryParameters["useCustomVaults"] = requestParameters["useCustomVaults"];
    }
    if (requestParameters["marketId"] != null) {
      queryParameters["marketId"] = requestParameters["marketId"];
    }
    if (requestParameters["vaultAddress"] != null) {
      queryParameters["vaultAddress"] = requestParameters["vaultAddress"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/getuserpositions`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Deliver a comprehensive snapshot of your active lending and borrowing positions across supported markets. The response details each position’s collateral (such as aTokens and underlying assets), balance scales, and collateral usage status, offering a clear picture of your overall exposure and risk profile.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Get User Positions
   */
  async lendborrowUserpositionsLegacyRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowUserpositionsLegacyRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Deliver a comprehensive snapshot of your active lending and borrowing positions across supported markets. The response details each position’s collateral (such as aTokens and underlying assets), balance scales, and collateral usage status, offering a clear picture of your overall exposure and risk profile.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Get User Positions
   */
  async lendborrowUserpositionsLegacy(requestParameters, initOverrides) {
    const response = await this.lendborrowUserpositionsLegacyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for lendborrowWithdraw without sending the request
   */
  async lendborrowWithdrawRequestOpts(requestParameters) {
    if (requestParameters["lendborrowWithdrawRequestPayload"] == null) {
      throw new RequiredError(
        "lendborrowWithdrawRequestPayload",
        'Required parameter "lendborrowWithdrawRequestPayload" was null or undefined when calling lendborrowWithdraw().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/lendborrow/withdraw`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["lendborrowWithdrawRequestPayload"]
    };
  }
  /**
   * Process the withdrawal of your deposited assets from the lending protocol. Specify the asset, amount, and recipient address, and use customizable gas parameters to ensure that the withdrawal is executed efficiently. This endpoint allows you to retrieve funds when needed while managing your overall position.  > 💡  **Compute Unit Value:** `205` (Fixed)
   * Withdraw
   */
  async lendborrowWithdrawRaw(requestParameters, initOverrides) {
    const requestOptions = await this.lendborrowWithdrawRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Process the withdrawal of your deposited assets from the lending protocol. Specify the asset, amount, and recipient address, and use customizable gas parameters to ensure that the withdrawal is executed efficiently. This endpoint allows you to retrieve funds when needed while managing your overall position.  > 💡  **Compute Unit Value:** `205` (Fixed)
   * Withdraw
   */
  async lendborrowWithdraw(requestParameters, initOverrides) {
    const response = await this.lendborrowWithdrawRaw(requestParameters, initOverrides);
    return await response.value();
  }
};
var LendborrowEntermarketstatusLendborrowIdEnum = {
  _1100: "1100"
};
var LendborrowGetassetinfoLendborrowIdEnum = {
  _1300: "1300",
  _1302: "1302"
};
var LendborrowGetclaimedrewardsLendborrowIdEnum = {
  _1300: "1300",
  _1302: "1302"
};
var LendborrowGetgovernordataLendborrowIdEnum = {
  _1300: "1300",
  _1302: "1302"
};
var LendborrowGetmaxamountsLendborrowIdEnum = {
  _1300: "1300",
  _1302: "1302"
};
var LendborrowGetuseraccountdataLendborrowIdEnum = {
  _1000: "1000",
  _1003: "1003",
  _1005: "1005",
  _1200: "1200",
  _1202: "1202",
  _1203: "1203",
  _1204: "1204",
  _1205: "1205",
  _1206: "1206",
  _1207: "1207",
  _1100: "1100",
  _1300: "1300",
  _1302: "1302",
  _1400: "1400",
  _1401: "1401",
  _1402: "1402",
  _1403: "1403",
  _1404: "1404"
};
var LendborrowPoolLendborrowIdEnum = {
  _1000: "1000",
  _1003: "1003",
  _1005: "1005",
  _1200: "1200",
  _1202: "1202",
  _1203: "1203",
  _1204: "1204",
  _1205: "1205",
  _1206: "1206",
  _1207: "1207",
  _1100: "1100",
  _1300: "1300",
  _1302: "1302",
  _1400: "1400",
  _1401: "1401",
  _1402: "1402",
  _1403: "1403",
  _1404: "1404"
};
var LendborrowPoolLegacyLendborrowIdEnum = {
  _1000: "1000",
  _1003: "1003",
  _1005: "1005",
  _1200: "1200",
  _1202: "1202",
  _1203: "1203",
  _1204: "1204",
  _1205: "1205",
  _1206: "1206",
  _1207: "1207",
  _1100: "1100",
  _1300: "1300",
  _1302: "1302",
  _1400: "1400",
  _1401: "1401",
  _1402: "1402",
  _1403: "1403",
  _1404: "1404"
};
var LendborrowPoolsLendborrowIdEnum = {
  _1000: "1000",
  _1003: "1003",
  _1005: "1005",
  _1200: "1200",
  _1202: "1202",
  _1203: "1203",
  _1204: "1204",
  _1205: "1205",
  _1206: "1206",
  _1207: "1207",
  _1100: "1100",
  _1300: "1300",
  _1302: "1302",
  _1400: "1400",
  _1401: "1401",
  _1402: "1402",
  _1403: "1403",
  _1404: "1404"
};
var LendborrowPoolsLegacyLendborrowIdEnum = {
  _1000: "1000",
  _1003: "1003",
  _1005: "1005",
  _1200: "1200",
  _1202: "1202",
  _1203: "1203",
  _1204: "1204",
  _1205: "1205",
  _1206: "1206",
  _1207: "1207",
  _1100: "1100",
  _1300: "1300",
  _1302: "1302",
  _1400: "1400",
  _1401: "1401",
  _1402: "1402",
  _1403: "1403",
  _1404: "1404"
};
var LendborrowUserpositionsLendborrowIdEnum = {
  _1000: "1000",
  _1003: "1003",
  _1005: "1005",
  _1200: "1200",
  _1202: "1202",
  _1203: "1203",
  _1204: "1204",
  _1205: "1205",
  _1206: "1206",
  _1207: "1207",
  _1100: "1100",
  _1300: "1300",
  _1302: "1302",
  _1400: "1400",
  _1401: "1401",
  _1402: "1402",
  _1403: "1403",
  _1404: "1404"
};
var LendborrowUserpositionsLegacyLendborrowIdEnum = {
  _1000: "1000",
  _1003: "1003",
  _1005: "1005",
  _1200: "1200",
  _1202: "1202",
  _1203: "1203",
  _1204: "1204",
  _1205: "1205",
  _1206: "1206",
  _1207: "1207",
  _1100: "1100",
  _1300: "1300",
  _1302: "1302",
  _1400: "1400",
  _1401: "1401",
  _1402: "1402",
  _1403: "1403",
  _1404: "1404"
};

// src/apis/LiquidstakingApi.ts
var LiquidstakingApi = class extends BaseAPI {
  /**
   * Creates request options for liquidstakingApprovewithdrawal without sending the request
   */
  async liquidstakingApprovewithdrawalRequestOpts(requestParameters) {
    if (requestParameters["liquidstakingApprovewithdrawalRequestPayload"] == null) {
      throw new RequiredError(
        "liquidstakingApprovewithdrawalRequestPayload",
        'Required parameter "liquidstakingApprovewithdrawalRequestPayload" was null or undefined when calling liquidstakingApprovewithdrawal().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/approvewithdrawal`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["liquidstakingApprovewithdrawalRequestPayload"]
    };
  }
  /**
   * Initiate a blockchain transaction that grants permission for a pending withdrawal request. This endpoint verifies that the specified user address is authorized to withdraw staked tokens, ensuring the withdrawal request is properly approved before execution.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Approve Withdrawal
   */
  async liquidstakingApprovewithdrawalRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingApprovewithdrawalRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Initiate a blockchain transaction that grants permission for a pending withdrawal request. This endpoint verifies that the specified user address is authorized to withdraw staked tokens, ensuring the withdrawal request is properly approved before execution.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Approve Withdrawal
   */
  async liquidstakingApprovewithdrawal(requestParameters, initOverrides) {
    const response = await this.liquidstakingApprovewithdrawalRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingBurn without sending the request
   */
  async liquidstakingBurnRequestOpts(requestParameters) {
    if (requestParameters["liquidstakingBurnRequestPayload"] == null) {
      throw new RequiredError(
        "liquidstakingBurnRequestPayload",
        'Required parameter "liquidstakingBurnRequestPayload" was null or undefined when calling liquidstakingBurn().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/burn`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["liquidstakingBurnRequestPayload"]
    };
  }
  /**
   * Execute a burn transaction that permanently removes a specified amount of osETH tokens from circulation. This operation adjusts the circulating supply in accordance with the protocol’s tokenomics and can affect the overall staking balance.  > 💡  **Compute Unit Value:** `20` (Fixed)
   * Burn
   */
  async liquidstakingBurnRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingBurnRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Execute a burn transaction that permanently removes a specified amount of osETH tokens from circulation. This operation adjusts the circulating supply in accordance with the protocol’s tokenomics and can affect the overall staking balance.  > 💡  **Compute Unit Value:** `20` (Fixed)
   * Burn
   */
  async liquidstakingBurn(requestParameters, initOverrides) {
    const response = await this.liquidstakingBurnRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingClaim without sending the request
   */
  async liquidstakingClaimRequestOpts(requestParameters) {
    if (requestParameters["liquidstakingClaimRequestPayload"] == null) {
      throw new RequiredError(
        "liquidstakingClaimRequestPayload",
        'Required parameter "liquidstakingClaimRequestPayload" was null or undefined when calling liquidstakingClaim().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/claim`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["liquidstakingClaimRequestPayload"]
    };
  }
  /**
   * Process the claiming of accrued staking rewards. This endpoint allows you to collect earned rewards by specifying token amounts and adjusting gas parameters for optimal transaction speed and cost efficiency across different liquid staking protocols.  > 💡  **Compute Unit Value:** `35` (Fixed)
   * Claim
   */
  async liquidstakingClaimRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingClaimRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Process the claiming of accrued staking rewards. This endpoint allows you to collect earned rewards by specifying token amounts and adjusting gas parameters for optimal transaction speed and cost efficiency across different liquid staking protocols.  > 💡  **Compute Unit Value:** `35` (Fixed)
   * Claim
   */
  async liquidstakingClaim(requestParameters, initOverrides) {
    const response = await this.liquidstakingClaimRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingDecreaseallowance without sending the request
   */
  async liquidstakingDecreaseallowanceRequestOpts(requestParameters) {
    if (requestParameters["liquidstakingDecreaseallowanceRequestPayload"] == null) {
      throw new RequiredError(
        "liquidstakingDecreaseallowanceRequestPayload",
        'Required parameter "liquidstakingDecreaseallowanceRequestPayload" was null or undefined when calling liquidstakingDecreaseallowance().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/decreaseallowance`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["liquidstakingDecreaseallowanceRequestPayload"]
    };
  }
  /**
   * Reduce the spending allowance for staked tokens by a specified amount. This transaction lets you fine-tune the approved amount that a spender can use on your behalf, with customizable gas settings to ensure efficient execution.  > 💡  **Compute Unit Value:** `55` (Fixed)
   * Decrease Allowance
   */
  async liquidstakingDecreaseallowanceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingDecreaseallowanceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Reduce the spending allowance for staked tokens by a specified amount. This transaction lets you fine-tune the approved amount that a spender can use on your behalf, with customizable gas settings to ensure efficient execution.  > 💡  **Compute Unit Value:** `55` (Fixed)
   * Decrease Allowance
   */
  async liquidstakingDecreaseallowance(requestParameters, initOverrides) {
    const response = await this.liquidstakingDecreaseallowanceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingDelegate without sending the request
   */
  async liquidstakingDelegateRequestOpts(requestParameters) {
    if (requestParameters["liquidstakingDelegateRequestPayload"] == null) {
      throw new RequiredError(
        "liquidstakingDelegateRequestPayload",
        'Required parameter "liquidstakingDelegateRequestPayload" was null or undefined when calling liquidstakingDelegate().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/delegate`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["liquidstakingDelegateRequestPayload"]
    };
  }
  /**
   * Initiate the delegation of staked assets to a chosen operator contract. By delegating your tokens, you can potentially enhance yield or participate in network operations, with configurable gas parameters to suit current network conditions.  > 💡  **Compute Unit Value:** `10` (Fixed)
   * Delegate
   */
  async liquidstakingDelegateRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingDelegateRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Initiate the delegation of staked assets to a chosen operator contract. By delegating your tokens, you can potentially enhance yield or participate in network operations, with configurable gas parameters to suit current network conditions.  > 💡  **Compute Unit Value:** `10` (Fixed)
   * Delegate
   */
  async liquidstakingDelegate(requestParameters, initOverrides) {
    const response = await this.liquidstakingDelegateRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingGetallowance without sending the request
   */
  async liquidstakingGetallowanceRequestOpts(requestParameters) {
    if (requestParameters["owner"] == null) {
      throw new RequiredError(
        "owner",
        'Required parameter "owner" was null or undefined when calling liquidstakingGetallowance().'
      );
    }
    if (requestParameters["tokenAddress"] == null) {
      throw new RequiredError(
        "tokenAddress",
        'Required parameter "tokenAddress" was null or undefined when calling liquidstakingGetallowance().'
      );
    }
    if (requestParameters["spender"] == null) {
      throw new RequiredError(
        "spender",
        'Required parameter "spender" was null or undefined when calling liquidstakingGetallowance().'
      );
    }
    const queryParameters = {};
    if (requestParameters["liquidStakingId"] != null) {
      queryParameters["liquidStakingId"] = requestParameters["liquidStakingId"];
    }
    if (requestParameters["owner"] != null) {
      queryParameters["owner"] = requestParameters["owner"];
    }
    if (requestParameters["tokenAddress"] != null) {
      queryParameters["tokenAddress"] = requestParameters["tokenAddress"];
    }
    if (requestParameters["spender"] != null) {
      queryParameters["spender"] = requestParameters["spender"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/getallowance`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieve the current token allowance that a designated spender is authorized to use on behalf of the token owner. Use this endpoint to verify approved limits before executing any subsequent spending or transfer operations.  > 💡  **Compute Unit Value:** `35` (Fixed)
   * Get Allowance
   */
  async liquidstakingGetallowanceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingGetallowanceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieve the current token allowance that a designated spender is authorized to use on behalf of the token owner. Use this endpoint to verify approved limits before executing any subsequent spending or transfer operations.  > 💡  **Compute Unit Value:** `35` (Fixed)
   * Get Allowance
   */
  async liquidstakingGetallowance(requestParameters, initOverrides) {
    const response = await this.liquidstakingGetallowanceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingGetapr without sending the request
   */
  async liquidstakingGetaprRequestOpts(requestParameters) {
    const queryParameters = {};
    if (requestParameters["liquidStakingId"] != null) {
      queryParameters["liquidStakingId"] = requestParameters["liquidStakingId"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/getapr`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Fetch the latest Annual Percentage Rate (APR) for staking rewards across various liquid staking protocols (such as Lido, Stader, Chorus One, EigenLayer, Benqi, and Jito) on different networks. This provides a quick snapshot of current yield rates to help inform staking decisions.  > 💡  **Compute Unit Value:** `45` (Fixed)
   * Get APR
   */
  async liquidstakingGetaprRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingGetaprRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Fetch the latest Annual Percentage Rate (APR) for staking rewards across various liquid staking protocols (such as Lido, Stader, Chorus One, EigenLayer, Benqi, and Jito) on different networks. This provides a quick snapshot of current yield rates to help inform staking decisions.  > 💡  **Compute Unit Value:** `45` (Fixed)
   * Get APR
   */
  async liquidstakingGetapr(requestParameters = {}, initOverrides) {
    const response = await this.liquidstakingGetaprRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingGetprotocolapr without sending the request
   */
  async liquidstakingGetprotocolaprRequestOpts(requestParameters) {
    const queryParameters = {};
    if (requestParameters["liquidStakingId"] != null) {
      queryParameters["liquidStakingId"] = requestParameters["liquidStakingId"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/getprotocolapr`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Obtain historical APR data for Lido on Ethereum over the past seven days. The response includes timestamped APR values along with associated block numbers, as well as a simple moving average (SMA) APR, allowing for trend analysis over time.  > 💡  **Compute Unit Value:** `5` (Fixed)
   * Get Protocol APR
   */
  async liquidstakingGetprotocolaprRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingGetprotocolaprRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Obtain historical APR data for Lido on Ethereum over the past seven days. The response includes timestamped APR values along with associated block numbers, as well as a simple moving average (SMA) APR, allowing for trend analysis over time.  > 💡  **Compute Unit Value:** `5` (Fixed)
   * Get Protocol APR
   */
  async liquidstakingGetprotocolapr(requestParameters = {}, initOverrides) {
    const response = await this.liquidstakingGetprotocolaprRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingGetrewards without sending the request
   */
  async liquidstakingGetrewardsRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling liquidstakingGetrewards().'
      );
    }
    const queryParameters = {};
    if (requestParameters["liquidStakingId"] != null) {
      queryParameters["liquidStakingId"] = requestParameters["liquidStakingId"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["page"] != null) {
      queryParameters["page"] = requestParameters["page"];
    }
    if (requestParameters["startBlock"] != null) {
      queryParameters["startBlock"] = requestParameters["startBlock"];
    }
    if (requestParameters["endBlock"] != null) {
      queryParameters["endBlock"] = requestParameters["endBlock"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/getrewards`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieve comprehensive staking metrics for a given user address. The response details historical reward events, cumulative reward totals (both in ETH and equivalent currency), average APR, and current stake-to-share ratios—offering a full picture of your staking performance and reward accrual.  > 💡  **Compute Unit Value:** `10` (Fixed)
   * Get Rewards
   */
  async liquidstakingGetrewardsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingGetrewardsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieve comprehensive staking metrics for a given user address. The response details historical reward events, cumulative reward totals (both in ETH and equivalent currency), average APR, and current stake-to-share ratios—offering a full picture of your staking performance and reward accrual.  > 💡  **Compute Unit Value:** `10` (Fixed)
   * Get Rewards
   */
  async liquidstakingGetrewards(requestParameters, initOverrides) {
    const response = await this.liquidstakingGetrewardsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingGetstake without sending the request
   */
  async liquidstakingGetstakeRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling liquidstakingGetstake().'
      );
    }
    const queryParameters = {};
    if (requestParameters["liquidStakingId"] != null) {
      queryParameters["liquidStakingId"] = requestParameters["liquidStakingId"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/getstake`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Query the current staked token balance for a specific address across supported liquid staking protocols. The returned data (e.g., stETH and wstETH amounts) allows users to monitor their staking positions and assess their exposure in real time.  > 💡  **Compute Unit Value:** `10` (Fixed)
   * Get Stake
   */
  async liquidstakingGetstakeRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingGetstakeRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Query the current staked token balance for a specific address across supported liquid staking protocols. The returned data (e.g., stETH and wstETH amounts) allows users to monitor their staking positions and assess their exposure in real time.  > 💡  **Compute Unit Value:** `10` (Fixed)
   * Get Stake
   */
  async liquidstakingGetstake(requestParameters, initOverrides) {
    const response = await this.liquidstakingGetstakeRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingGetwithdrawalrequests without sending the request
   */
  async liquidstakingGetwithdrawalrequestsRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling liquidstakingGetwithdrawalrequests().'
      );
    }
    const queryParameters = {};
    if (requestParameters["liquidStakingId"] != null) {
      queryParameters["liquidStakingId"] = requestParameters["liquidStakingId"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/getwithdrawalrequests`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Fetch a list of all withdrawal request identifiers (typically represented as NFT IDs) associated with a specific user address. This endpoint is useful for tracking the status of pending withdrawal requests and managing your unstaking operations.  > 💡  **Compute Unit Value:** `10` (Fixed)
   * Get Withdrawal Requests
   */
  async liquidstakingGetwithdrawalrequestsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingGetwithdrawalrequestsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Fetch a list of all withdrawal request identifiers (typically represented as NFT IDs) associated with a specific user address. This endpoint is useful for tracking the status of pending withdrawal requests and managing your unstaking operations.  > 💡  **Compute Unit Value:** `10` (Fixed)
   * Get Withdrawal Requests
   */
  async liquidstakingGetwithdrawalrequests(requestParameters, initOverrides) {
    const response = await this.liquidstakingGetwithdrawalrequestsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingGetwithdrawalstatus without sending the request
   */
  async liquidstakingGetwithdrawalstatusRequestOpts(requestParameters) {
    if (requestParameters["requestId"] == null) {
      throw new RequiredError(
        "requestId",
        'Required parameter "requestId" was null or undefined when calling liquidstakingGetwithdrawalstatus().'
      );
    }
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling liquidstakingGetwithdrawalstatus().'
      );
    }
    const queryParameters = {};
    if (requestParameters["liquidStakingId"] != null) {
      queryParameters["liquidStakingId"] = requestParameters["liquidStakingId"];
    }
    if (requestParameters["requestId"] != null) {
      queryParameters["requestId"] = requestParameters["requestId"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/getwithdrawalstatus`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Check the detailed status of a specific withdrawal request using its NFT token ID. The response provides key information such as the amounts of stETH and shares involved, the timestamp of the request, and flags indicating whether the request has been finalized or claimed.  > 💡  **Compute Unit Value:** `10` (Fixed)
   * Get Withdrawal Status
   */
  async liquidstakingGetwithdrawalstatusRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingGetwithdrawalstatusRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Check the detailed status of a specific withdrawal request using its NFT token ID. The response provides key information such as the amounts of stETH and shares involved, the timestamp of the request, and flags indicating whether the request has been finalized or claimed.  > 💡  **Compute Unit Value:** `10` (Fixed)
   * Get Withdrawal Status
   */
  async liquidstakingGetwithdrawalstatus(requestParameters, initOverrides) {
    const response = await this.liquidstakingGetwithdrawalstatusRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingIncreaseallowance without sending the request
   */
  async liquidstakingIncreaseallowanceRequestOpts(requestParameters) {
    if (requestParameters["liquidstakingIncreaseallowanceRequestPayload"] == null) {
      throw new RequiredError(
        "liquidstakingIncreaseallowanceRequestPayload",
        'Required parameter "liquidstakingIncreaseallowanceRequestPayload" was null or undefined when calling liquidstakingIncreaseallowance().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/increaseallowance`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["liquidstakingIncreaseallowanceRequestPayload"]
    };
  }
  /**
   * Increases the spending allowance for staked tokens with configurable gas parameters and amount specifications.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * Increase Allowance
   */
  async liquidstakingIncreaseallowanceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingIncreaseallowanceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Increases the spending allowance for staked tokens with configurable gas parameters and amount specifications.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * Increase Allowance
   */
  async liquidstakingIncreaseallowance(requestParameters, initOverrides) {
    const response = await this.liquidstakingIncreaseallowanceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingMint without sending the request
   */
  async liquidstakingMintRequestOpts(requestParameters) {
    if (requestParameters["liquidstakingMintRequestPayload"] == null) {
      throw new RequiredError(
        "liquidstakingMintRequestPayload",
        'Required parameter "liquidstakingMintRequestPayload" was null or undefined when calling liquidstakingMint().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/mint`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["liquidstakingMintRequestPayload"]
    };
  }
  /**
   * Initiate a transaction to mint new osETH tokens by specifying the desired amount. This operation creates osETH in accordance with the protocol\'s staking rules and enables participation in liquid staking, with customizable gas settings for efficient execution.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Mint
   */
  async liquidstakingMintRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingMintRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Initiate a transaction to mint new osETH tokens by specifying the desired amount. This operation creates osETH in accordance with the protocol\'s staking rules and enables participation in liquid staking, with customizable gas settings for efficient execution.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Mint
   */
  async liquidstakingMint(requestParameters, initOverrides) {
    const response = await this.liquidstakingMintRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingRequestwithdrawal without sending the request
   */
  async liquidstakingRequestwithdrawalRequestOpts(requestParameters) {
    if (requestParameters["liquidstakingRequestwithdrawalRequestPayload"] == null) {
      throw new RequiredError(
        "liquidstakingRequestwithdrawalRequestPayload",
        'Required parameter "liquidstakingRequestwithdrawalRequestPayload" was null or undefined when calling liquidstakingRequestwithdrawal().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/requestwithdrawal`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["liquidstakingRequestwithdrawalRequestPayload"]
    };
  }
  /**
   * Submit a request to withdraw a specified amount of staked tokens. This endpoint processes withdrawal requests by requiring parameters such as the withdrawal amount and owner address, and supports configurable gas settings to ensure a smooth and cost-effective transaction.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Request Withdrawal
   */
  async liquidstakingRequestwithdrawalRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingRequestwithdrawalRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Submit a request to withdraw a specified amount of staked tokens. This endpoint processes withdrawal requests by requiring parameters such as the withdrawal amount and owner address, and supports configurable gas settings to ensure a smooth and cost-effective transaction.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Request Withdrawal
   */
  async liquidstakingRequestwithdrawal(requestParameters, initOverrides) {
    const response = await this.liquidstakingRequestwithdrawalRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingRestake without sending the request
   */
  async liquidstakingRestakeRequestOpts(requestParameters) {
    if (requestParameters["liquidstakingRestakeRequestPayload"] == null) {
      throw new RequiredError(
        "liquidstakingRestakeRequestPayload",
        'Required parameter "liquidstakingRestakeRequestPayload" was null or undefined when calling liquidstakingRestake().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/restake`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["liquidstakingRestakeRequestPayload"]
    };
  }
  /**
   * Execute a restaking transaction—specifically on EigenLayer—that allows you to reinvest your liquid staking tokens. This endpoint supports token-specific parameters and configurable gas settings, helping you optimize the reinvestment process.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Restake
   */
  async liquidstakingRestakeRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingRestakeRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Execute a restaking transaction—specifically on EigenLayer—that allows you to reinvest your liquid staking tokens. This endpoint supports token-specific parameters and configurable gas settings, helping you optimize the reinvestment process.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Restake
   */
  async liquidstakingRestake(requestParameters, initOverrides) {
    const response = await this.liquidstakingRestakeRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingStake without sending the request
   */
  async liquidstakingStakeRequestOpts(requestParameters) {
    if (requestParameters["liquidstakingStakeRequestPayload"] == null) {
      throw new RequiredError(
        "liquidstakingStakeRequestPayload",
        'Required parameter "liquidstakingStakeRequestPayload" was null or undefined when calling liquidstakingStake().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/stake`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["liquidstakingStakeRequestPayload"]
    };
  }
  /**
   * Initiate a staking transaction to deposit tokens into a protocol such as Lido. By converting ETH into stETH, you can participate in liquid staking, with the ability to specify the staking amount and adjust gas parameters for efficient transaction processing.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Stake
   */
  async liquidstakingStakeRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingStakeRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Initiate a staking transaction to deposit tokens into a protocol such as Lido. By converting ETH into stETH, you can participate in liquid staking, with the ability to specify the staking amount and adjust gas parameters for efficient transaction processing.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Stake
   */
  async liquidstakingStake(requestParameters, initOverrides) {
    const response = await this.liquidstakingStakeRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingUndelegate without sending the request
   */
  async liquidstakingUndelegateRequestOpts(requestParameters) {
    if (requestParameters["liquidstakingUndelegateRequestPayload"] == null) {
      throw new RequiredError(
        "liquidstakingUndelegateRequestPayload",
        'Required parameter "liquidstakingUndelegateRequestPayload" was null or undefined when calling liquidstakingUndelegate().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/undelegate`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["liquidstakingUndelegateRequestPayload"]
    };
  }
  /**
   * Process the undelegation of staked assets from an operator contract, effectively reversing the delegation action. This endpoint enables you to reclaim your staked tokens with configurable gas settings to ensure a smooth transition.  > 💡  **Compute Unit Value:** `5` (Fixed)
   * Undelegate
   */
  async liquidstakingUndelegateRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingUndelegateRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Process the undelegation of staked assets from an operator contract, effectively reversing the delegation action. This endpoint enables you to reclaim your staked tokens with configurable gas settings to ensure a smooth transition.  > 💡  **Compute Unit Value:** `5` (Fixed)
   * Undelegate
   */
  async liquidstakingUndelegate(requestParameters, initOverrides) {
    const response = await this.liquidstakingUndelegateRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingUnwrap without sending the request
   */
  async liquidstakingUnwrapRequestOpts(requestParameters) {
    if (requestParameters["liquidstakingUnwrapRequestPayload"] == null) {
      throw new RequiredError(
        "liquidstakingUnwrapRequestPayload",
        'Required parameter "liquidstakingUnwrapRequestPayload" was null or undefined when calling liquidstakingUnwrap().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/unwrap`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["liquidstakingUnwrapRequestPayload"]
    };
  }
  /**
   * Convert wrapped stETH (wstETH) back into its underlying stETH tokens. Specify the amount to unwrap and adjust gas parameters as needed to optimize the conversion process within the liquid staking protocol.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Unwrap
   */
  async liquidstakingUnwrapRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingUnwrapRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Convert wrapped stETH (wstETH) back into its underlying stETH tokens. Specify the amount to unwrap and adjust gas parameters as needed to optimize the conversion process within the liquid staking protocol.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Unwrap
   */
  async liquidstakingUnwrap(requestParameters, initOverrides) {
    const response = await this.liquidstakingUnwrapRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for liquidstakingWrap without sending the request
   */
  async liquidstakingWrapRequestOpts(requestParameters) {
    if (requestParameters["liquidstakingWrapRequestPayload"] == null) {
      throw new RequiredError(
        "liquidstakingWrapRequestPayload",
        'Required parameter "liquidstakingWrapRequestPayload" was null or undefined when calling liquidstakingWrap().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/liquidstaking/wrap`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["liquidstakingWrapRequestPayload"]
    };
  }
  /**
   * Initiate the conversion of stETH tokens into their wrapped version (wstETH). This wrapping process enables enhanced flexibility or additional functionality within the protocol, and supports customizable token amounts and gas settings to ensure efficient processing.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Wrap
   */
  async liquidstakingWrapRaw(requestParameters, initOverrides) {
    const requestOptions = await this.liquidstakingWrapRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Initiate the conversion of stETH tokens into their wrapped version (wstETH). This wrapping process enables enhanced flexibility or additional functionality within the protocol, and supports customizable token amounts and gas settings to ensure efficient processing.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Wrap
   */
  async liquidstakingWrap(requestParameters, initOverrides) {
    const response = await this.liquidstakingWrapRaw(requestParameters, initOverrides);
    return await response.value();
  }
};
var LiquidstakingGetallowanceLiquidStakingIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _2: "2",
  _56: "56",
  _97: "97",
  _137: "137",
  _43114: "43114"
};
var LiquidstakingGetaprLiquidStakingIdEnum = {
  _1: "1",
  _200: "200",
  _201: "201",
  _2: "2",
  _56: "56",
  _137: "137",
  _43114: "43114",
  _900: "900"
};
var LiquidstakingGetprotocolaprLiquidStakingIdEnum = {
  _1: "1"
};
var LiquidstakingGetrewardsLiquidStakingIdEnum = {
  _1: "1",
  _200: "200",
  _201: "201",
  _2: "2",
  _56: "56",
  _97: "97",
  _137: "137",
  _43114: "43114",
  _900: "900"
};
var LiquidstakingGetstakeLiquidStakingIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _200: "200",
  _201: "201",
  _2: "2",
  _56: "56",
  _97: "97",
  _137: "137",
  _3: "3",
  _17000: "17000",
  _43114: "43114",
  _900: "900"
};
var LiquidstakingGetwithdrawalrequestsLiquidStakingIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _200: "200",
  _201: "201",
  _2: "2",
  _56: "56",
  _97: "97",
  _137: "137",
  _43114: "43114"
};
var LiquidstakingGetwithdrawalstatusLiquidStakingIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _2: "2",
  _56: "56",
  _97: "97",
  _137: "137",
  _43114: "43114"
};

// src/apis/NftApi.ts
var NftApi = class extends BaseAPI {
  /**
   * Creates request options for nftGetbalance without sending the request
   */
  async nftGetbalanceRequestOpts(requestParameters) {
    if (requestParameters["nftCollection"] == null) {
      throw new RequiredError(
        "nftCollection",
        'Required parameter "nftCollection" was null or undefined when calling nftGetbalance().'
      );
    }
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling nftGetbalance().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["nftCollection"] != null) {
      queryParameters["nftCollection"] = requestParameters["nftCollection"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["nftProtocolId"] != null) {
      queryParameters["nftProtocolId"] = requestParameters["nftProtocolId"];
    }
    if (requestParameters["tokenId"] != null) {
      queryParameters["tokenId"] = requestParameters["tokenId"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/nft/getbalance`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves the quantity of NFTs held by a specific wallet address for a given collection across multiple blockchain networks. This endpoint allows users and developers to quickly determine the number of NFTs an address owns within a particular collection, facilitating portfolio tracking, auditing of NFT holdings, and interactions with NFT marketplaces or decentralized applications.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Get Balance
   */
  async nftGetbalanceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.nftGetbalanceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves the quantity of NFTs held by a specific wallet address for a given collection across multiple blockchain networks. This endpoint allows users and developers to quickly determine the number of NFTs an address owns within a particular collection, facilitating portfolio tracking, auditing of NFT holdings, and interactions with NFT marketplaces or decentralized applications.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Get Balance
   */
  async nftGetbalance(requestParameters, initOverrides) {
    const response = await this.nftGetbalanceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for nftGetmetadata without sending the request
   */
  async nftGetmetadataRequestOpts(requestParameters) {
    if (requestParameters["nftCollection"] == null) {
      throw new RequiredError(
        "nftCollection",
        'Required parameter "nftCollection" was null or undefined when calling nftGetmetadata().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["nftCollection"] != null) {
      queryParameters["nftCollection"] = requestParameters["nftCollection"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/nft/getmetadata`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Fetches comprehensive metadata for a specified NFT collection, including key details such as the collection’s name, symbol, attributes, images, and additional descriptive information. This endpoint is essential for rendering rich NFT displays, powering detailed collection analytics, and enabling seamless integration with NFT marketplaces across supported blockchain networks.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Get Metadata
   */
  async nftGetmetadataRaw(requestParameters, initOverrides) {
    const requestOptions = await this.nftGetmetadataRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Fetches comprehensive metadata for a specified NFT collection, including key details such as the collection’s name, symbol, attributes, images, and additional descriptive information. This endpoint is essential for rendering rich NFT displays, powering detailed collection analytics, and enabling seamless integration with NFT marketplaces across supported blockchain networks.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Get Metadata
   */
  async nftGetmetadata(requestParameters, initOverrides) {
    const response = await this.nftGetmetadataRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for nftGetowner without sending the request
   */
  async nftGetownerRequestOpts(requestParameters) {
    if (requestParameters["nftCollection"] == null) {
      throw new RequiredError(
        "nftCollection",
        'Required parameter "nftCollection" was null or undefined when calling nftGetowner().'
      );
    }
    if (requestParameters["nftIndex"] == null) {
      throw new RequiredError(
        "nftIndex",
        'Required parameter "nftIndex" was null or undefined when calling nftGetowner().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["nftCollection"] != null) {
      queryParameters["nftCollection"] = requestParameters["nftCollection"];
    }
    if (requestParameters["nftProtocolId"] != null) {
      queryParameters["nftProtocolId"] = requestParameters["nftProtocolId"];
    }
    if (requestParameters["nftIndex"] != null) {
      queryParameters["nftIndex"] = requestParameters["nftIndex"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/nft/getowner`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Identifies and retrieves the current owner of a specific NFT within a collection on supported blockchain networks. By providing the necessary identifiers or parameters, this endpoint returns the owner’s public address, thereby enabling verification of ownership, tracking of NFT transfers, and supporting secondary market transactions.  > 💡  **Compute Unit Value:** `85` (Fixed)
   * Get Owner
   */
  async nftGetownerRaw(requestParameters, initOverrides) {
    const requestOptions = await this.nftGetownerRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Identifies and retrieves the current owner of a specific NFT within a collection on supported blockchain networks. By providing the necessary identifiers or parameters, this endpoint returns the owner’s public address, thereby enabling verification of ownership, tracking of NFT transfers, and supporting secondary market transactions.  > 💡  **Compute Unit Value:** `85` (Fixed)
   * Get Owner
   */
  async nftGetowner(requestParameters, initOverrides) {
    const response = await this.nftGetownerRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for nftHistoricallogs without sending the request
   */
  async nftHistoricallogsRequestOpts(requestParameters) {
    if (requestParameters["nftCollection"] == null) {
      throw new RequiredError(
        "nftCollection",
        'Required parameter "nftCollection" was null or undefined when calling nftHistoricallogs().'
      );
    }
    if (requestParameters["type"] == null) {
      throw new RequiredError(
        "type",
        'Required parameter "type" was null or undefined when calling nftHistoricallogs().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["nftCollection"] != null) {
      queryParameters["nftCollection"] = requestParameters["nftCollection"];
    }
    if (requestParameters["nftProtocolId"] != null) {
      queryParameters["nftProtocolId"] = requestParameters["nftProtocolId"];
    }
    if (requestParameters["startBlock"] != null) {
      queryParameters["startBlock"] = requestParameters["startBlock"];
    }
    if (requestParameters["page"] != null) {
      queryParameters["page"] = requestParameters["page"];
    }
    if (requestParameters["endBlock"] != null) {
      queryParameters["endBlock"] = requestParameters["endBlock"];
    }
    if (requestParameters["type"] != null) {
      queryParameters["type"] = requestParameters["type"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/nft/historicallogs`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Extracts detailed historical event logs for NFT collections within a specified block range on Ethereum. Designed to support different NFT standards (such as ERC721 and ERC1155), the endpoint returns granular log data—including event topics, parameters (like from, to, and tokenId), block details, and transaction identifiers—allowing developers and auditors to analyze the full history of NFT-related events, track transfers, and perform forensic or compliance analyses.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Historical Logs
   */
  async nftHistoricallogsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.nftHistoricallogsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Extracts detailed historical event logs for NFT collections within a specified block range on Ethereum. Designed to support different NFT standards (such as ERC721 and ERC1155), the endpoint returns granular log data—including event topics, parameters (like from, to, and tokenId), block details, and transaction identifiers—allowing developers and auditors to analyze the full history of NFT-related events, track transfers, and perform forensic or compliance analyses.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Historical Logs
   */
  async nftHistoricallogs(requestParameters, initOverrides) {
    const response = await this.nftHistoricallogsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for nftHistoricaltransactions without sending the request
   */
  async nftHistoricaltransactionsRequestOpts(requestParameters) {
    if (requestParameters["nftCollection"] == null) {
      throw new RequiredError(
        "nftCollection",
        'Required parameter "nftCollection" was null or undefined when calling nftHistoricaltransactions().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["nftCollection"] != null) {
      queryParameters["nftCollection"] = requestParameters["nftCollection"];
    }
    if (requestParameters["nftProtocolId"] != null) {
      queryParameters["nftProtocolId"] = requestParameters["nftProtocolId"];
    }
    if (requestParameters["startBlock"] != null) {
      queryParameters["startBlock"] = requestParameters["startBlock"];
    }
    if (requestParameters["page"] != null) {
      queryParameters["page"] = requestParameters["page"];
    }
    if (requestParameters["endBlock"] != null) {
      queryParameters["endBlock"] = requestParameters["endBlock"];
    }
    if (requestParameters["sort"] != null) {
      queryParameters["sort"] = requestParameters["sort"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/nft/historicaltransactions`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a chronological list of historical NFT transactions for a specified collection on Ethereum, with customizable block ranges, pagination, and sorting. The response includes comprehensive details (block numbers, timestamps, transaction hashes, addresses, token IDs, gas metrics) to facilitate NFT auditing, market analysis, and dashboard integrations.  > 💡  **Compute Unit Value:** `90` (Fixed)
   * Historical Transactions
   */
  async nftHistoricaltransactionsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.nftHistoricaltransactionsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a chronological list of historical NFT transactions for a specified collection on Ethereum, with customizable block ranges, pagination, and sorting. The response includes comprehensive details (block numbers, timestamps, transaction hashes, addresses, token IDs, gas metrics) to facilitate NFT auditing, market analysis, and dashboard integrations.  > 💡  **Compute Unit Value:** `90` (Fixed)
   * Historical Transactions
   */
  async nftHistoricaltransactions(requestParameters, initOverrides) {
    const response = await this.nftHistoricaltransactionsRaw(requestParameters, initOverrides);
    return await response.value();
  }
};
var NftGetbalanceChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _137: "137",
  _250: "250",
  _10: "10",
  _11155420: "11155420",
  _42161: "42161",
  _421614: "421614",
  _25: "25",
  _338: "338",
  _84532: "84532",
  _8453: "8453",
  _43113: "43113",
  _97: "97",
  _80002: "80002",
  _300: "300",
  _324: "324",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636",
  _900: "900",
  _901: "901"
};
var NftGetmetadataChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _137: "137",
  _250: "250",
  _10: "10",
  _11155420: "11155420",
  _42161: "42161",
  _421614: "421614",
  _25: "25",
  _338: "338",
  _84532: "84532",
  _8453: "8453",
  _43113: "43113",
  _97: "97",
  _80002: "80002",
  _300: "300",
  _324: "324",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636",
  _900: "900",
  _901: "901"
};
var NftGetownerChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _137: "137",
  _900: "900",
  _901: "901",
  _250: "250",
  _10: "10",
  _11155420: "11155420",
  _42161: "42161",
  _421614: "421614",
  _25: "25",
  _338: "338",
  _84532: "84532",
  _8453: "8453",
  _43113: "43113",
  _97: "97",
  _80002: "80002",
  _300: "300",
  _324: "324",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636"
};
var NftHistoricallogsTypeEnum = {
  Approval: "Approval",
  Transfer: "Transfer"
};
var NftHistoricallogsChainIdEnum = {
  _1: "1"
};
var NftHistoricaltransactionsChainIdEnum = {
  _1: "1"
};
var NftHistoricaltransactionsSortEnum = {
  Desc: "desc",
  Asc: "asc"
};

// src/apis/OracleApi.ts
var OracleApi = class extends BaseAPI {
  /**
   * Creates request options for oracleGetprice without sending the request
   */
  async oracleGetpriceRequestOpts(requestParameters) {
    if (requestParameters["asset"] == null) {
      throw new RequiredError(
        "asset",
        'Required parameter "asset" was null or undefined when calling oracleGetprice().'
      );
    }
    const queryParameters = {};
    if (requestParameters["asset"] != null) {
      queryParameters["asset"] = requestParameters["asset"];
    }
    if (requestParameters["oracleId"] != null) {
      queryParameters["oracleId"] = requestParameters["oracleId"];
    }
    if (requestParameters["oracleName"] != null) {
      queryParameters["oracleName"] = requestParameters["oracleName"];
    }
    if (requestParameters["timestamp"] != null) {
      queryParameters["timestamp"] = requestParameters["timestamp"];
    }
    if (requestParameters["hours"] != null) {
      queryParameters["hours"] = requestParameters["hours"];
    }
    if (requestParameters["days"] != null) {
      queryParameters["days"] = requestParameters["days"];
    }
    if (requestParameters["weeks"] != null) {
      queryParameters["weeks"] = requestParameters["weeks"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/oracle/getprice`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Fetch the price of a specified token in USDT from supported oracle protocols on both EVM and non-EVM chains. This endpoint allows you to retrieve real-time or historical pricing data for a token by specifying an oracle or querying all available oracles.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Get Price
   */
  async oracleGetpriceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.oracleGetpriceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Fetch the price of a specified token in USDT from supported oracle protocols on both EVM and non-EVM chains. This endpoint allows you to retrieve real-time or historical pricing data for a token by specifying an oracle or querying all available oracles.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Get Price
   */
  async oracleGetprice(requestParameters, initOverrides) {
    const response = await this.oracleGetpriceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for oracleGetsupportedtokens without sending the request
   */
  async oracleGetsupportedtokensRequestOpts(requestParameters) {
    const queryParameters = {};
    if (requestParameters["oracleId"] != null) {
      queryParameters["oracleId"] = requestParameters["oracleId"];
    }
    if (requestParameters["oracleName"] != null) {
      queryParameters["oracleName"] = requestParameters["oracleName"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/oracle/getsupportedtokens`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * This endpoint retrieves a comprehensive list of tokens available for price feeds and data verification. It returns a mapping of token symbols to their contract addresses, aggregating data from multiple oracle providers so you can easily integrate accurate, up-to-date pricing into your applications.  > 💡  **Compute Unit Value:** `5` (Fixed)
   * Get Supported Tokens
   */
  async oracleGetsupportedtokensRaw(requestParameters, initOverrides) {
    const requestOptions = await this.oracleGetsupportedtokensRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * This endpoint retrieves a comprehensive list of tokens available for price feeds and data verification. It returns a mapping of token symbols to their contract addresses, aggregating data from multiple oracle providers so you can easily integrate accurate, up-to-date pricing into your applications.  > 💡  **Compute Unit Value:** `5` (Fixed)
   * Get Supported Tokens
   */
  async oracleGetsupportedtokens(requestParameters = {}, initOverrides) {
    const response = await this.oracleGetsupportedtokensRaw(requestParameters, initOverrides);
    return await response.value();
  }
};
var OracleGetpriceOracleIdEnum = {
  _1000: "1000",
  _1001: "1001",
  _1002: "1002",
  _1003: "1003",
  _1004: "1004",
  _1100: "1100",
  _1200: "1200",
  _1300: "1300",
  _1301: "1301",
  _1302: "1302",
  _1303: "1303"
};
var OracleGetsupportedtokensOracleIdEnum = {
  _1000: "1000",
  _1001: "1001",
  _1002: "1002",
  _1003: "1003",
  _1004: "1004",
  _1100: "1100",
  _1200: "1200",
  _1300: "1300",
  _1301: "1301",
  _1302: "1302",
  _1303: "1303"
};

// src/apis/RwaApi.ts
var RwaApi = class extends BaseAPI {
  /**
   * Creates request options for rwaBurn without sending the request
   */
  async rwaBurnRequestOpts(requestParameters) {
    if (requestParameters["rwaBurnRequestPayload"] == null) {
      throw new RequiredError(
        "rwaBurnRequestPayload",
        'Required parameter "rwaBurnRequestPayload" was null or undefined when calling rwaBurn().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/rwa/burn`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["rwaBurnRequestPayload"]
    };
  }
  /**
   * Executes a burn operation to permanently remove a specified quantity of custom assets from circulation. It supports multiple networks—including Stellar (and its testnet) as well as XRPLedger (and its testnet)—and requires key parameters such as the initiating wallet address, the amount to burn, the issuer\'s address, and the asset code. This irreversible operation is typically used to manage token supply or implement deflationary measures for custom assets.  > 💡  **Compute Unit Value:** `50` (Fixed)
   * Burn
   */
  async rwaBurnRaw(requestParameters, initOverrides) {
    const requestOptions = await this.rwaBurnRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Executes a burn operation to permanently remove a specified quantity of custom assets from circulation. It supports multiple networks—including Stellar (and its testnet) as well as XRPLedger (and its testnet)—and requires key parameters such as the initiating wallet address, the amount to burn, the issuer\'s address, and the asset code. This irreversible operation is typically used to manage token supply or implement deflationary measures for custom assets.  > 💡  **Compute Unit Value:** `50` (Fixed)
   * Burn
   */
  async rwaBurn(requestParameters, initOverrides) {
    const response = await this.rwaBurnRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for rwaFreeze without sending the request
   */
  async rwaFreezeRequestOpts(requestParameters) {
    if (requestParameters["rwaFreezeRequestPayload"] == null) {
      throw new RequiredError(
        "rwaFreezeRequestPayload",
        'Required parameter "rwaFreezeRequestPayload" was null or undefined when calling rwaFreeze().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/rwa/freeze`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["rwaFreezeRequestPayload"]
    };
  }
  /**
   * Revokes authorization for an account\'s trustline, effectively freezing their ability to transact with the specified asset.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Freeze
   */
  async rwaFreezeRaw(requestParameters, initOverrides) {
    const requestOptions = await this.rwaFreezeRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Revokes authorization for an account\'s trustline, effectively freezing their ability to transact with the specified asset.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Freeze
   */
  async rwaFreeze(requestParameters, initOverrides) {
    const response = await this.rwaFreezeRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for rwaGetallassets without sending the request
   */
  async rwaGetallassetsRequestOpts(requestParameters) {
    if (requestParameters["from"] == null) {
      throw new RequiredError(
        "from",
        'Required parameter "from" was null or undefined when calling rwaGetallassets().'
      );
    }
    if (requestParameters["contract"] == null) {
      throw new RequiredError(
        "contract",
        'Required parameter "contract" was null or undefined when calling rwaGetallassets().'
      );
    }
    if (requestParameters["assetCode"] == null) {
      throw new RequiredError(
        "assetCode",
        'Required parameter "assetCode" was null or undefined when calling rwaGetallassets().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["from"] != null) {
      queryParameters["from"] = requestParameters["from"];
    }
    if (requestParameters["contract"] != null) {
      queryParameters["contract"] = requestParameters["contract"];
    }
    if (requestParameters["assetCode"] != null) {
      queryParameters["assetCode"] = requestParameters["assetCode"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/rwa/getallassets`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a comprehensive list of all tokenized assets associated with a specific smart contract on the blockchain. It generates an XDR hash, which is processed through the Blockdaemon SDK to fetch and return details of all assets registered under the contract.  > 💡  **Compute Unit Value:** `55` (Fixed)
   * Get All Assets
   */
  async rwaGetallassetsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.rwaGetallassetsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a comprehensive list of all tokenized assets associated with a specific smart contract on the blockchain. It generates an XDR hash, which is processed through the Blockdaemon SDK to fetch and return details of all assets registered under the contract.  > 💡  **Compute Unit Value:** `55` (Fixed)
   * Get All Assets
   */
  async rwaGetallassets(requestParameters, initOverrides) {
    const response = await this.rwaGetallassetsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for rwaGetasset without sending the request
   */
  async rwaGetassetRequestOpts(requestParameters) {
    if (requestParameters["from"] == null) {
      throw new RequiredError(
        "from",
        'Required parameter "from" was null or undefined when calling rwaGetasset().'
      );
    }
    if (requestParameters["contract"] == null) {
      throw new RequiredError(
        "contract",
        'Required parameter "contract" was null or undefined when calling rwaGetasset().'
      );
    }
    if (requestParameters["assetCode"] == null) {
      throw new RequiredError(
        "assetCode",
        'Required parameter "assetCode" was null or undefined when calling rwaGetasset().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["from"] != null) {
      queryParameters["from"] = requestParameters["from"];
    }
    if (requestParameters["contract"] != null) {
      queryParameters["contract"] = requestParameters["contract"];
    }
    if (requestParameters["assetCode"] != null) {
      queryParameters["assetCode"] = requestParameters["assetCode"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/rwa/getasset`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves information about a specific tokenized asset on the blockchain. It generates an XDR hash, which is processed through the Blockdaemon SDK to obtain the full asset details, including its metadata, ownership structure, and contract-level attributes.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Get Asset
   */
  async rwaGetassetRaw(requestParameters, initOverrides) {
    const requestOptions = await this.rwaGetassetRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves information about a specific tokenized asset on the blockchain. It generates an XDR hash, which is processed through the Blockdaemon SDK to obtain the full asset details, including its metadata, ownership structure, and contract-level attributes.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Get Asset
   */
  async rwaGetasset(requestParameters, initOverrides) {
    const response = await this.rwaGetassetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for rwaGetownerassets without sending the request
   */
  async rwaGetownerassetsRequestOpts(requestParameters) {
    if (requestParameters["from"] == null) {
      throw new RequiredError(
        "from",
        'Required parameter "from" was null or undefined when calling rwaGetownerassets().'
      );
    }
    if (requestParameters["contract"] == null) {
      throw new RequiredError(
        "contract",
        'Required parameter "contract" was null or undefined when calling rwaGetownerassets().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["from"] != null) {
      queryParameters["from"] = requestParameters["from"];
    }
    if (requestParameters["contract"] != null) {
      queryParameters["contract"] = requestParameters["contract"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/rwa/getownerassets`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves tokenized assets owned by a specified wallet address on the blockchain by generating an XDR hash, which the Blockdaemon SDK processes to obtain complete ownership details. This endpoint benefits asset verification platforms, wallet services, and on-chain portfolio management tools, allowing users to track and manage real-world assets.  > 💡  **Compute Unit Value:** `35` (Fixed)
   * Get Owner Assets
   */
  async rwaGetownerassetsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.rwaGetownerassetsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves tokenized assets owned by a specified wallet address on the blockchain by generating an XDR hash, which the Blockdaemon SDK processes to obtain complete ownership details. This endpoint benefits asset verification platforms, wallet services, and on-chain portfolio management tools, allowing users to track and manage real-world assets.  > 💡  **Compute Unit Value:** `35` (Fixed)
   * Get Owner Assets
   */
  async rwaGetownerassets(requestParameters, initOverrides) {
    const response = await this.rwaGetownerassetsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for rwaIssue without sending the request
   */
  async rwaIssueRequestOpts(requestParameters) {
    if (requestParameters["rwaIssueRequestPayload"] == null) {
      throw new RequiredError(
        "rwaIssueRequestPayload",
        'Required parameter "rwaIssueRequestPayload" was null or undefined when calling rwaIssue().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/rwa/issue`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["rwaIssueRequestPayload"]
    };
  }
  /**
   * Creates or mints new assets on the Stellar network by executing a payment operation from issuer to distributor with specified asset codes and amounts.  > 💡  **Compute Unit Value:** `35` (Fixed)
   * Issue
   */
  async rwaIssueRaw(requestParameters, initOverrides) {
    const requestOptions = await this.rwaIssueRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Creates or mints new assets on the Stellar network by executing a payment operation from issuer to distributor with specified asset codes and amounts.  > 💡  **Compute Unit Value:** `35` (Fixed)
   * Issue
   */
  async rwaIssue(requestParameters, initOverrides) {
    const response = await this.rwaIssueRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for rwaRegister without sending the request
   */
  async rwaRegisterRequestOpts(requestParameters) {
    if (requestParameters["rwaRegisterRequestPayload"] == null) {
      throw new RequiredError(
        "rwaRegisterRequestPayload",
        'Required parameter "rwaRegisterRequestPayload" was null or undefined when calling rwaRegister().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/rwa/register`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["rwaRegisterRequestPayload"]
    };
  }
  /**
   * Facilitates the creation and registration of an RWA as a token on the blockchain by interacting with smart contracts at the chain level to deploy and register the asset, providing transaction details (chain ID, sender address, gas) for seamless tokenization and ecosystem management.  > 💡  **Compute Unit Value:** `70` (Fixed)
   * Register
   */
  async rwaRegisterRaw(requestParameters, initOverrides) {
    const requestOptions = await this.rwaRegisterRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Facilitates the creation and registration of an RWA as a token on the blockchain by interacting with smart contracts at the chain level to deploy and register the asset, providing transaction details (chain ID, sender address, gas) for seamless tokenization and ecosystem management.  > 💡  **Compute Unit Value:** `70` (Fixed)
   * Register
   */
  async rwaRegister(requestParameters, initOverrides) {
    const response = await this.rwaRegisterRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for rwaSettrustline without sending the request
   */
  async rwaSettrustlineRequestOpts(requestParameters) {
    if (requestParameters["rwaSettrustlineRequestPayload"] == null) {
      throw new RequiredError(
        "rwaSettrustlineRequestPayload",
        'Required parameter "rwaSettrustlineRequestPayload" was null or undefined when calling rwaSettrustline().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/rwa/settrustline`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["rwaSettrustlineRequestPayload"]
    };
  }
  /**
   * Establishes a secure trustline relationship between an issuer and distributor with specified asset limits and authorization parameters on the Stellar network.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Set Trustline
   */
  async rwaSettrustlineRaw(requestParameters, initOverrides) {
    const requestOptions = await this.rwaSettrustlineRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Establishes a secure trustline relationship between an issuer and distributor with specified asset limits and authorization parameters on the Stellar network.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Set Trustline
   */
  async rwaSettrustline(requestParameters, initOverrides) {
    const response = await this.rwaSettrustlineRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for rwaTransfer without sending the request
   */
  async rwaTransferRequestOpts(requestParameters) {
    if (requestParameters["rwaTransferRequestPayload"] == null) {
      throw new RequiredError(
        "rwaTransferRequestPayload",
        'Required parameter "rwaTransferRequestPayload" was null or undefined when calling rwaTransfer().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/rwa/transfer`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["rwaTransferRequestPayload"]
    };
  }
  /**
   * Enables secure on-chain transfers of both native (XLM) and custom assets between Stellar accounts. It requires the sender’s and recipient’s public addresses, the asset code, and the transfer amount. The endpoint processes the transaction on the blockchain, ensuring authenticity and returning transaction details such as the chain ID and gas used.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Transfer
   */
  async rwaTransferRaw(requestParameters, initOverrides) {
    const requestOptions = await this.rwaTransferRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Enables secure on-chain transfers of both native (XLM) and custom assets between Stellar accounts. It requires the sender’s and recipient’s public addresses, the asset code, and the transfer amount. The endpoint processes the transaction on the blockchain, ensuring authenticity and returning transaction details such as the chain ID and gas used.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Transfer
   */
  async rwaTransfer(requestParameters, initOverrides) {
    const response = await this.rwaTransferRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for rwaUnfreeze without sending the request
   */
  async rwaUnfreezeRequestOpts(requestParameters) {
    if (requestParameters["rwaUnfreezeRequestPayload"] == null) {
      throw new RequiredError(
        "rwaUnfreezeRequestPayload",
        'Required parameter "rwaUnfreezeRequestPayload" was null or undefined when calling rwaUnfreeze().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/rwa/unfreeze`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["rwaUnfreezeRequestPayload"]
    };
  }
  /**
   * Restores the functionality of a previously frozen asset within the RWA framework. It reactivates an asset—typically frozen for regulatory, security, or administrative reasons—so that it can resume normal blockchain operations. A successful request returns key details such as the chain ID, gas used, and a reference ID for tracking the unfreeze operation.  > 💡  **Compute Unit Value:** `55` (Fixed)
   * Unfreeze
   */
  async rwaUnfreezeRaw(requestParameters, initOverrides) {
    const requestOptions = await this.rwaUnfreezeRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Restores the functionality of a previously frozen asset within the RWA framework. It reactivates an asset—typically frozen for regulatory, security, or administrative reasons—so that it can resume normal blockchain operations. A successful request returns key details such as the chain ID, gas used, and a reference ID for tracking the unfreeze operation.  > 💡  **Compute Unit Value:** `55` (Fixed)
   * Unfreeze
   */
  async rwaUnfreeze(requestParameters, initOverrides) {
    const response = await this.rwaUnfreezeRaw(requestParameters, initOverrides);
    return await response.value();
  }
};
var RwaGetallassetsChainIdEnum = {
  _1500: "1500",
  _1501: "1501"
};
var RwaGetassetChainIdEnum = {
  _1500: "1500",
  _1501: "1501"
};
var RwaGetownerassetsChainIdEnum = {
  _1500: "1500",
  _1501: "1501"
};

// src/apis/StablecoinApi.ts
var StablecoinApi = class extends BaseAPI {
  /**
   * Creates request options for stablecoinGetprice without sending the request
   */
  async stablecoinGetpriceRequestOpts(requestParameters) {
    if (requestParameters["asset"] == null) {
      throw new RequiredError(
        "asset",
        'Required parameter "asset" was null or undefined when calling stablecoinGetprice().'
      );
    }
    const queryParameters = {};
    if (requestParameters["asset"] != null) {
      queryParameters["asset"] = requestParameters["asset"];
    }
    if (requestParameters["stablecoinId"] != null) {
      queryParameters["stablecoinId"] = requestParameters["stablecoinId"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/stablecoin/getprice`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves the latest USDT-denominated market price for supported stablecoins (e.g., USDT, USDC), including round ID, price, and recorded timestamps.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Get Price
   */
  async stablecoinGetpriceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.stablecoinGetpriceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves the latest USDT-denominated market price for supported stablecoins (e.g., USDT, USDC), including round ID, price, and recorded timestamps.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Get Price
   */
  async stablecoinGetprice(requestParameters, initOverrides) {
    const response = await this.stablecoinGetpriceRaw(requestParameters, initOverrides);
    return await response.value();
  }
};

// src/apis/SupportApi.ts
var SupportApi = class extends BaseAPI {
  /**
   * Creates request options for getapimetadatav2 without sending the request
   */
  async getapimetadatav2RequestOpts(requestParameters) {
    if (requestParameters["category"] == null) {
      throw new RequiredError(
        "category",
        'Required parameter "category" was null or undefined when calling getapimetadatav2().'
      );
    }
    if (requestParameters["endpoint"] == null) {
      throw new RequiredError(
        "endpoint",
        'Required parameter "endpoint" was null or undefined when calling getapimetadatav2().'
      );
    }
    const queryParameters = {};
    if (requestParameters["category"] != null) {
      queryParameters["category"] = requestParameters["category"];
    }
    if (requestParameters["endpoint"] != null) {
      queryParameters["endpoint"] = requestParameters["endpoint"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Return the breakdown a breakdown of supported chains and protocol IDs for a given DeFi API endpoint.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Get API Metadata
   */
  async getapimetadatav2Raw(requestParameters, initOverrides) {
    const requestOptions = await this.getapimetadatav2RequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Return the breakdown a breakdown of supported chains and protocol IDs for a given DeFi API endpoint.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Get API Metadata
   */
  async getapimetadatav2(requestParameters, initOverrides) {
    const response = await this.getapimetadatav2Raw(requestParameters, initOverrides);
    return await response.value();
  }
};

// src/apis/SyntheticApi.ts
var SyntheticApi = class extends BaseAPI {
  /**
   * Creates request options for syntheticDepositpeggedtoken without sending the request
   */
  async syntheticDepositpeggedtokenRequestOpts(requestParameters) {
    if (requestParameters["syntheticDepositpeggedtokenRequestPayload"] == null) {
      throw new RequiredError(
        "syntheticDepositpeggedtokenRequestPayload",
        'Required parameter "syntheticDepositpeggedtokenRequestPayload" was null or undefined when calling syntheticDepositpeggedtoken().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/synthetic/depositpeggedtoken`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["syntheticDepositpeggedtokenRequestPayload"]
    };
  }
  /**
   * Enables users to deposit synthetic pegged tokens into the Synthetix protocol with customizable gas settings and transaction parameters. Upon a successful transaction, the endpoint returns key details including the chain ID, sender and recipient addresses, gas used, and the raw transaction data, ensuring a secure and efficient deposit process.  > 💡  **Compute Unit Value:** `130` (Fixed)
   * Deposit Pegged Token
   */
  async syntheticDepositpeggedtokenRaw(requestParameters, initOverrides) {
    const requestOptions = await this.syntheticDepositpeggedtokenRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Enables users to deposit synthetic pegged tokens into the Synthetix protocol with customizable gas settings and transaction parameters. Upon a successful transaction, the endpoint returns key details including the chain ID, sender and recipient addresses, gas used, and the raw transaction data, ensuring a secure and efficient deposit process.  > 💡  **Compute Unit Value:** `130` (Fixed)
   * Deposit Pegged Token
   */
  async syntheticDepositpeggedtoken(requestParameters, initOverrides) {
    const response = await this.syntheticDepositpeggedtokenRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for syntheticGetprice without sending the request
   */
  async syntheticGetpriceRequestOpts(requestParameters) {
    if (requestParameters["amount"] == null) {
      throw new RequiredError(
        "amount",
        'Required parameter "amount" was null or undefined when calling syntheticGetprice().'
      );
    }
    const queryParameters = {};
    if (requestParameters["syntheticId"] != null) {
      queryParameters["syntheticId"] = requestParameters["syntheticId"];
    }
    if (requestParameters["amount"] != null) {
      queryParameters["amount"] = requestParameters["amount"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/synthetic/getprice`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Calculates the exchange rate between synthetic (pegged) tokens and protocol tokens. It accepts a specified synthetic token amount and returns the equivalent number of protocol tokens based on current market conditions, providing accurate and reliable pricing data for Synthetix operations.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Get Price
   */
  async syntheticGetpriceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.syntheticGetpriceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Calculates the exchange rate between synthetic (pegged) tokens and protocol tokens. It accepts a specified synthetic token amount and returns the equivalent number of protocol tokens based on current market conditions, providing accurate and reliable pricing data for Synthetix operations.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Get Price
   */
  async syntheticGetprice(requestParameters, initOverrides) {
    const response = await this.syntheticGetpriceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for syntheticWithdrawpeggedtoken without sending the request
   */
  async syntheticWithdrawpeggedtokenRequestOpts(requestParameters) {
    if (requestParameters["syntheticWithdrawpeggedtokenRequestPayload"] == null) {
      throw new RequiredError(
        "syntheticWithdrawpeggedtokenRequestPayload",
        'Required parameter "syntheticWithdrawpeggedtokenRequestPayload" was null or undefined when calling syntheticWithdrawpeggedtoken().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/synthetic/withdrawpeggedtoken`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["syntheticWithdrawpeggedtokenRequestPayload"]
    };
  }
  /**
   * Processes the withdrawal of synthetic tokens from the Synthetix protocol. It executes a transaction that includes configurable gas priorities and built-in security validations, ensuring that synthetic token withdrawals are handled securely and efficiently on the blockchain.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Withdraw Pegged Token
   */
  async syntheticWithdrawpeggedtokenRaw(requestParameters, initOverrides) {
    const requestOptions = await this.syntheticWithdrawpeggedtokenRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Processes the withdrawal of synthetic tokens from the Synthetix protocol. It executes a transaction that includes configurable gas priorities and built-in security validations, ensuring that synthetic token withdrawals are handled securely and efficiently on the blockchain.  > 💡  **Compute Unit Value:** `100` (Fixed)
   * Withdraw Pegged Token
   */
  async syntheticWithdrawpeggedtoken(requestParameters, initOverrides) {
    const response = await this.syntheticWithdrawpeggedtokenRaw(requestParameters, initOverrides);
    return await response.value();
  }
};
var SyntheticGetpriceSyntheticIdEnum = {
  _6000: "6000"
};

// src/apis/TagsApi.ts
var TagsApi = class extends BaseAPI {
  /**
   * Creates request options for tokensV1TagsLegacy without sending the request
   */
  async tokensV1TagsLegacyRequestOpts() {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/tags`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a list of available tags that can be used to filter the tokens response.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * List Token Tags
   */
  async tokensV1TagsLegacyRaw(initOverrides) {
    const requestOptions = await this.tokensV1TagsLegacyRequestOpts();
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a list of available tags that can be used to filter the tokens response.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * List Token Tags
   */
  async tokensV1TagsLegacy(initOverrides) {
    const response = await this.tokensV1TagsLegacyRaw(initOverrides);
    return await response.value();
  }
};

// src/apis/TokensApi.ts
var TokensApi = class extends BaseAPI {
  /**
   * Creates request options for getTokenTags without sending the request
   */
  async getTokenTagsRequestOpts() {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/tokens/v1/tags`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * ![Beta](https://img.shields.io/badge/Status-Beta-yellow) <br/> Retrieves a list of available tags that can be used to filter the tokens response.  > 💡  **Compute Unit Value:** `60` (fixed)
   * List Token Tags
   */
  async getTokenTagsRaw(initOverrides) {
    const requestOptions = await this.getTokenTagsRequestOpts();
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * ![Beta](https://img.shields.io/badge/Status-Beta-yellow) <br/> Retrieves a list of available tags that can be used to filter the tokens response.  > 💡  **Compute Unit Value:** `60` (fixed)
   * List Token Tags
   */
  async getTokenTags(initOverrides) {
    const response = await this.getTokenTagsRaw(initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for getTokens without sending the request
   */
  async getTokensRequestOpts(requestParameters) {
    const queryParameters = {};
    if (requestParameters["chainID"] != null) {
      queryParameters["chainID"] = requestParameters["chainID"];
    }
    if (requestParameters["tokenAddress"] != null) {
      queryParameters["tokenAddress"] = requestParameters["tokenAddress"];
    }
    if (requestParameters["tokenSymbol"] != null) {
      queryParameters["tokenSymbol"] = requestParameters["tokenSymbol"];
    }
    if (requestParameters["tagLimit"] != null) {
      queryParameters["tagLimit"] = requestParameters["tagLimit"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/tokens/v1`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * ![Beta](https://img.shields.io/badge/Status-Beta-yellow) <br/> Retrieves a list of tokens along with their metadata, such as the token\'s name, symbol, decimals, logo URI, and associated tags. This information is useful for displaying token details and allowing users to search or filter tokens based on specific criteria. Tags can be used to narrow down the returned tokens. Example: Use the tag `stablecoins` to only return stablecoin tokens.  > 💡  **Compute Unit Value:** `60` (fixed)
   * List Supported Tokens with Metadata
   */
  async getTokensRaw(requestParameters, initOverrides) {
    const requestOptions = await this.getTokensRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * ![Beta](https://img.shields.io/badge/Status-Beta-yellow) <br/> Retrieves a list of tokens along with their metadata, such as the token\'s name, symbol, decimals, logo URI, and associated tags. This information is useful for displaying token details and allowing users to search or filter tokens based on specific criteria. Tags can be used to narrow down the returned tokens. Example: Use the tag `stablecoins` to only return stablecoin tokens.  > 💡  **Compute Unit Value:** `60` (fixed)
   * List Supported Tokens with Metadata
   */
  async getTokens(requestParameters = {}, initOverrides) {
    const response = await this.getTokensRaw(requestParameters, initOverrides);
    return await response.value();
  }
};

// src/apis/UtilityApi.ts
var UtilityApi = class extends BaseAPI {
  /**
   * Creates request options for getUrlsBd without sending the request
   */
  async getUrlsBdRequestOpts() {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/urls/bd.txt`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Returns a newline-separated plain-text list of all Blockdaemon DeFi API endpoint URLs for the production environment.
   * List Blockdaemon Production API URLs
   */
  async getUrlsBdRaw(initOverrides) {
    const requestOptions = await this.getUrlsBdRequestOpts();
    const response = await this.request(requestOptions, initOverrides);
    if (this.isJsonMime(response.headers.get("content-type"))) {
      return new JSONApiResponse(response);
    } else {
      return new TextApiResponse(response);
    }
  }
  /**
   * Returns a newline-separated plain-text list of all Blockdaemon DeFi API endpoint URLs for the production environment.
   * List Blockdaemon Production API URLs
   */
  async getUrlsBd(initOverrides) {
    const response = await this.getUrlsBdRaw(initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for getUrlsStgBd without sending the request
   */
  async getUrlsStgBdRequestOpts() {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/urls/stg-bd.txt`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Returns a newline-separated plain-text list of all Blockdaemon DeFi API endpoint URLs for the staging environment.
   * List Blockdaemon Staging API URLs
   */
  async getUrlsStgBdRaw(initOverrides) {
    const requestOptions = await this.getUrlsStgBdRequestOpts();
    const response = await this.request(requestOptions, initOverrides);
    if (this.isJsonMime(response.headers.get("content-type"))) {
      return new JSONApiResponse(response);
    } else {
      return new TextApiResponse(response);
    }
  }
  /**
   * Returns a newline-separated plain-text list of all Blockdaemon DeFi API endpoint URLs for the staging environment.
   * List Blockdaemon Staging API URLs
   */
  async getUrlsStgBd(initOverrides) {
    const response = await this.getUrlsStgBdRaw(initOverrides);
    return await response.value();
  }
};

// src/apis/V1Api.ts
var V1Api = class extends BaseAPI {
  /**
   * Creates request options for tokensV1Legacy without sending the request
   */
  async tokensV1LegacyRequestOpts(requestParameters) {
    const queryParameters = {};
    if (requestParameters["chainID"] != null) {
      queryParameters["chainID"] = requestParameters["chainID"];
    }
    if (requestParameters["tokenAddress"] != null) {
      queryParameters["tokenAddress"] = requestParameters["tokenAddress"];
    }
    if (requestParameters["tokenSymbol"] != null) {
      queryParameters["tokenSymbol"] = requestParameters["tokenSymbol"];
    }
    if (requestParameters["tagLimit"] != null) {
      queryParameters["tagLimit"] = requestParameters["tagLimit"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/v1`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a list of tokens along with their metadata, such as the token\'s name, symbol, decimals, logo URI, and associated tags. This information is useful for displaying token details and allowing users to search or filter tokens based on specific criteria. Tags can be used to narrow down the returned tokens. Example: Use the tag `stablecoins` to only return stablecoin tokens.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * List Supported Tokens with Metadata
   */
  async tokensV1LegacyRaw(requestParameters, initOverrides) {
    const requestOptions = await this.tokensV1LegacyRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a list of tokens along with their metadata, such as the token\'s name, symbol, decimals, logo URI, and associated tags. This information is useful for displaying token details and allowing users to search or filter tokens based on specific criteria. Tags can be used to narrow down the returned tokens. Example: Use the tag `stablecoins` to only return stablecoin tokens.  > 💡  **Compute Unit Value:** `60` (Fixed)
   * List Supported Tokens with Metadata
   */
  async tokensV1Legacy(requestParameters = {}, initOverrides) {
    const response = await this.tokensV1LegacyRaw(requestParameters, initOverrides);
    return await response.value();
  }
};

// src/apis/WethApi.ts
var WethApi = class extends BaseAPI {
  /**
   * Creates request options for wethGetbalance without sending the request
   */
  async wethGetbalanceRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling wethGetbalance().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["vsCurrencies"] != null) {
      queryParameters["vsCurrencies"] = requestParameters["vsCurrencies"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/weth/getbalance`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves the current WETH balance for a specified wallet address across multiple networks. It accepts optional parameters (RPC, chain ID, chain symbol) to target a specific network, making it ideal for portfolio tracking and wallet management.  > 💡  **Compute Unit Value:** `45` (Fixed)
   * Get Balance
   */
  async wethGetbalanceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.wethGetbalanceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves the current WETH balance for a specified wallet address across multiple networks. It accepts optional parameters (RPC, chain ID, chain symbol) to target a specific network, making it ideal for portfolio tracking and wallet management.  > 💡  **Compute Unit Value:** `45` (Fixed)
   * Get Balance
   */
  async wethGetbalance(requestParameters, initOverrides) {
    const response = await this.wethGetbalanceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for wethHistoricalLogs without sending the request
   */
  async wethHistoricalLogsRequestOpts(requestParameters) {
    if (requestParameters["type"] == null) {
      throw new RequiredError(
        "type",
        'Required parameter "type" was null or undefined when calling wethHistoricalLogs().'
      );
    }
    const queryParameters = {};
    if (requestParameters["chainId"] != null) {
      queryParameters["chainId"] = requestParameters["chainId"];
    }
    if (requestParameters["chainSymbol"] != null) {
      queryParameters["chainSymbol"] = requestParameters["chainSymbol"];
    }
    if (requestParameters["startBlock"] != null) {
      queryParameters["startBlock"] = requestParameters["startBlock"];
    }
    if (requestParameters["endBlock"] != null) {
      queryParameters["endBlock"] = requestParameters["endBlock"];
    }
    if (requestParameters["type"] != null) {
      queryParameters["type"] = requestParameters["type"];
    }
    if (requestParameters["page"] != null) {
      queryParameters["page"] = requestParameters["page"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/weth/historical/logs`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Returns a chronological history of WETH deposit and withdrawal events within a specified block range, including contract addresses, event topics, parameters, and block metadata. Users can filter by event type, chain ID, chain symbol, and block range, enabling robust auditing and analysis of historical token activity.  > 💡  **Compute Unit Value:** `70` (Fixed)
   * Historical Logs
   */
  async wethHistoricalLogsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.wethHistoricalLogsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Returns a chronological history of WETH deposit and withdrawal events within a specified block range, including contract addresses, event topics, parameters, and block metadata. Users can filter by event type, chain ID, chain symbol, and block range, enabling robust auditing and analysis of historical token activity.  > 💡  **Compute Unit Value:** `70` (Fixed)
   * Historical Logs
   */
  async wethHistoricalLogs(requestParameters, initOverrides) {
    const response = await this.wethHistoricalLogsRaw(requestParameters, initOverrides);
    return await response.value();
  }
};
var WethGetbalanceChainIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _56: "56",
  _97: "97",
  _43114: "43114",
  _43113: "43113",
  _137: "137",
  _80002: "80002",
  _25: "25",
  _338: "338",
  _42161: "42161",
  _250: "250",
  _4002: "4002",
  _10: "10",
  _11155420: "11155420",
  _8453: "8453",
  _84532: "84532",
  _324: "324",
  _300: "300"
};
var WethHistoricalLogsTypeEnum = {
  Approval: "Approval",
  Transfer: "Transfer"
};
var WethHistoricalLogsChainIdEnum = {
  _1: "1"
};

// src/apis/YieldaggregatorApi.ts
var YieldaggregatorApi = class extends BaseAPI {
  /**
   * Creates request options for yieldaggregatorAddliquidity without sending the request
   */
  async yieldaggregatorAddliquidityRequestOpts(requestParameters) {
    if (requestParameters["yieldaggregatorAddliquidityRequestPayload"] == null) {
      throw new RequiredError(
        "yieldaggregatorAddliquidityRequestPayload",
        'Required parameter "yieldaggregatorAddliquidityRequestPayload" was null or undefined when calling yieldaggregatorAddliquidity().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/yieldaggregator/addliquidity`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["yieldaggregatorAddliquidityRequestPayload"]
    };
  }
  /**
   * Constructs a transaction payload for adding liquidity to a specified market. The payload includes chain, address, gas, and function data, enabling users to deposit tokens and expand yield opportunities.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Add Liquidity
   */
  async yieldaggregatorAddliquidityRaw(requestParameters, initOverrides) {
    const requestOptions = await this.yieldaggregatorAddliquidityRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Constructs a transaction payload for adding liquidity to a specified market. The payload includes chain, address, gas, and function data, enabling users to deposit tokens and expand yield opportunities.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Add Liquidity
   */
  async yieldaggregatorAddliquidity(requestParameters, initOverrides) {
    const response = await this.yieldaggregatorAddliquidityRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for yieldaggregatorDepositpool without sending the request
   */
  async yieldaggregatorDepositpoolRequestOpts(requestParameters) {
    if (requestParameters["yieldaggregatorDepositpoolRequestPayload"] == null) {
      throw new RequiredError(
        "yieldaggregatorDepositpoolRequestPayload",
        'Required parameter "yieldaggregatorDepositpoolRequestPayload" was null or undefined when calling yieldaggregatorDepositpool().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/yieldaggregator/depositpool`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["yieldaggregatorDepositpoolRequestPayload"]
    };
  }
  /**
   * Enables users to deposit tokens into yield-generating pools on supported blockchains, specifying the target pool either via a direct poolAddress or by providing the tokenAddress with additional parameters. Upon success, the endpoint returns key details (chainId, addresses, transaction data, referenceId) that streamline investment management in decentralized finance protocols.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Deposit Pool
   */
  async yieldaggregatorDepositpoolRaw(requestParameters, initOverrides) {
    const requestOptions = await this.yieldaggregatorDepositpoolRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Enables users to deposit tokens into yield-generating pools on supported blockchains, specifying the target pool either via a direct poolAddress or by providing the tokenAddress with additional parameters. Upon success, the endpoint returns key details (chainId, addresses, transaction data, referenceId) that streamline investment management in decentralized finance protocols.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Deposit Pool
   */
  async yieldaggregatorDepositpool(requestParameters, initOverrides) {
    const response = await this.yieldaggregatorDepositpoolRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for yieldaggregatorDepositvault without sending the request
   */
  async yieldaggregatorDepositvaultRequestOpts(requestParameters) {
    if (requestParameters["yieldaggregatorDepositvaultRequestPayload"] == null) {
      throw new RequiredError(
        "yieldaggregatorDepositvaultRequestPayload",
        'Required parameter "yieldaggregatorDepositvaultRequestPayload" was null or undefined when calling yieldaggregatorDepositvault().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/yieldaggregator/depositvault`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["yieldaggregatorDepositvaultRequestPayload"]
    };
  }
  /**
   * Initiates token deposits into yield-generating vaults with configurable gas parameters and flexible identification methods (via vaultAddress or tokenAddress and vaultNumber). The endpoint processes the deposit transaction on the designated blockchain and returns key details—such as chain ID, addresses, transaction value, gas usage, and a unique reference ID—essential for managing multiple vaults efficiently.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Deposit Vault
   */
  async yieldaggregatorDepositvaultRaw(requestParameters, initOverrides) {
    const requestOptions = await this.yieldaggregatorDepositvaultRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Initiates token deposits into yield-generating vaults with configurable gas parameters and flexible identification methods (via vaultAddress or tokenAddress and vaultNumber). The endpoint processes the deposit transaction on the designated blockchain and returns key details—such as chain ID, addresses, transaction value, gas usage, and a unique reference ID—essential for managing multiple vaults efficiently.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Deposit Vault
   */
  async yieldaggregatorDepositvault(requestParameters, initOverrides) {
    const response = await this.yieldaggregatorDepositvaultRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for yieldaggregatorGetbalance without sending the request
   */
  async yieldaggregatorGetbalanceRequestOpts(requestParameters) {
    if (requestParameters["address"] == null) {
      throw new RequiredError(
        "address",
        'Required parameter "address" was null or undefined when calling yieldaggregatorGetbalance().'
      );
    }
    if (requestParameters["marketAddress"] == null) {
      throw new RequiredError(
        "marketAddress",
        'Required parameter "marketAddress" was null or undefined when calling yieldaggregatorGetbalance().'
      );
    }
    const queryParameters = {};
    if (requestParameters["address"] != null) {
      queryParameters["address"] = requestParameters["address"];
    }
    if (requestParameters["tokenAddress"] != null) {
      queryParameters["tokenAddress"] = requestParameters["tokenAddress"];
    }
    if (requestParameters["vaultNumber"] != null) {
      queryParameters["vaultNumber"] = requestParameters["vaultNumber"];
    }
    if (requestParameters["yieldAggregatorId"] != null) {
      queryParameters["yieldAggregatorId"] = requestParameters["yieldAggregatorId"];
    }
    if (requestParameters["vaultAddress"] != null) {
      queryParameters["vaultAddress"] = requestParameters["vaultAddress"];
    }
    if (requestParameters["marketAddress"] != null) {
      queryParameters["marketAddress"] = requestParameters["marketAddress"];
    }
    if (requestParameters["poolAddress"] != null) {
      queryParameters["poolAddress"] = requestParameters["poolAddress"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/yieldaggregator/getbalance`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a user’s current liquidity balance and position within yield-generating vaults or pools across supported protocols and blockchain networks. This endpoint returns a numeric value representing the total liquidity available for the given address, with detailed breakdowns of both vault and pool balances. Users can query their balance either by providing a direct vaultAddress or by supplying a tokenAddress along with a vaultNumber, in addition to required parameters such as the user’s public address and yieldAggregatorId. This comprehensive data is essential for developers and users seeking to monitor yield positions, manage liquidity, track earned yields, and integrate real-time yield data into DeFi portfolio management and performance analytics tools.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Get Balance
   */
  async yieldaggregatorGetbalanceRaw(requestParameters, initOverrides) {
    const requestOptions = await this.yieldaggregatorGetbalanceRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a user’s current liquidity balance and position within yield-generating vaults or pools across supported protocols and blockchain networks. This endpoint returns a numeric value representing the total liquidity available for the given address, with detailed breakdowns of both vault and pool balances. Users can query their balance either by providing a direct vaultAddress or by supplying a tokenAddress along with a vaultNumber, in addition to required parameters such as the user’s public address and yieldAggregatorId. This comprehensive data is essential for developers and users seeking to monitor yield positions, manage liquidity, track earned yields, and integrate real-time yield data into DeFi portfolio management and performance analytics tools.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Get Balance
   */
  async yieldaggregatorGetbalance(requestParameters, initOverrides) {
    const response = await this.yieldaggregatorGetbalanceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for yieldaggregatorGetmarketdata without sending the request
   */
  async yieldaggregatorGetmarketdataRequestOpts(requestParameters) {
    if (requestParameters["marketAddress"] == null) {
      throw new RequiredError(
        "marketAddress",
        'Required parameter "marketAddress" was null or undefined when calling yieldaggregatorGetmarketdata().'
      );
    }
    const queryParameters = {};
    if (requestParameters["yieldAggregatorId"] != null) {
      queryParameters["yieldAggregatorId"] = requestParameters["yieldAggregatorId"];
    }
    if (requestParameters["marketAddress"] != null) {
      queryParameters["marketAddress"] = requestParameters["marketAddress"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/yieldaggregator/getmarketdata`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Provides detailed financial metrics (liquidity, trading volume, token supply, PT, LP, SY) for a specified yield market. This snapshot helps users and developers monitor market performance and make informed DeFi decisions.  > 💡  **Compute Unit Value:** `70` (Fixed)
   * Get Market Data
   */
  async yieldaggregatorGetmarketdataRaw(requestParameters, initOverrides) {
    const requestOptions = await this.yieldaggregatorGetmarketdataRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Provides detailed financial metrics (liquidity, trading volume, token supply, PT, LP, SY) for a specified yield market. This snapshot helps users and developers monitor market performance and make informed DeFi decisions.  > 💡  **Compute Unit Value:** `70` (Fixed)
   * Get Market Data
   */
  async yieldaggregatorGetmarketdata(requestParameters, initOverrides) {
    const response = await this.yieldaggregatorGetmarketdataRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for yieldaggregatorGetmarkets without sending the request
   */
  async yieldaggregatorGetmarketsRequestOpts(requestParameters) {
    const queryParameters = {};
    if (requestParameters["yieldAggregatorId"] != null) {
      queryParameters["yieldAggregatorId"] = requestParameters["yieldAggregatorId"];
    }
    if (requestParameters["includeInactive"] != null) {
      queryParameters["includeInactive"] = requestParameters["includeInactive"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/yieldaggregator/getmarkets`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Retrieves a comprehensive list of yield markets (e.g., Pendle Finance) with identifiers, expiry dates, asset addresses, and token addresses (PT, YT, SY). This data helps users discover active markets, assess liquidity opportunities, and optimize yield strategies across supported networks.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Get Markets
   */
  async yieldaggregatorGetmarketsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.yieldaggregatorGetmarketsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Retrieves a comprehensive list of yield markets (e.g., Pendle Finance) with identifiers, expiry dates, asset addresses, and token addresses (PT, YT, SY). This data helps users discover active markets, assess liquidity opportunities, and optimize yield strategies across supported networks.  > 💡  **Compute Unit Value:** `25` (Fixed)
   * Get Markets
   */
  async yieldaggregatorGetmarkets(requestParameters = {}, initOverrides) {
    const response = await this.yieldaggregatorGetmarketsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for yieldaggregatorGetmarkettokens without sending the request
   */
  async yieldaggregatorGetmarkettokensRequestOpts(requestParameters) {
    if (requestParameters["marketAddress"] == null) {
      throw new RequiredError(
        "marketAddress",
        'Required parameter "marketAddress" was null or undefined when calling yieldaggregatorGetmarkettokens().'
      );
    }
    const queryParameters = {};
    if (requestParameters["yieldAggregatorId"] != null) {
      queryParameters["yieldAggregatorId"] = requestParameters["yieldAggregatorId"];
    }
    if (requestParameters["marketAddress"] != null) {
      queryParameters["marketAddress"] = requestParameters["marketAddress"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/yieldaggregator/getmarkettokens`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Returns the token configuration for a given yield market, including mint/redeem token arrays and a swap configuration object. This information clarifies token flows for effective liquidity management and yield optimization.  > 💡  **Compute Unit Value:** `75` (Fixed)
   * Get Market Tokens
   */
  async yieldaggregatorGetmarkettokensRaw(requestParameters, initOverrides) {
    const requestOptions = await this.yieldaggregatorGetmarkettokensRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Returns the token configuration for a given yield market, including mint/redeem token arrays and a swap configuration object. This information clarifies token flows for effective liquidity management and yield optimization.  > 💡  **Compute Unit Value:** `75` (Fixed)
   * Get Market Tokens
   */
  async yieldaggregatorGetmarkettokens(requestParameters, initOverrides) {
    const response = await this.yieldaggregatorGetmarkettokensRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for yieldaggregatorGetvaults without sending the request
   */
  async yieldaggregatorGetvaultsRequestOpts(requestParameters) {
    if (requestParameters["tokenAddress"] == null) {
      throw new RequiredError(
        "tokenAddress",
        'Required parameter "tokenAddress" was null or undefined when calling yieldaggregatorGetvaults().'
      );
    }
    const queryParameters = {};
    if (requestParameters["yieldAggregatorId"] != null) {
      queryParameters["yieldAggregatorId"] = requestParameters["yieldAggregatorId"];
    }
    if (requestParameters["tokenAddress"] != null) {
      queryParameters["tokenAddress"] = requestParameters["tokenAddress"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/yieldaggregator/getvaults`;
    return {
      path: urlPath,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    };
  }
  /**
   * Fetches vault information (APY, fees, performance) for yield-generating opportunities across supported blockchains.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Get Vaults
   */
  async yieldaggregatorGetvaultsRaw(requestParameters, initOverrides) {
    const requestOptions = await this.yieldaggregatorGetvaultsRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Fetches vault information (APY, fees, performance) for yield-generating opportunities across supported blockchains.  > 💡  **Compute Unit Value:** `115` (Fixed)
   * Get Vaults
   */
  async yieldaggregatorGetvaults(requestParameters, initOverrides) {
    const response = await this.yieldaggregatorGetvaultsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for yieldaggregatorMint without sending the request
   */
  async yieldaggregatorMintRequestOpts(requestParameters) {
    if (requestParameters["yieldaggregatorMintRequestPayload"] == null) {
      throw new RequiredError(
        "yieldaggregatorMintRequestPayload",
        'Required parameter "yieldaggregatorMintRequestPayload" was null or undefined when calling yieldaggregatorMint().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/yieldaggregator/mint`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["yieldaggregatorMintRequestPayload"]
    };
  }
  /**
   * Converts deposited assets into yield-bearing tokens (or share tokens) within a yield aggregator protocol. The response includes transaction details (chain ID, addresses, gas, reference ID) for secure tracking.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Mint
   */
  async yieldaggregatorMintRaw(requestParameters, initOverrides) {
    const requestOptions = await this.yieldaggregatorMintRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Converts deposited assets into yield-bearing tokens (or share tokens) within a yield aggregator protocol. The response includes transaction details (chain ID, addresses, gas, reference ID) for secure tracking.  > 💡  **Compute Unit Value:** `30` (Fixed)
   * Mint
   */
  async yieldaggregatorMint(requestParameters, initOverrides) {
    const response = await this.yieldaggregatorMintRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for yieldaggregatorRedeem without sending the request
   */
  async yieldaggregatorRedeemRequestOpts(requestParameters) {
    if (requestParameters["yieldaggregatorRedeemRequestPayload"] == null) {
      throw new RequiredError(
        "yieldaggregatorRedeemRequestPayload",
        'Required parameter "yieldaggregatorRedeemRequestPayload" was null or undefined when calling yieldaggregatorRedeem().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/yieldaggregator/redeem`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["yieldaggregatorRedeemRequestPayload"]
    };
  }
  /**
   * Converts yield-bearing tokens back into their underlying asset within a yield aggregator protocol. The response includes transaction metadata (chain ID, addresses, gas) and a unique reference ID for tracking.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Redeem
   */
  async yieldaggregatorRedeemRaw(requestParameters, initOverrides) {
    const requestOptions = await this.yieldaggregatorRedeemRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Converts yield-bearing tokens back into their underlying asset within a yield aggregator protocol. The response includes transaction metadata (chain ID, addresses, gas) and a unique reference ID for tracking.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Redeem
   */
  async yieldaggregatorRedeem(requestParameters, initOverrides) {
    const response = await this.yieldaggregatorRedeemRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for yieldaggregatorRemoveliquidity without sending the request
   */
  async yieldaggregatorRemoveliquidityRequestOpts(requestParameters) {
    if (requestParameters["yieldaggregatorRemoveliquidityRequestPayload"] == null) {
      throw new RequiredError(
        "yieldaggregatorRemoveliquidityRequestPayload",
        'Required parameter "yieldaggregatorRemoveliquidityRequestPayload" was null or undefined when calling yieldaggregatorRemoveliquidity().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/yieldaggregator/removeliquidity`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["yieldaggregatorRemoveliquidityRequestPayload"]
    };
  }
  /**
   * Withdraws tokens from a yield aggregator market, supporting advanced options like dual-token removal and slippage control. The response includes chain details, addresses, gas usage, and a unique reference ID for transaction tracking.  > 💡  **Compute Unit Value:** `120` (Fixed)
   * Remove Liquidity
   */
  async yieldaggregatorRemoveliquidityRaw(requestParameters, initOverrides) {
    const requestOptions = await this.yieldaggregatorRemoveliquidityRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Withdraws tokens from a yield aggregator market, supporting advanced options like dual-token removal and slippage control. The response includes chain details, addresses, gas usage, and a unique reference ID for transaction tracking.  > 💡  **Compute Unit Value:** `120` (Fixed)
   * Remove Liquidity
   */
  async yieldaggregatorRemoveliquidity(requestParameters, initOverrides) {
    const response = await this.yieldaggregatorRemoveliquidityRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for yieldaggregatorSwap without sending the request
   */
  async yieldaggregatorSwapRequestOpts(requestParameters) {
    if (requestParameters["yieldaggregatorSwapRequestPayload"] == null) {
      throw new RequiredError(
        "yieldaggregatorSwapRequestPayload",
        'Required parameter "yieldaggregatorSwapRequestPayload" was null or undefined when calling yieldaggregatorSwap().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/yieldaggregator/swap`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["yieldaggregatorSwapRequestPayload"]
    };
  }
  /**
   * Executes token swaps within a yield aggregator protocol, providing transaction metadata (chain ID, addresses, gas, reference ID). Configurable slippage, swap path, and gas priority let users tailor swaps to market conditions.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Swap
   */
  async yieldaggregatorSwapRaw(requestParameters, initOverrides) {
    const requestOptions = await this.yieldaggregatorSwapRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Executes token swaps within a yield aggregator protocol, providing transaction metadata (chain ID, addresses, gas, reference ID). Configurable slippage, swap path, and gas priority let users tailor swaps to market conditions.  > 💡  **Compute Unit Value:** `95` (Fixed)
   * Swap
   */
  async yieldaggregatorSwap(requestParameters, initOverrides) {
    const response = await this.yieldaggregatorSwapRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for yieldaggregatorTransferliquidity without sending the request
   */
  async yieldaggregatorTransferliquidityRequestOpts(requestParameters) {
    if (requestParameters["yieldaggregatorTransferliquidityRequestPayload"] == null) {
      throw new RequiredError(
        "yieldaggregatorTransferliquidityRequestPayload",
        'Required parameter "yieldaggregatorTransferliquidityRequestPayload" was null or undefined when calling yieldaggregatorTransferliquidity().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/yieldaggregator/transferliquidity`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["yieldaggregatorTransferliquidityRequestPayload"]
    };
  }
  /**
   * Moves liquidity between markets, specifying source/destination addresses plus amounts of LP, principal, and yield tokens. Optional parameters (slippage, redeemRewards, zeroPriceImpact) fine-tune the transaction, with a reference ID for tracking.  > 💡  **Compute Unit Value:** `125` (Fixed)
   * Transfer Liquidity
   */
  async yieldaggregatorTransferliquidityRaw(requestParameters, initOverrides) {
    const requestOptions = await this.yieldaggregatorTransferliquidityRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Moves liquidity between markets, specifying source/destination addresses plus amounts of LP, principal, and yield tokens. Optional parameters (slippage, redeemRewards, zeroPriceImpact) fine-tune the transaction, with a reference ID for tracking.  > 💡  **Compute Unit Value:** `125` (Fixed)
   * Transfer Liquidity
   */
  async yieldaggregatorTransferliquidity(requestParameters, initOverrides) {
    const response = await this.yieldaggregatorTransferliquidityRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for yieldaggregatorWithdrawpool without sending the request
   */
  async yieldaggregatorWithdrawpoolRequestOpts(requestParameters) {
    if (requestParameters["yieldaggregatorWithdrawpoolRequestPayload"] == null) {
      throw new RequiredError(
        "yieldaggregatorWithdrawpoolRequestPayload",
        'Required parameter "yieldaggregatorWithdrawpoolRequestPayload" was null or undefined when calling yieldaggregatorWithdrawpool().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/yieldaggregator/withdrawpool`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["yieldaggregatorWithdrawpoolRequestPayload"]
    };
  }
  /**
   * Processes withdrawals from yield-generating pools with configurable gas parameters and flexible pool identification options.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Withdraw Pool
   */
  async yieldaggregatorWithdrawpoolRaw(requestParameters, initOverrides) {
    const requestOptions = await this.yieldaggregatorWithdrawpoolRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Processes withdrawals from yield-generating pools with configurable gas parameters and flexible pool identification options.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Withdraw Pool
   */
  async yieldaggregatorWithdrawpool(requestParameters, initOverrides) {
    const response = await this.yieldaggregatorWithdrawpoolRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates request options for yieldaggregatorWithdrawvault without sending the request
   */
  async yieldaggregatorWithdrawvaultRequestOpts(requestParameters) {
    if (requestParameters["yieldaggregatorWithdrawvaultRequestPayload"] == null) {
      throw new RequiredError(
        "yieldaggregatorWithdrawvaultRequestPayload",
        'Required parameter "yieldaggregatorWithdrawvaultRequestPayload" was null or undefined when calling yieldaggregatorWithdrawvault().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-Key"] = await this.configuration.apiKey("X-API-Key");
    }
    let urlPath = `/defi/v1/yieldaggregator/withdrawvault`;
    return {
      path: urlPath,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters["yieldaggregatorWithdrawvaultRequestPayload"]
    };
  }
  /**
   * Executes withdrawals from yield vaults, returning the user’s deposited tokens. Multiple identification methods (vaultAddress, tokenAddress, vaultNumber) are supported, with transaction details provided for verification and tracking.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Withdraw Vault
   */
  async yieldaggregatorWithdrawvaultRaw(requestParameters, initOverrides) {
    const requestOptions = await this.yieldaggregatorWithdrawvaultRequestOpts(requestParameters);
    const response = await this.request(requestOptions, initOverrides);
    return new JSONApiResponse(response);
  }
  /**
   * Executes withdrawals from yield vaults, returning the user’s deposited tokens. Multiple identification methods (vaultAddress, tokenAddress, vaultNumber) are supported, with transaction details provided for verification and tracking.  > 💡  **Compute Unit Value:** `40` (Fixed)
   * Withdraw Vault
   */
  async yieldaggregatorWithdrawvault(requestParameters, initOverrides) {
    const response = await this.yieldaggregatorWithdrawvaultRaw(requestParameters, initOverrides);
    return await response.value();
  }
};
var YieldaggregatorGetbalanceYieldAggregatorIdEnum = {
  _5000: "5000",
  _5001: "5001",
  _5002: "5002",
  _5003: "5003",
  _5200: "5200",
  _5201: "5201",
  _5202: "5202",
  _5203: "5203",
  _5204: "5204",
  _5100: "5100"
};
var YieldaggregatorGetmarketdataYieldAggregatorIdEnum = {
  _5200: "5200",
  _5201: "5201",
  _5202: "5202",
  _5203: "5203",
  _5204: "5204"
};
var YieldaggregatorGetmarketsYieldAggregatorIdEnum = {
  _5200: "5200",
  _5201: "5201",
  _5202: "5202",
  _5203: "5203",
  _5204: "5204"
};
var YieldaggregatorGetmarkettokensYieldAggregatorIdEnum = {
  _5200: "5200",
  _5201: "5201",
  _5202: "5202",
  _5203: "5203",
  _5204: "5204"
};
var YieldaggregatorGetvaultsYieldAggregatorIdEnum = {
  _5000: "5000",
  _5001: "5001",
  _5002: "5002",
  _5003: "5003",
  _5100: "5100"
};

// src/models/index.ts
var AaSenduseropsRequestPayloadChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _137: "137",
  _250: "250",
  _10: "10",
  _11155420: "11155420",
  _42161: "42161",
  _421614: "421614",
  _25: "25",
  _338: "338",
  _84532: "84532",
  _8453: "8453",
  _43113: "43113",
  _97: "97",
  _80002: "80002",
  _300: "300",
  _324: "324"
};
var BridgeAddliquidityRequestPayloadBridgeIdEnum = {
  _100: "100",
  _500: "500"
};
var BridgeRemoveliquidityRequestPayloadBridgeIdEnum = {
  _100: "100",
  _500: "500"
};
var BridgeSwap200ResponseDataAnyOf2PayloadMethodUsedEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var BridgeSwap200ResponseDataPayloadMethodUsedEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var BridgeSwapRequestPayloadBridgeIdEnum = {
  _100: "100",
  _600: "600",
  _300: "300",
  _400: "400",
  _500: "500"
};
var BridgeSwapRequestPayloadBatchOptionEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var BridgeSwapaggregatorRequestPayloadBatchOptionEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var BridgeSwaps200ResponseDataAnyOf1InnerPayloadMethodUsedEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var BridgeSwapwithapprovalRequestPayloadBridgeIdEnum = {
  _600: "600",
  _300: "300",
  _400: "400",
  _100: "100",
  _500: "500"
};
var BridgeSwapwithapprovalRequestPayloadBatchOptionEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var BridgeUnlockRequestPayloadBridgeIdEnum = {
  _300: "300"
};
var ChainCreateaccountRequestPayloadChainIdEnum = {
  _1500: "1500",
  _1501: "1501",
  _1600: "1600",
  _1601: "1601"
};
var ChainCreatefeetransactionRequestPayloadChainIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _25: "25",
  _56: "56",
  _97: "97",
  _43114: "43114",
  _338: "338",
  _43113: "43113",
  _421614: "421614",
  _17000: "17000",
  _42161: "42161",
  _10: "10",
  _11155420: "11155420",
  _4002: "4002",
  _324: "324",
  _300: "300",
  _137: "137",
  _80002: "80002",
  _250: "250",
  _8453: "8453",
  _84532: "84532"
};
var ChainDecodetransactionRequestPayloadChainIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _56: "56",
  _97: "97",
  _43114: "43114",
  _338: "338",
  _900: "900",
  _901: "901",
  _1200: "1200",
  _1201: "1201",
  _43113: "43113",
  _42161: "42161",
  _10: "10",
  _11155420: "11155420",
  _4002: "4002",
  _324: "324",
  _300: "300",
  Cosmoshub4: "cosmoshub-4",
  _1600: "1600",
  _1601: "1601",
  _1700: "1700",
  _1701: "1701",
  Osmosis1: "osmosis-1",
  OsmoTest5: "osmo-test-5",
  _1800: "1800",
  _1801: "1801",
  _137: "137",
  _80002: "80002",
  _250: "250",
  _8453: "8453",
  _84532: "84532",
  _1500: "1500",
  _1501: "1501",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636",
  _8217: "8217",
  _1001: "1001"
};
var ChainEstimategasRequestPayloadChainIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _56: "56",
  _25: "25",
  _97: "97",
  _43114: "43114",
  _338: "338",
  _43113: "43113",
  _42161: "42161",
  _421614: "421614",
  _17000: "17000",
  _10: "10",
  _11155420: "11155420",
  _4002: "4002",
  _324: "324",
  _300: "300",
  _137: "137",
  _80002: "80002",
  _250: "250",
  _8453: "8453",
  _84532: "84532",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636",
  _8217: "8217",
  _1001: "1001"
};
var ChainGenericsmartcontractmethodRequestPayloadChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _42161: "42161",
  _10: "10",
  _324: "324",
  _137: "137",
  _8453: "8453"
};
var ChainGenericsmartcontractmethodRequestPayloadAbiEnum = {
  Empty: ""
};
var ChainRegistercoinRequestPayloadChainIdEnum = {
  _1401: "1401",
  _1400: "1400"
};
var ChainSendtransactionRequestPayloadChainIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _56: "56",
  _25: "25",
  _97: "97",
  _43114: "43114",
  _338: "338",
  _900: "900",
  _901: "901",
  _1000: "1000",
  _1002: "1002",
  _1200: "1200",
  _1201: "1201",
  _43113: "43113",
  _42161: "42161",
  _421614: "421614",
  _10: "10",
  _11155420: "11155420",
  _4002: "4002",
  _324: "324",
  _300: "300",
  _1300: "1300",
  _1301: "1301",
  _101: "101",
  _102: "102",
  _103: "103",
  Cosmoshub4: "cosmoshub-4",
  _1600: "1600",
  _1601: "1601",
  _1700: "1700",
  _1701: "1701",
  Osmosis1: "osmosis-1",
  OsmoTest5: "osmo-test-5",
  _1800: "1800",
  _1801: "1801",
  _137: "137",
  _80002: "80002",
  _250: "250",
  _8453: "8453",
  _84532: "84532",
  _1400: "1400",
  _1401: "1401",
  _400: "400",
  _401: "401",
  _1500: "1500",
  _1501: "1501",
  _17000: "17000",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636",
  _8217: "8217",
  _1001: "1001"
};
var ChainSimulatetransactionRequestPayloadChainIdEnum = {
  _1500: "1500",
  _1501: "1501"
};
var DexAddliquidityRequestPayloadDexIdEnum = {
  _1000: "1000",
  _1001: "1001",
  _1300: "1300",
  _1301: "1301",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _1310: "1310",
  _1100: "1100",
  _1101: "1101",
  _1200: "1200",
  _1201: "1201",
  _2900: "2900",
  _2901: "2901",
  _2800: "2800",
  _2808: "2808",
  _2809: "2809",
  _1500: "1500",
  _1400: "1400",
  _1402: "1402",
  _2300: "2300",
  _2100: "2100",
  _2101: "2101",
  _2500: "2500",
  _2501: "2501",
  _2700: "2700",
  _2400: "2400",
  _2401: "2401",
  _3000: "3000",
  _3001: "3001",
  _3300: "3300",
  _3301: "3301",
  _3500: "3500",
  _3501: "3501",
  _3400: "3400",
  _3401: "3401",
  _3600: "3600"
};
var DexAddliquidityRequestPayloadStrategyEnum = {
  Stable: "stable",
  Uncorrelated: "uncorrelated"
};
var DexCancelorderRequestPayloadDexIdEnum = {
  _2200: "2200",
  _2201: "2201",
  _2202: "2202",
  _2203: "2203",
  _2204: "2204",
  _2205: "2205",
  _2206: "2206"
};
var DexCreateorderRequestPayloadDexIdEnum = {
  _1900: "1900",
  _2200: "2200",
  _2201: "2201",
  _2202: "2202",
  _2203: "2203",
  _2204: "2204",
  _2205: "2205",
  _2206: "2206"
};
var DexFillorderRequestPayloadDexIdEnum = {
  _2200: "2200",
  _2201: "2201",
  _2202: "2202",
  _2203: "2203",
  _2204: "2204",
  _2205: "2205",
  _2206: "2206"
};
var DexPublishorderRequestPayloadDexIdEnum = {
  _2200: "2200",
  _2201: "2201",
  _2202: "2202",
  _2203: "2203",
  _2204: "2204",
  _2205: "2205",
  _2206: "2206"
};
var DexPublishorderRequestPayloadOrderTypeEnum = {
  Create: "create",
  Cancel: "cancel"
};
var DexRemoveliquidityRequestPayloadDexIdEnum = {
  _1000: "1000",
  _1001: "1001",
  _1300: "1300",
  _1301: "1301",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _1310: "1310",
  _1100: "1100",
  _1101: "1101",
  _1200: "1200",
  _1201: "1201",
  _2900: "2900",
  _2901: "2901",
  _2800: "2800",
  _2808: "2808",
  _2809: "2809",
  _1500: "1500",
  _1400: "1400",
  _1402: "1402",
  _2300: "2300",
  _2100: "2100",
  _2101: "2101",
  _2500: "2500",
  _2501: "2501",
  _2700: "2700",
  _2400: "2400",
  _2401: "2401",
  _3000: "3000",
  _3001: "3001",
  _3300: "3300",
  _3301: "3301",
  _3500: "3500",
  _3501: "3501",
  _3400: "3400",
  _3401: "3401",
  _3600: "3600"
};
var DexSwap200ResponseDataPayloadMethodUsedEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var DexSwapRequestPayloadDexIdEnum = {
  _1000: "1000",
  _1001: "1001",
  _1900: "1900",
  _1300: "1300",
  _1301: "1301",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _1310: "1310",
  _1100: "1100",
  _1101: "1101",
  _1200: "1200",
  _1201: "1201",
  _2900: "2900",
  _2901: "2901",
  _2800: "2800",
  _2808: "2808",
  _2809: "2809",
  _1500: "1500",
  _1400: "1400",
  _1402: "1402",
  _2300: "2300",
  _2100: "2100",
  _2101: "2101",
  _2500: "2500",
  _2501: "2501",
  _2700: "2700",
  _2600: "2600",
  _2400: "2400",
  _2401: "2401",
  _1600: "1600",
  _1602: "1602",
  _1604: "1604",
  _1605: "1605",
  _1607: "1607",
  _1608: "1608",
  _1700: "1700",
  _1701: "1701",
  _1702: "1702",
  _1703: "1703",
  _1704: "1704",
  _1706: "1706",
  _1707: "1707",
  _2200: "2200",
  _2201: "2201",
  _2202: "2202",
  _2203: "2203",
  _2204: "2204",
  _2205: "2205",
  _2206: "2206",
  _3000: "3000",
  _3001: "3001",
  _3100: "3100",
  _3300: "3300",
  _3301: "3301",
  _3500: "3500",
  _3501: "3501",
  _3400: "3400",
  _3401: "3401",
  _3600: "3600",
  _3800: "3800",
  _3700: "3700"
};
var DexSwapRequestPayloadBatchOptionEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var DexSwapaggregatorRequestPayloadChainIdEnum = {
  _1: "1",
  _43114: "43114",
  _56: "56",
  _137: "137",
  _42161: "42161",
  _10: "10",
  _8453: "8453",
  _324: "324",
  _5000: "5000",
  _900: "900",
  _1400: "1400",
  _1200: "1200",
  _101: "101",
  _1700: "1700",
  _1500: "1500",
  _1100: "1100",
  _1600: "1600",
  _8217: "8217"
};
var DexSwapaggregatorRequestPayloadBatchOptionEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var DexSwaps200ResponseDataAnyOf1InnerPayloadMethodUsedEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var DexSwapwithapprovalRequestPayloadDexIdEnum = {
  _1000: "1000",
  _1001: "1001",
  _1300: "1300",
  _1301: "1301",
  _1305: "1305",
  _1306: "1306",
  _1307: "1307",
  _1308: "1308",
  _1309: "1309",
  _1310: "1310",
  _1100: "1100",
  _1101: "1101",
  _1200: "1200",
  _1201: "1201",
  _2900: "2900",
  _2901: "2901",
  _2800: "2800",
  _2808: "2808",
  _2809: "2809",
  _1500: "1500",
  _1400: "1400",
  _1402: "1402",
  _2300: "2300",
  _2100: "2100",
  _2101: "2101",
  _2500: "2500",
  _2501: "2501",
  _2700: "2700",
  _2600: "2600",
  _2400: "2400",
  _2401: "2401",
  _1600: "1600",
  _1602: "1602",
  _1604: "1604",
  _1605: "1605",
  _1607: "1607",
  _1608: "1608",
  _1700: "1700",
  _1701: "1701",
  _1702: "1702",
  _1703: "1703",
  _1704: "1704",
  _1706: "1706",
  _1707: "1707",
  _2200: "2200",
  _2201: "2201",
  _2202: "2202",
  _2203: "2203",
  _2204: "2204",
  _2205: "2205",
  _2206: "2206",
  _3000: "3000",
  _3001: "3001",
  _3100: "3100",
  _3300: "3300",
  _3301: "3301",
  _3500: "3500",
  _3501: "3501",
  _3400: "3400",
  _3401: "3401",
  _3600: "3600",
  _3800: "3800",
  _3700: "3700"
};
var DexSwapwithapprovalRequestPayloadBatchOptionEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var FungibletokenApproveRequestPayloadChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _137: "137",
  _900: "900",
  _901: "901",
  _250: "250",
  _10: "10",
  _11155420: "11155420",
  _42161: "42161",
  _421614: "421614",
  _25: "25",
  _338: "338",
  _84532: "84532",
  _8453: "8453",
  _43113: "43113",
  _97: "97",
  _80002: "80002",
  _300: "300",
  _324: "324",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636"
};
var FungibletokenConvertbasetokentowraptokenRequestPayloadChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _137: "137",
  _250: "250",
  _10: "10",
  _11155420: "11155420",
  _42161: "42161",
  _421614: "421614",
  _25: "25",
  _338: "338",
  _84532: "84532",
  _8453: "8453",
  _43113: "43113",
  _97: "97",
  _80002: "80002",
  _300: "300",
  _324: "324",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636"
};
var FungibletokenConvertwraptokentobasetokenRequestPayloadChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _137: "137",
  _250: "250",
  _10: "10",
  _11155420: "11155420",
  _42161: "42161",
  _421614: "421614",
  _25: "25",
  _338: "338",
  _84532: "84532",
  _8453: "8453",
  _43113: "43113",
  _97: "97",
  _80002: "80002",
  _300: "300",
  _324: "324",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636",
  _8217: "8217",
  _1001: "1001"
};
var FungibletokenTransferRequestPayloadChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _137: "137",
  _900: "900",
  _901: "901",
  _250: "250",
  _10: "10",
  _11155420: "11155420",
  _42161: "42161",
  _421614: "421614",
  _25: "25",
  _338: "338",
  _84532: "84532",
  _8453: "8453",
  _43113: "43113",
  _97: "97",
  _80002: "80002",
  _300: "300",
  _324: "324",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636"
};
var FungibletokenTransferfromRequestPayloadChainIdEnum = {
  _1: "1",
  _56: "56",
  _43114: "43114",
  _137: "137",
  _250: "250",
  _10: "10",
  _11155420: "11155420",
  _42161: "42161",
  _421614: "421614",
  _25: "25",
  _338: "338",
  _84532: "84532",
  _8453: "8453",
  _43113: "43113",
  _97: "97",
  _80002: "80002",
  _300: "300",
  _324: "324",
  _5000: "5000",
  _5003: "5003",
  _3637: "3637",
  _3636: "3636"
};
var LendborrowAllowRequestPayloadLendborrowIdEnum = {
  _1300: "1300",
  _1302: "1302"
};
var LendborrowBorrow200ResponseDataPayloadMethodUsedEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var LendborrowBorrowRequestPayloadLendborrowIdEnum = {
  _1000: "1000",
  _1003: "1003",
  _1005: "1005",
  _1200: "1200",
  _1202: "1202",
  _1203: "1203",
  _1204: "1204",
  _1205: "1205",
  _1206: "1206",
  _1207: "1207",
  _1100: "1100",
  _1300: "1300",
  _1302: "1302",
  _1400: "1400",
  _1401: "1401",
  _1402: "1402",
  _1403: "1403",
  _1404: "1404"
};
var LendborrowBorrowRequestPayloadBatchOptionEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var LendborrowBundleactionsRequestPayloadLendborrowIdEnum = {
  _1300: "1300",
  _1302: "1302"
};
var LendborrowClaimrewardsRequestPayloadLendborrowIdEnum = {
  _1300: "1300",
  _1302: "1302"
};
var LendborrowDeposit200ResponseDataPayloadMethodUsedEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var LendborrowDepositRequestPayloadLendborrowIdEnum = {
  _1000: "1000",
  _1003: "1003",
  _1005: "1005",
  _1200: "1200",
  _1202: "1202",
  _1203: "1203",
  _1204: "1204",
  _1205: "1205",
  _1206: "1206",
  _1207: "1207",
  _1100: "1100",
  _1300: "1300",
  _1302: "1302",
  _1400: "1400",
  _1401: "1401",
  _1402: "1402",
  _1403: "1403",
  _1404: "1404"
};
var LendborrowDepositRequestPayloadBatchOptionEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var LendborrowEntermarketRequestPayloadLendborrowIdEnum = {
  _1100: "1100"
};
var LendborrowExitisolationmodeRequestPayloadLendborrowIdEnum = {
  _1200: "1200",
  _1202: "1202",
  _1203: "1203",
  _1204: "1204",
  _1205: "1205",
  _1206: "1206",
  _1207: "1207"
};
var LendborrowExitmarketRequestPayloadLendborrowIdEnum = {
  _1100: "1100"
};
var LendborrowMigrateRequestPayloadLendborrowIdEnum = {
  _1200: "1200",
  _1205: "1205"
};
var LendborrowRepay200ResponseDataPayloadMethodUsedEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var LendborrowRepayRequestPayloadLendborrowIdEnum = {
  _1000: "1000",
  _1003: "1003",
  _1005: "1005",
  _1200: "1200",
  _1202: "1202",
  _1203: "1203",
  _1204: "1204",
  _1205: "1205",
  _1206: "1206",
  _1207: "1207",
  _1100: "1100",
  _1300: "1300",
  _1302: "1302",
  _1400: "1400",
  _1401: "1401",
  _1402: "1402",
  _1403: "1403",
  _1404: "1404"
};
var LendborrowRepayRequestPayloadBatchOptionEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var LendborrowSetuseremodeRequestPayloadLendborrowIdEnum = {
  _1200: "1200",
  _1202: "1202",
  _1203: "1203",
  _1204: "1204",
  _1205: "1205",
  _1206: "1206",
  _1207: "1207"
};
var LendborrowSetuseremodeRequestPayloadCategoryIdEnum = {
  _0: "0",
  _1: "1"
};
var LendborrowTransfer200ResponseDataPayloadMethodUsedEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var LendborrowTransferRequestPayloadLendborrowIdEnum = {
  _1300: "1300",
  _1302: "1302"
};
var LendborrowTransferRequestPayloadBatchOptionEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var LendborrowWithdraw200ResponseDataPayloadMethodUsedEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var LendborrowWithdrawRequestPayloadLendborrowIdEnum = {
  _1000: "1000",
  _1003: "1003",
  _1005: "1005",
  _1200: "1200",
  _1202: "1202",
  _1203: "1203",
  _1204: "1204",
  _1205: "1205",
  _1206: "1206",
  _1207: "1207",
  _1100: "1100",
  _1300: "1300",
  _1302: "1302",
  _1400: "1400",
  _1401: "1401",
  _1402: "1402",
  _1403: "1403",
  _1404: "1404"
};
var LendborrowWithdrawRequestPayloadBatchOptionEnum = {
  Eip5792: "EIP-5792",
  Eip7702: "EIP-7702"
};
var LiquidstakingApprovewithdrawalRequestPayloadLiquidStakingIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _2: "2",
  _56: "56",
  _97: "97",
  _137: "137"
};
var LiquidstakingBurnRequestPayloadLiquidStakingIdEnum = {
  _200: "200",
  _201: "201"
};
var LiquidstakingClaimRequestPayloadLiquidStakingIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _200: "200",
  _201: "201",
  _2: "2",
  _56: "56",
  _97: "97",
  _137: "137",
  _3: "3",
  _17000: "17000",
  _43114: "43114"
};
var LiquidstakingDecreaseallowanceRequestPayloadLiquidStakingIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _2: "2",
  _56: "56",
  _97: "97",
  _137: "137"
};
var LiquidstakingDelegateRequestPayloadLiquidStakingIdEnum = {
  _3: "3",
  _17000: "17000"
};
var LiquidstakingIncreaseallowanceRequestPayloadLiquidStakingIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _2: "2",
  _56: "56",
  _97: "97",
  _137: "137"
};
var LiquidstakingMintRequestPayloadLiquidStakingIdEnum = {
  _200: "200",
  _201: "201"
};
var LiquidstakingRequestwithdrawalRequestPayloadLiquidStakingIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _200: "200",
  _201: "201",
  _2: "2",
  _56: "56",
  _97: "97",
  _137: "137",
  _3: "3",
  _17000: "17000",
  _43114: "43114",
  _900: "900"
};
var LiquidstakingRestakeRequestPayloadLiquidStakingIdEnum = {
  _3: "3",
  _17000: "17000"
};
var LiquidstakingStakeRequestPayloadLiquidStakingIdEnum = {
  _1: "1",
  _11155111: "11155111",
  _200: "200",
  _201: "201",
  _2: "2",
  _56: "56",
  _97: "97",
  _137: "137",
  _43114: "43114",
  _900: "900"
};
var LiquidstakingUndelegateRequestPayloadLiquidStakingIdEnum = {
  _3: "3",
  _17000: "17000"
};
var LiquidstakingUnwrapRequestPayloadLiquidStakingIdEnum = {
  _1: "1",
  _11155111: "11155111"
};
var LiquidstakingWrapRequestPayloadLiquidStakingIdEnum = {
  _1: "1",
  _11155111: "11155111"
};
var RwaBurnRequestPayloadChainIdEnum = {
  _1500: "1500",
  _1501: "1501",
  _1600: "1600",
  _1601: "1601"
};
var RwaFreezeRequestPayloadChainIdEnum = {
  _1500: "1500",
  _1501: "1501",
  _1600: "1600",
  _1601: "1601"
};
var RwaIssueRequestPayloadChainIdEnum = {
  _1500: "1500",
  _1501: "1501",
  _1600: "1600",
  _1601: "1601"
};
var RwaRegisterRequestPayloadChainIdEnum = {
  _1500: "1500",
  _1501: "1501"
};
var RwaSettrustlineRequestPayloadChainIdEnum = {
  _1500: "1500",
  _1501: "1501",
  _1600: "1600",
  _1601: "1601"
};
var RwaTransferRequestPayloadChainIdEnum = {
  _1500: "1500",
  _1501: "1501",
  _1600: "1600",
  _1601: "1601"
};
var RwaUnfreezeRequestPayloadChainIdEnum = {
  _1500: "1500",
  _1501: "1501"
};
var SyntheticDepositpeggedtokenRequestPayloadSyntheticIdEnum = {
  _6000: "6000"
};
var SyntheticWithdrawpeggedtokenRequestPayloadSyntheticIdEnum = {
  _6000: "6000"
};
var YieldaggregatorAddliquidityRequestPayloadYieldAggregatorIdEnum = {
  _5200: "5200",
  _5201: "5201",
  _5202: "5202",
  _5203: "5203",
  _5204: "5204"
};
var YieldaggregatorDepositpoolRequestPayloadYieldAggregatorIdEnum = {
  _5100: "5100"
};
var YieldaggregatorDepositvaultRequestPayloadYieldAggregatorIdEnum = {
  _5000: "5000",
  _5001: "5001",
  _5002: "5002",
  _5003: "5003",
  _5100: "5100"
};
var YieldaggregatorMintRequestPayloadYieldAggregatorIdEnum = {
  _5200: "5200",
  _5201: "5201",
  _5202: "5202",
  _5203: "5203",
  _5204: "5204"
};
var YieldaggregatorRedeemRequestPayloadYieldAggregatorIdEnum = {
  _5200: "5200",
  _5201: "5201",
  _5202: "5202",
  _5203: "5203",
  _5204: "5204"
};
var YieldaggregatorRemoveliquidityRequestPayloadYieldAggregatorIdEnum = {
  _5200: "5200",
  _5201: "5201",
  _5202: "5202",
  _5203: "5203",
  _5204: "5204"
};
var YieldaggregatorSwapRequestPayloadYieldAggregatorIdEnum = {
  _5200: "5200",
  _5201: "5201",
  _5202: "5202",
  _5203: "5203",
  _5204: "5204"
};
var YieldaggregatorTransferliquidityRequestPayloadYieldAggregatorIdEnum = {
  _5200: "5200",
  _5201: "5201",
  _5202: "5202",
  _5203: "5203",
  _5204: "5204"
};
var YieldaggregatorWithdrawpoolRequestPayloadYieldAggregatorIdEnum = {
  _5100: "5100"
};
var YieldaggregatorWithdrawvaultRequestPayloadYieldAggregatorIdEnum = {
  _5000: "5000",
  _5001: "5001",
  _5002: "5002",
  _5003: "5003",
  _5100: "5100"
};
export {
  AaApi,
  AaGetpaymasterdataChainIdEnum,
  AaGetsignaturemessageChainIdEnum,
  AaGetuseropsChainIdEnum,
  AaSenduseropsRequestPayloadChainIdEnum,
  AccountApi,
  AccountV1AllowanceChainIdEnum,
  AccountV1AllowanceLegacyChainIdEnum,
  AccountV1PortfolioAssetTypeEnum,
  AccountV1PortfolioChainIdEnum,
  AccountV1PortfolioLegacyAssetTypeEnum,
  AccountV1PortfolioLegacyChainIdEnum,
  BASE_PATH,
  BaseAPI,
  BlobApiResponse,
  BridgeAddliquidityRequestPayloadBridgeIdEnum,
  BridgeApi,
  BridgeGetliquidityBridgeIdEnum,
  BridgeGetrouteBridgeIdEnum,
  BridgeGetsignatureBridgeIdEnum,
  BridgeGetsupportedchainsBridgeIdEnum,
  BridgeGetsupportedtokensBridgeIdEnum,
  BridgePriceBridgeIdEnum,
  BridgePriceLegacyBridgeIdEnum,
  BridgeRemoveliquidityRequestPayloadBridgeIdEnum,
  BridgeSwap200ResponseDataAnyOf2PayloadMethodUsedEnum,
  BridgeSwap200ResponseDataPayloadMethodUsedEnum,
  BridgeSwapRequestPayloadBatchOptionEnum,
  BridgeSwapRequestPayloadBridgeIdEnum,
  BridgeSwapaggregatorRequestPayloadBatchOptionEnum,
  BridgeSwaps200ResponseDataAnyOf1InnerPayloadMethodUsedEnum,
  BridgeSwapwithapprovalRequestPayloadBatchOptionEnum,
  BridgeSwapwithapprovalRequestPayloadBridgeIdEnum,
  BridgeTransactionsBridgeIdEnum,
  BridgeTransactionsLegacyBridgeIdEnum,
  BridgeUnlockRequestPayloadBridgeIdEnum,
  COLLECTION_FORMATS,
  ChainApi,
  ChainCreateaccountRequestPayloadChainIdEnum,
  ChainCreatefeetransactionRequestPayloadChainIdEnum,
  ChainDecodetransactionRequestPayloadChainIdEnum,
  ChainEstimategasRequestPayloadChainIdEnum,
  ChainGenericsmartcontractmethodRequestPayloadAbiEnum,
  ChainGenericsmartcontractmethodRequestPayloadChainIdEnum,
  ChainGetapprovaldataChainIdEnum,
  ChainGetbalanceChainIdEnum,
  ChainGetblockChainIdEnum,
  ChainGeteventsChainIdEnum,
  ChainGeteventsSortOrderEnum,
  ChainGeteventsTypeEnum,
  ChainGetgasfeesChainIdEnum,
  ChainGetgaspriceChainIdEnum,
  ChainGethistoricalrewardsChainIdEnum,
  ChainGethistoricalrewardsSortOrderEnum,
  ChainGetlatestledgerChainIdEnum,
  ChainGetstakepositionsChainIdEnum,
  ChainGetstorageChainIdEnum,
  ChainGetsuibalanceChainIdEnum,
  ChainGettickerpriceChainIdEnum,
  ChainGettokenaddressChainIdEnum,
  ChainGettransactionChainIdEnum,
  ChainGetusertransactionsChainIdEnum,
  ChainGetusertransactionsSortOrderEnum,
  ChainRegistercoinRequestPayloadChainIdEnum,
  ChainSendtransactionRequestPayloadChainIdEnum,
  ChainSimulatetransactionRequestPayloadChainIdEnum,
  Configuration,
  DefaultConfig,
  DerivativeApi,
  DerivativeGetassetsDerivativeIdEnum,
  DerivativeGetfillsDerivativeIdEnum,
  DerivativeGethistoricalpnlDerivativeIdEnum,
  DerivativeGetorderDerivativeIdEnum,
  DerivativeGetordersDerivativeIdEnum,
  DerivativeGetperpetualpositionsDerivativeIdEnum,
  DerivativeGetsubaccountsDerivativeIdEnum,
  DerivativeGettransfersDerivativeIdEnum,
  DexAddliquidityRequestPayloadDexIdEnum,
  DexAddliquidityRequestPayloadStrategyEnum,
  DexApi,
  DexCancelorderRequestPayloadDexIdEnum,
  DexCreateorderRequestPayloadDexIdEnum,
  DexFillorderRequestPayloadDexIdEnum,
  DexGetactiveamountDexIdEnum,
  DexGethistoricalpoolchartdataDexIdEnum,
  DexGethistoricaltimeseriesDexIdEnum,
  DexGethistoricaltransactionsDexIdEnum,
  DexGetindividualpositionDexIdEnum,
  DexGetliquiditybytickDexIdEnum,
  DexGetliquiditysourcesDexIdEnum,
  DexGetmakerordersDexIdEnum,
  DexGetordersDexIdEnum,
  DexGetordersSortEnum,
  DexGetpoolDexIdEnum,
  DexGetpoolchartdataDexIdEnum,
  DexGetpoolindividualliquidityDexIdEnum,
  DexGetpoolliquidityDexIdEnum,
  DexGetpoolsDexIdEnum,
  DexGetpooltradedataDexIdEnum,
  DexGetpooltradedataEventTypeEnum,
  DexGetrouteDexIdEnum,
  DexGettokenidpriceDexIdEnum,
  DexGettokenliquidityDexIdEnum,
  DexGetuserliquidityDexIdEnum,
  DexGetusertokenidsDexIdEnum,
  DexPriceDexIdEnum,
  DexPriceLegacyDexIdEnum,
  DexPublishorderRequestPayloadDexIdEnum,
  DexPublishorderRequestPayloadOrderTypeEnum,
  DexQuotesChainIdEnum,
  DexQuotesLegacyChainIdEnum,
  DexRemoveliquidityRequestPayloadDexIdEnum,
  DexSwap200ResponseDataPayloadMethodUsedEnum,
  DexSwapRequestPayloadBatchOptionEnum,
  DexSwapRequestPayloadDexIdEnum,
  DexSwapaggregatorRequestPayloadBatchOptionEnum,
  DexSwapaggregatorRequestPayloadChainIdEnum,
  DexSwaps200ResponseDataAnyOf1InnerPayloadMethodUsedEnum,
  DexSwapwithapprovalRequestPayloadBatchOptionEnum,
  DexSwapwithapprovalRequestPayloadDexIdEnum,
  FetchError,
  FungibletokenApi,
  FungibletokenApproveRequestPayloadChainIdEnum,
  FungibletokenConvertbasetokentowraptokenRequestPayloadChainIdEnum,
  FungibletokenConvertwraptokentobasetokenRequestPayloadChainIdEnum,
  FungibletokenGettokendetailsChainIdEnum,
  FungibletokenGetuserbalanceChainIdEnum,
  FungibletokenHistoricalWethChainIdEnum,
  FungibletokenHistoricalWethSortEnum,
  FungibletokenHistoricallogsChainIdEnum,
  FungibletokenHistoricallogsTypeEnum,
  FungibletokenHistoricaltransactionsChainIdEnum,
  FungibletokenHistoricaltransactionsSortEnum,
  FungibletokenHistoricalusertransactionsChainIdEnum,
  FungibletokenHistoricalusertransactionsSortOrderEnum,
  FungibletokenTransferRequestPayloadChainIdEnum,
  FungibletokenTransferfromRequestPayloadChainIdEnum,
  Getapimetadatav2Api,
  JSONApiResponse,
  LendborrowAllowRequestPayloadLendborrowIdEnum,
  LendborrowApi,
  LendborrowBorrow200ResponseDataPayloadMethodUsedEnum,
  LendborrowBorrowRequestPayloadBatchOptionEnum,
  LendborrowBorrowRequestPayloadLendborrowIdEnum,
  LendborrowBundleactionsRequestPayloadLendborrowIdEnum,
  LendborrowClaimrewardsRequestPayloadLendborrowIdEnum,
  LendborrowDeposit200ResponseDataPayloadMethodUsedEnum,
  LendborrowDepositRequestPayloadBatchOptionEnum,
  LendborrowDepositRequestPayloadLendborrowIdEnum,
  LendborrowEntermarketRequestPayloadLendborrowIdEnum,
  LendborrowEntermarketstatusLendborrowIdEnum,
  LendborrowExitisolationmodeRequestPayloadLendborrowIdEnum,
  LendborrowExitmarketRequestPayloadLendborrowIdEnum,
  LendborrowGetassetinfoLendborrowIdEnum,
  LendborrowGetclaimedrewardsLendborrowIdEnum,
  LendborrowGetgovernordataLendborrowIdEnum,
  LendborrowGetmaxamountsLendborrowIdEnum,
  LendborrowGetuseraccountdataLendborrowIdEnum,
  LendborrowMigrateRequestPayloadLendborrowIdEnum,
  LendborrowPoolLegacyLendborrowIdEnum,
  LendborrowPoolLendborrowIdEnum,
  LendborrowPoolsLegacyLendborrowIdEnum,
  LendborrowPoolsLendborrowIdEnum,
  LendborrowRepay200ResponseDataPayloadMethodUsedEnum,
  LendborrowRepayRequestPayloadBatchOptionEnum,
  LendborrowRepayRequestPayloadLendborrowIdEnum,
  LendborrowSetuseremodeRequestPayloadCategoryIdEnum,
  LendborrowSetuseremodeRequestPayloadLendborrowIdEnum,
  LendborrowTransfer200ResponseDataPayloadMethodUsedEnum,
  LendborrowTransferRequestPayloadBatchOptionEnum,
  LendborrowTransferRequestPayloadLendborrowIdEnum,
  LendborrowUserpositionsLegacyLendborrowIdEnum,
  LendborrowUserpositionsLendborrowIdEnum,
  LendborrowWithdraw200ResponseDataPayloadMethodUsedEnum,
  LendborrowWithdrawRequestPayloadBatchOptionEnum,
  LendborrowWithdrawRequestPayloadLendborrowIdEnum,
  LiquidstakingApi,
  LiquidstakingApprovewithdrawalRequestPayloadLiquidStakingIdEnum,
  LiquidstakingBurnRequestPayloadLiquidStakingIdEnum,
  LiquidstakingClaimRequestPayloadLiquidStakingIdEnum,
  LiquidstakingDecreaseallowanceRequestPayloadLiquidStakingIdEnum,
  LiquidstakingDelegateRequestPayloadLiquidStakingIdEnum,
  LiquidstakingGetallowanceLiquidStakingIdEnum,
  LiquidstakingGetaprLiquidStakingIdEnum,
  LiquidstakingGetprotocolaprLiquidStakingIdEnum,
  LiquidstakingGetrewardsLiquidStakingIdEnum,
  LiquidstakingGetstakeLiquidStakingIdEnum,
  LiquidstakingGetwithdrawalrequestsLiquidStakingIdEnum,
  LiquidstakingGetwithdrawalstatusLiquidStakingIdEnum,
  LiquidstakingIncreaseallowanceRequestPayloadLiquidStakingIdEnum,
  LiquidstakingMintRequestPayloadLiquidStakingIdEnum,
  LiquidstakingRequestwithdrawalRequestPayloadLiquidStakingIdEnum,
  LiquidstakingRestakeRequestPayloadLiquidStakingIdEnum,
  LiquidstakingStakeRequestPayloadLiquidStakingIdEnum,
  LiquidstakingUndelegateRequestPayloadLiquidStakingIdEnum,
  LiquidstakingUnwrapRequestPayloadLiquidStakingIdEnum,
  LiquidstakingWrapRequestPayloadLiquidStakingIdEnum,
  NftApi,
  NftGetbalanceChainIdEnum,
  NftGetmetadataChainIdEnum,
  NftGetownerChainIdEnum,
  NftHistoricallogsChainIdEnum,
  NftHistoricallogsTypeEnum,
  NftHistoricaltransactionsChainIdEnum,
  NftHistoricaltransactionsSortEnum,
  OracleApi,
  OracleGetpriceOracleIdEnum,
  OracleGetsupportedtokensOracleIdEnum,
  RequiredError,
  ResponseError,
  RwaApi,
  RwaBurnRequestPayloadChainIdEnum,
  RwaFreezeRequestPayloadChainIdEnum,
  RwaGetallassetsChainIdEnum,
  RwaGetassetChainIdEnum,
  RwaGetownerassetsChainIdEnum,
  RwaIssueRequestPayloadChainIdEnum,
  RwaRegisterRequestPayloadChainIdEnum,
  RwaSettrustlineRequestPayloadChainIdEnum,
  RwaTransferRequestPayloadChainIdEnum,
  RwaUnfreezeRequestPayloadChainIdEnum,
  StablecoinApi,
  SupportApi,
  SyntheticApi,
  SyntheticDepositpeggedtokenRequestPayloadSyntheticIdEnum,
  SyntheticGetpriceSyntheticIdEnum,
  SyntheticWithdrawpeggedtokenRequestPayloadSyntheticIdEnum,
  TagsApi,
  TextApiResponse,
  TokensApi,
  UtilityApi,
  V1Api,
  VoidApiResponse,
  WethApi,
  WethGetbalanceChainIdEnum,
  WethHistoricalLogsChainIdEnum,
  WethHistoricalLogsTypeEnum,
  YieldaggregatorAddliquidityRequestPayloadYieldAggregatorIdEnum,
  YieldaggregatorApi,
  YieldaggregatorDepositpoolRequestPayloadYieldAggregatorIdEnum,
  YieldaggregatorDepositvaultRequestPayloadYieldAggregatorIdEnum,
  YieldaggregatorGetbalanceYieldAggregatorIdEnum,
  YieldaggregatorGetmarketdataYieldAggregatorIdEnum,
  YieldaggregatorGetmarketsYieldAggregatorIdEnum,
  YieldaggregatorGetmarkettokensYieldAggregatorIdEnum,
  YieldaggregatorGetvaultsYieldAggregatorIdEnum,
  YieldaggregatorMintRequestPayloadYieldAggregatorIdEnum,
  YieldaggregatorRedeemRequestPayloadYieldAggregatorIdEnum,
  YieldaggregatorRemoveliquidityRequestPayloadYieldAggregatorIdEnum,
  YieldaggregatorSwapRequestPayloadYieldAggregatorIdEnum,
  YieldaggregatorTransferliquidityRequestPayloadYieldAggregatorIdEnum,
  YieldaggregatorWithdrawpoolRequestPayloadYieldAggregatorIdEnum,
  YieldaggregatorWithdrawvaultRequestPayloadYieldAggregatorIdEnum,
  canConsumeForm,
  exists,
  querystring
};
